import React, { useState, useEffect, useCallback, useRef } from "react";
import { loadFromFirebase, saveToFirebase, subscribeToFirebase } from "./firebase";

/* ═══════════ SESSION PERSISTENCE ═══════════ */
const SESSION_KEY = "acc_session_v1";
const saveSession  = (s) => { try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch {} };
const loadSession  = () => { try { const v=sessionStorage.getItem(SESSION_KEY); return v?JSON.parse(v):null; } catch { return null; } };
const clearSession = () => { try { sessionStorage.removeItem(SESSION_KEY); } catch {} };

/* ═══════════ HELPERS ═══════════ */
const uid      = () => Math.random().toString(36).slice(2,10);
const nowISO   = () => new Date().toISOString();
const fmtDT    = (iso) => iso ? new Date(iso).toLocaleString("ar-EG",{dateStyle:"short",timeStyle:"short"}) : "—";
const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString("ar-EG",{dateStyle:"medium"}) : "—";
const currency = (n)   => Number(n||0).toLocaleString("ar-EG") + " ج.م";
const num      = (n)   => Number(n||0);

const fuzzy = (text, query) => {
  if (!query) return true;
  const normalize = s => (s||"").toLowerCase()
    .replace(/أ|إ|آ/g,"ا").replace(/ة/g,"ه").replace(/ى/g,"ي")
    .replace(/\s+/g,"");
  const t = normalize(text); const q = normalize(query);
  if (t.includes(q)) return true;
  for (let i=0;i<q.length;i++) { const s=q.slice(0,i)+q.slice(i+1); if(t.includes(s)) return true; }
  return false;
};

/* ═══════════ CONFIRM DIALOG ═══════════ */
let _confirmResolve=null;
let _setConfirmState=null;
const showConfirm=(msg,opts={})=>new Promise(res=>{
  _confirmResolve=res;
  _setConfirmState&&_setConfirmState({open:true,msg,title:opts.title||"تأكيد",danger:opts.danger||false,confirmText:opts.confirmText||"تأكيد",cancelText:opts.cancelText||"إلغاء"});
});

function ConfirmDialogProvider(){
  const [state,setState]=useState({open:false,msg:"",title:"تأكيد",danger:false,confirmText:"تأكيد",cancelText:"إلغاء"});
  useEffect(()=>{_setConfirmState=setState;},[]);
  if(!state.open) return null;
  const resolve=(val)=>{setState(s=>({...s,open:false}));_confirmResolve&&_confirmResolve(val);};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.75)",backdropFilter:"blur(4px)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,padding:"24px 24px 20px",width:"100%",maxWidth:380,boxShadow:"0 24px 60px rgba(0,0,0,.6)"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
          <div style={{width:38,height:38,borderRadius:"50%",background:state.danger?"#450a0a":"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {state.danger
              ?<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              :<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            }
          </div>
          <div>
            <div style={{color:"#f1f5f9",fontWeight:700,fontSize:15,marginBottom:2}}>{state.title}</div>
            <div style={{color:"#94a3b8",fontSize:13,lineHeight:1.5}}>{state.msg}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button style={{background:"#1e293b",border:"1px solid #334155",borderRadius:8,padding:"8px 16px",color:"#94a3b8",cursor:"pointer",fontFamily:"inherit",fontSize:13}} onClick={()=>resolve(false)}>{state.cancelText}</button>
          <button style={{background:state.danger?"#dc2626":"#1d4ed8",border:"none",borderRadius:8,padding:"8px 18px",color:"#fff",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:700}} onClick={()=>resolve(true)}>{state.confirmText}</button>
        </div>
      </div>
    </div>
  );
}

const defaultDB = () => ({
  users:    [{ id:"admin", role:"admin", name:"الإدارة", password:"admin123" }],
  courseTypes: [], // [#1] قائمة أسماء الدورات المتاحة من الإدارة
  courses:  [],
  students: {},
  entries:  [],
  books:    {},
  bookPayments: {},
  attendance: {},
  archivedBatches: [],
  chatMessages: [], // [#11] رسائل الشات
});

/* ═══════════ ENTRY TYPES ═══════════ */
const ENTRY_TYPES = [
  { id:"sub_mansura",  group:"income",  label:"اشتراك – المنصورة",  color:"#22c55e" },
  { id:"sub_cairo",    group:"income",  label:"اشتراك – القاهرة",   color:"#16a34a" },
  { id:"income_admin", group:"income",  label:"وارد من الإدارة",    color:"#3b82f6" },
  { id:"income_donor", group:"income",  label:"وارد من متبرع",      color:"#8b5cf6" },
  { id:"income_other", group:"income",  label:"واردات أخرى",        color:"#06b6d4" },
  { id:"exp_supplies", group:"expense", label:"مستلزمات",           color:"#f59e0b" },
  { id:"exp_other",    group:"expense", label:"مصروف آخر",          color:"#fb923c" },
  { id:"salary",       group:"expense", label:"مكافأة / مرتب",      color:"#ec4899" },
  { id:"gift",         group:"expense", label:"تكريم مشرف",         color:"#14b8a6" },
  { id:"transport",    group:"expense", label:"انتقالات",           color:"#6366f1" },
  { id:"book_print",   group:"books",   label:"تكلفة طباعة كتب",    color:"#f43f5e" },
];
const GROUPS=[{id:"income",label:"إيرادات",color:"#22c55e"},{id:"expense",label:"مصروفات",color:"#ef4444"},{id:"books",label:"كتب",color:"#0ea5e9"}];
const typeById=(id)=>ENTRY_TYPES.find(t=>t.id===id)||{id:"unknown",group:"expense",label:id||"—",color:"#94a3b8"};

const bookStats=(db,courseId)=>{
  const books=(db.books||{})[courseId]||[];
  const students=(db.students||{})[courseId]||[];
  const bookPay=((db.bookPayments||{})[courseId])||{};
  let revenue=0,pending=0,exemptCount=0;
  books.forEach(b=>{
    const statuses=bookPay[b.id]||{};
    students.forEach(s=>{
      const st=statuses[s.id];
      if(st==="paid") revenue+=num(b.salePrice);
      else if(st==="owed") pending+=num(b.salePrice);
      else if(st==="exempt") exemptCount++;
    });
  });
  return{revenue,pending,exemptCount};
};

/* ═══════════ ROOT ═══════════ */
export default function App() {
  const [db,setDbState]=useState(null);
  const [session,setSession]=useState(()=>loadSession());
  const [loading,setLoading]=useState(true);
  const [globalSearch,setGlobalSearch]=useState("");
  const [showSearch,setShowSearch]=useState(false);
  const [searchNav,setSearchNav]=useState(null); // {courseId, page}

  useEffect(()=>{loadFromFirebase(defaultDB()).then(data=>{setDbState({...defaultDB(),...data});setLoading(false);});},[]);
  useEffect(()=>{const u=subscribeToFirebase(d=>setDbState(p=>({...defaultDB(),...p,...d})));return()=>u();},[]);

  const setDB=useCallback((upd)=>{
    setDbState(prev=>{const next=typeof upd==="function"?upd(prev):upd;saveToFirebase(next);return next;});
  },[]);

  const handleLogin = useCallback((s)=>{ saveSession(s); setSession(s); },[]);
  const handleLogout = useCallback(()=>{ clearSession(); setSession(null); },[]);

  if(loading||!db) return(
    <div style={{minHeight:"100vh",background:"#060c18",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:12,fontFamily:"Cairo,sans-serif"}}>
      <div style={{width:110,height:110,borderRadius:"50%",overflow:"hidden",background:"#ffffff",boxShadow:"0 8px 32px rgba(0,0,0,.35)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:4,padding:14}}>
        <img src={RAWAHEL_LOGO} alt="رواحل" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
      </div>
      <div style={{color:"#94a3b8",fontSize:13,letterSpacing:1}}>جاري التحميل</div>
      <style>{`@keyframes sl{0%{transform:translateX(-100%)}100%{transform:translateX(300%)}}`}</style>
      <div style={{width:100,height:2,background:"#1e293b",borderRadius:4,overflow:"hidden"}}><div style={{width:"35%",height:"100%",background:"linear-gradient(90deg,#d4be8c,#f5e6c0)",animation:"sl 1.2s infinite ease-in-out"}}/></div>
    </div>
  );

  const validatedSession = (() => {
    if(!session) return null;
    if(session.role==="admin") return session;
    const course=(db.courses||[]).find(c=>c.id===session.courseId&&!c.archived);
    if(!course){ clearSession(); return null; }
    return session;
  })();

  const searchResults = globalSearch.length>1 ? buildSearchResults(db, globalSearch) : [];

  if(!validatedSession) return <LoginScreen db={db} setDB={setDB} onLogin={handleLogin}/>;

  return(
    <div style={{position:"relative"}}>
      <ConfirmDialogProvider/>
      {/* Global search overlay */}
      {showSearch&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:300,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"60px 16px 16px"}}>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:16,width:"100%",maxWidth:600,maxHeight:"80vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid #1e293b",display:"flex",gap:10,alignItems:"center"}}>
              <input autoFocus style={{...D.input,flex:1,fontSize:15}} placeholder="ابحث في كل السيستم..." value={globalSearch}
                onChange={e=>setGlobalSearch(e.target.value)}/>
              <button style={D.closeBtn} onClick={()=>{setShowSearch(false);setGlobalSearch("");}}>✕</button>
            </div>
            <div style={{overflowY:"auto",padding:16}}>
              {globalSearch.length<2&&<div style={{color:"#475569",textAlign:"center",padding:32}}>اكتب كلمتين على الأقل للبحث</div>}
              {globalSearch.length>=2&&searchResults.length===0&&<div style={{color:"#475569",textAlign:"center",padding:32}}>لا توجد نتائج</div>}
              {searchResults.map((r,i)=>(
                <div key={i} style={{...D.entryRow,marginBottom:8,cursor:"pointer",transition:"border-color .15s"}}
                  onClick={()=>{
                    if(r.action){setSearchNav(r.action);}
                    setShowSearch(false);setGlobalSearch("");
                  }}>
                  <div style={{flex:1}}>
                    <div style={{color:"#f1f5f9",fontWeight:600,fontSize:13}}>{r.title}</div>
                    <div style={{color:"#64748b",fontSize:11}}>{r.sub}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    {r.amount&&<div style={{color:"#4ade80",fontWeight:700,fontSize:13}}>{currency(r.amount)}</div>}
                    <span style={{color:"#475569",fontSize:11}}>انتقال ←</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search trigger button (floating) */}
      <button onClick={()=>setShowSearch(true)} style={{position:"fixed",bottom:24,left:24,zIndex:200,background:"#1d4ed8",border:"none",borderRadius:"50%",width:50,height:50,cursor:"pointer",boxShadow:"0 4px 20px rgba(29,78,216,.5)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}} title="بحث شامل">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
    </button>

      {session.role==="admin"
        ? <AdminDashboard db={db} setDB={setDB} session={validatedSession} onLogout={handleLogout} searchNav={searchNav} onSearchNavDone={()=>setSearchNav(null)}/>
        : <SupervisorDashboard db={db} setDB={setDB} session={validatedSession} onLogout={handleLogout} searchNav={searchNav} onSearchNavDone={()=>setSearchNav(null)}/>
      }
    </div>
  );
}

function buildSearchResults(db, query) {
  const results=[];
  (db.entries||[]).forEach(e=>{
    const t=typeById(e.type);
    const fields=[t.label,e.studentName,e.recipient,e.description,e.amount+""].filter(Boolean).join(" ");
    if(fuzzy(fields,query)){
      const course=(db.courses||[]).find(c=>c.id===e.courseId);
      results.push({
        title:`${t.label} — ${currency(e.amount)}`,
        sub:`${course?.name||""} · ${fmtDT(e.createdAt)}${e.studentName?" · "+e.studentName:""}`,
        amount:e.amount,
        action:{type:"entry",courseId:e.courseId,page:"entries"},
      });
    }
  });
  Object.entries(db.students||{}).forEach(([cid,list])=>{
    const course=(db.courses||[]).find(c=>c.id===cid);
    (list||[]).forEach(s=>{
      if(fuzzy(s.name,query)) results.push({
        title:s.name,
        sub:`${course?.name||""} · ${s.branch} · ${s.paid?"دفع":"لم يدفع"}`,
        action:{type:"student",courseId:cid,page:"students",studentId:s.id},
      });
    });
  });
  (db.courses||[]).forEach(c=>{
    if(fuzzy(c.name+" "+c.manager,query)) results.push({
      title:c.name,
      sub:`دفعة ${c.batchNumber} · ${c.branch} · ${c.manager}`,
      action:{type:"course",courseId:c.id,page:"overview"},
    });
  });
  return results.slice(0,40);
}

/* ═══════════ LOGIN SCREEN ═══════════ */
function LoginScreen({db,setDB,onLogin}){
  const [mode,setMode]=useState("login");
  const [loginId,setLoginId]=useState("admin");
  const [pw,setPw]=useState("");
  const [showPw,setShowPw]=useState(false);
  const [err,setErr]=useState("");

  // [#1] حالة إنشاء دفعة جديدة — المشرف يختار من أسماء الدورات المتاحة
  const [nc,setNc]=useState({
    courseTypeId:"",   // اختيار نوع الدورة من القائمة
    branches:["المنصورة"],
    batchMansura:"1",
    batchCairo:"1",
    manager:"",
    managerMansura:"",
    managerCairo:"",
    password:"",
    confirmPw:"",
    logo:"",
    sameManager:true,
  });
  const [showNcPw,setShowNcPw]=useState(false);
  const setNcF=k=>e=>setNc(p=>({...p,[k]:e.target.value}));
  const logoRef=useRef();

  const activeCourses=(db.courses||[]).filter(c=>!c.archived);
  const allLogins=[
    {id:"admin",label:"الإدارة المركزية",sub:"",logo:db.centerLogo||""},
    ...activeCourses.map(c=>({id:c.id,label:c.name,sub:`دفعة ${c.batchNumber} · ${c.branch}`,logo:c.logo||""}))
  ];
  const selectedLogin=allLogins.find(l=>l.id===loginId);

  const doLogin=()=>{
    if(loginId==="admin"){
      const admin=(db.users||[]).find(u=>u.id==="admin");
      if(pw===admin?.password){onLogin({userId:"admin",role:"admin",courseId:null});return;}
      setErr("كلمة المرور غير صحيحة");return;
    }
    const course=(db.courses||[]).find(c=>c.id===loginId);
    if(!course){setErr("الدورة غير موجودة");return;}
    if(pw===course.password){onLogin({userId:course.manager,role:"supervisor",courseId:course.id});return;}
    setErr("كلمة المرور غير صحيحة");
  };

  // [#1] إنشاء دفعة جديدة — الاسم يأتي من courseTypes التي تحددها الإدارة
  const doNew=()=>{
    if(!nc.password){setErr("أدخل كلمة المرور");return;}
    if(nc.password!==nc.confirmPw){setErr("كلمتا المرور غير متطابقتان");return;}
    if(!nc.branches||nc.branches.length===0){setErr("اختر فرع واحد على الأقل");return;}
    // الاسم يجب أن يكون من قائمة الإدارة فقط — لا يُسمح بإنشاء اسم حر
    const ct=(db.courseTypes||[]).find(t=>t.id===nc.courseTypeId);
    if(!ct){setErr("اختر نوع الدورة من القائمة — هذه القائمة تحددها الإدارة فقط");return;}
    const courseName=ct.name;
    const bothBranches=nc.branches.length===2;
    if(bothBranches){
      const idM=uid(), idC=uid();
      const manM=nc.managerMansura||nc.manager||"—";
      const manC=nc.managerCairo||nc.manager||"—";
      setDB(p=>({...p,
        courses:[...(p.courses||[]),
          {id:idM,name:courseName,courseTypeId:nc.courseTypeId,batchNumber:Number(nc.batchMansura),branch:"المنصورة",branches:["المنصورة"],manager:manM,assistants:[],trainers:[],password:nc.password,createdAt:nowISO(),archived:false,logo:nc.logo||"",subPrice:0},
          {id:idC,name:courseName,courseTypeId:nc.courseTypeId,batchNumber:Number(nc.batchCairo),branch:"القاهرة",branches:["القاهرة"],manager:manC,assistants:[],trainers:[],password:nc.password,createdAt:nowISO(),archived:false,logo:nc.logo||"",subPrice:0},
        ],
        students:{...(p.students||{}),[idM]:[]  ,[idC]:[]},
        books:   {...(p.books||{}),   [idM]:[]  ,[idC]:[]},
      }));
    } else {
      const id=uid();
      const br=nc.branches[0];
      const batchNum=br==="القاهرة"?Number(nc.batchCairo):Number(nc.batchMansura);
      const manager=nc.sameManager?(nc.manager||"—"):(br==="القاهرة"?(nc.managerCairo||"—"):(nc.managerMansura||"—"));
      setDB(p=>({...p,
        courses:[...(p.courses||[]),{id,name:courseName,courseTypeId:nc.courseTypeId,batchNumber:batchNum,branch:br,branches:[br],manager,assistants:[],trainers:[],password:nc.password,createdAt:nowISO(),archived:false,logo:nc.logo||"",subPrice:0}],
        students:{...(p.students||{}),[id]:[]},
        books:{...(p.books||{}),[id]:[]},
      }));
    }
    setMode("login");setErr("");
    setNc({courseTypeId:"",customName:"",branches:["المنصورة"],batchMansura:"1",batchCairo:"1",manager:"",managerMansura:"",managerCairo:"",password:"",confirmPw:"",logo:"",sameManager:true});
  };

  const toB64=(file,cb)=>{const r=new FileReader();r.onload=e=>cb(e.target.result);r.readAsDataURL(file);};
  const uploadCourseLogo=e=>{if(e.target.files[0])toB64(e.target.files[0],b=>setNc(p=>({...p,logo:b})));};

  const availableCourseTypes=db.courseTypes||[];
  const selectedCourseType=availableCourseTypes.find(t=>t.id===nc.courseTypeId);

  return(
    <div style={LS.bg} className="rw-login-bg">
      <div style={LS.card}>
        {/* Logo + Brand */}
        <div style={{textAlign:"center",marginBottom:28}}>
          <div className="rw-logo-glow" style={{width:88,height:88,borderRadius:"50%",overflow:"hidden",margin:"0 auto",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:16,padding:10}}>
            {selectedLogin?.logo
              ?<img src={selectedLogin.logo} alt="logo" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
              :<img src={RAWAHEL_LOGO} alt="رواحل" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
            }
          </div>
          <div style={{color:"#60a5fa",fontSize:11,fontWeight:600,letterSpacing:3,textTransform:"uppercase",marginBottom:6}}>RAWAHEL</div>
          <h1 style={{color:"#f1f5f9",fontSize:19,fontWeight:800,margin:0}}>
            {selectedLogin?.id==="admin"?"الإدارة المركزية":selectedLogin?.label||"نظام الحسابات"}
          </h1>
          {selectedLogin?.sub&&<p style={{color:"#4b6488",fontSize:11,margin:"6px 0 0"}}>{selectedLogin.sub}</p>}
        </div>

        {mode==="login"&&<>
          <div style={{marginBottom:14}}>
            <div style={{color:"#64748b",fontSize:11,marginBottom:8}}>اختر الحساب</div>
            {/* الإدارة */}
            <button onClick={()=>{setLoginId("admin");setErr("");}}
              style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:12,border:"2px solid "+(loginId==="admin"?"#7c3aed":"#1e293b"),background:loginId==="admin"?"#1a0a2e":"#1e293b",cursor:"pointer",fontFamily:"inherit",textAlign:"right",transition:"all .15s",width:"100%",marginBottom:10}}>
              <div style={{width:40,height:40,borderRadius:10,overflow:"hidden",flexShrink:0,background:"#0f172a",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid #4c1d95"}}>
                {allLogins[0].logo?<img src={allLogins[0].logo} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:18,color:"#c084fc",fontWeight:800}}>A</span>}
              </div>
              <div style={{flex:1}}>
                <div style={{color:loginId==="admin"?"#e9d5ff":"#94a3b8",fontSize:14,fontWeight:700}}>الإدارة المركزية</div>
                <div style={{color:"#6d28d9",fontSize:11}}>وصول كامل للنظام</div>
              </div>
              {loginId==="admin"&&<span style={{color:"#7c3aed",fontSize:18}}>✓</span>}
            </button>

            {/* الدورات */}
            {allLogins.slice(1).length>0&&<>
              <div style={{color:"#475569",fontSize:11,fontWeight:700,marginBottom:6,letterSpacing:1}}>الدورات النشطة</div>
              <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:240,overflowY:"auto",paddingLeft:2}}>
                {allLogins.slice(1).map(l=>(
                  <button key={l.id} onClick={()=>{setLoginId(l.id);setErr("");}}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderRadius:12,border:"2px solid "+(loginId===l.id?"#3b82f6":"#1e293b"),background:loginId===l.id?"#1e3a5f":"#111827",cursor:"pointer",fontFamily:"inherit",textAlign:"right",transition:"all .15s",width:"100%"}}>
                    <div style={{width:38,height:38,borderRadius:9,overflow:"hidden",flexShrink:0,background:"#0f172a",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid #1e3a5f"}}>
                      {l.logo?<img src={l.logo} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>:<span style={{fontSize:16,fontWeight:800,color:"#60a5fa"}}>{l.label?.[0]||"?"}</span>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{color:loginId===l.id?"#f1f5f9":"#94a3b8",fontSize:13,fontWeight:loginId===l.id?700:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{l.label}</div>
                      {l.sub&&<div style={{color:"#475569",fontSize:11}}>{l.sub}</div>}
                    </div>
                    {loginId===l.id&&<span style={{color:"#3b82f6",fontSize:16,flexShrink:0}}>✓</span>}
                  </button>
                ))}
              </div>
            </>}
          </div>
          <Field label="كلمة المرور">
            <div style={{position:"relative"}}>
              <input style={{...LS.input,paddingLeft:40}} type={showPw?"text":"password"} placeholder="••••••••" value={pw}
                onChange={e=>{setPw(e.target.value);setErr("");}} onKeyDown={e=>e.key==="Enter"&&doLogin()}/>
              <button onClick={()=>setShowPw(p=>!p)} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",fontSize:14}}>
                {showPw?(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>):(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}
              </button>
            </div>
          </Field>
          {err&&<p style={LS.err}>{err}</p>}
          <button style={LS.loginBtn} onClick={doLogin}>دخول</button>
          {availableCourseTypes.length>0&&(
            <button style={LS.newBtn} onClick={()=>{setMode("newcourse");setErr("");}}>+ دورة / دفعة جديدة</button>
          )}
          {availableCourseTypes.length===0&&(
            <div style={{marginTop:12,padding:"10px 14px",background:"#0f172a",border:"1px dashed #1e293b",borderRadius:10,color:"#475569",fontSize:12,textAlign:"center"}}>
              لا توجد أنواع دورات متاحة — تواصل مع الإدارة لإضافة أنواع الدورات أولاً
            </div>
          )}
        </>}

        {mode==="newcourse"&&<>
          <p style={{color:"#94a3b8",fontSize:13,marginBottom:16,textAlign:"center"}}>إنشاء دفعة جديدة</p>

          {/* اسم الدورة — يُختار فقط من قائمة الإدارة، لا يمكن كتابة اسم حر */}
          <Field label="نوع الدورة *">
            <CustomSelect
              value={nc.courseTypeId}
              onChange={v=>setNc(p=>({...p,courseTypeId:v}))}
              placeholder="اختر من القائمة"
              options={availableCourseTypes.map(t=>({value:t.id,label:t.name}))}
            />
            {selectedCourseType&&<div style={{marginTop:8,padding:"8px 12px",background:"#1e3a5f",borderRadius:8,color:"#93c5fd",fontSize:12}}>
              {selectedCourseType.name} {selectedCourseType.description&&`— ${selectedCourseType.description}`}
            </div>}
            {availableCourseTypes.length===0&&(
              <div style={{marginTop:8,padding:"10px 14px",background:"#0f172a",border:"1px dashed #1e293b",borderRadius:10,color:"#475569",fontSize:12,textAlign:"center"}}>
                لا توجد أنواع دورات متاحة — تواصل مع الإدارة لإضافة أنواع الدورات أولاً
              </div>
            )}
          </Field>

          <Field label="الفروع المشاركة">
            <div style={{display:"flex",gap:10}}>
              {["المنصورة","القاهرة"].map(br=>{
                const checked=(nc.branches||[]).includes(br);
                return(
                  <button key={br} type="button"
                    style={{flex:1,padding:"12px",borderRadius:10,border:`2px solid ${checked?"#3b82f6":"#334155"}`,background:checked?"#1e3a5f":"#1e293b",color:checked?"#93c5fd":"#64748b",cursor:"pointer",fontFamily:"inherit",fontSize:14,fontWeight:checked?700:400,transition:"all .15s"}}
                    onClick={()=>{const cur=nc.branches||[];const next=checked?cur.filter(x=>x!==br):[...cur,br];setNc(p=>({...p,branches:next}));}}>
                    {checked?"+ ":""}{br}
                  </button>
                );
              })}
            </div>
          </Field>

          {nc.branches?.length===2?(
            <div style={{background:"#1e293b",borderRadius:12,padding:"14px",marginBottom:14}}>
              <div style={{color:"#94a3b8",fontSize:12,fontWeight:700,marginBottom:10}}>أرقام الدفعات</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <Field label="المنصورة — دفعة رقم">
                  <input style={LS.input} type="number" min="1" value={nc.batchMansura} onChange={setNcF("batchMansura")}/>
                </Field>
                <Field label="القاهرة — دفعة رقم">
                  <input style={LS.input} type="number" min="1" value={nc.batchCairo} onChange={setNcF("batchCairo")}/>
                </Field>
              </div>
            </div>
          ):(
            <Field label="دفعة رقم">
              <input style={LS.input} type="number" min="1"
                value={nc.branches?.[0]==="القاهرة"?nc.batchCairo:nc.batchMansura}
                onChange={e=>{
                  if(nc.branches?.[0]==="القاهرة") setNc(p=>({...p,batchCairo:e.target.value}));
                  else setNc(p=>({...p,batchMansura:e.target.value}));
                }}/>
            </Field>
          )}

          {nc.branches?.length===2?(
            <div style={{background:"#1e293b",borderRadius:12,padding:"14px",marginBottom:14}}>
              <div style={{color:"#94a3b8",fontSize:12,fontWeight:700,marginBottom:4}}>المشرفون</div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                <input type="checkbox" id="sameManager" checked={nc.sameManager}
                  onChange={e=>setNc(p=>({...p,sameManager:e.target.checked}))} style={{cursor:"pointer"}}/>
                <label htmlFor="sameManager" style={{color:"#94a3b8",fontSize:12,cursor:"pointer"}}>نفس المشرف للفرعين</label>
              </div>
              {nc.sameManager
                ?<Field label="المشرف الرئيسي"><input style={LS.input} value={nc.manager} onChange={setNcF("manager")}/></Field>
                :<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <Field label="مشرف المنصورة"><input style={LS.input} value={nc.managerMansura} onChange={setNcF("managerMansura")}/></Field>
                  <Field label="مشرف القاهرة"><input style={LS.input} value={nc.managerCairo} onChange={setNcF("managerCairo")}/></Field>
                </div>
              }
            </div>
          ):(
            <Field label="المشرف الرئيسي"><input style={LS.input} value={nc.manager} onChange={setNcF("manager")}/></Field>
          )}

          <Field label="كلمة المرور (مشتركة)">
            <div style={{position:"relative"}}>
              <input style={{...LS.input,paddingLeft:40}} type={showNcPw?"text":"password"} value={nc.password} onChange={setNcF("password")}/>
              <button onClick={()=>setShowNcPw(p=>!p)} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",fontSize:14}}>{showNcPw?(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>):(<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>)}</button>
            </div>
          </Field>
          <Field label="تأكيد كلمة المرور">
            <input style={LS.input} type="password" value={nc.confirmPw} onChange={setNcF("confirmPw")}/>
          </Field>

          <Field label="لوجو الدورة (اختياري)">
            <div style={{display:"flex",gap:10,alignItems:"center"}}>
              {nc.logo
                ?<img src={nc.logo} alt="logo" style={{width:44,height:44,borderRadius:10,objectFit:"contain",background:"#1e293b",padding:3}}/>
                :<div style={{width:44,height:44,borderRadius:10,background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",color:"#475569",fontSize:20}}>-</div>
              }
              <button style={{...D.pill,fontSize:11}} onClick={()=>logoRef.current.click()}>رفع لوجو</button>
              {nc.logo&&<button style={{...D.pill,fontSize:11,color:"#f87171"}} onClick={()=>setNc(p=>({...p,logo:""}))}>حذف</button>}
              <input ref={logoRef} type="file" accept="image/*" style={{display:"none"}} onChange={uploadCourseLogo}/>
            </div>
          </Field>

          {/* Preview */}
          {nc.courseTypeId&&nc.branches?.length>0&&(
            <div style={{background:"#1e3a5f",border:"1px solid #1d4ed8",borderRadius:10,padding:"12px 14px",marginBottom:14}}>
              <div style={{color:"#93c5fd",fontSize:12,fontWeight:700,marginBottom:6}}>ملخص ما سيتم إنشاؤه</div>
              {nc.branches.map(br=>(
                <div key={br} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:"#3b82f6",display:"inline-block"}}/>
                  <span style={{color:"#f1f5f9",fontSize:12}}>{selectedCourseType?.name} — دفعة {br==="القاهرة"?nc.batchCairo:nc.batchMansura} · {br}</span>
                </div>
              ))}
            </div>
          )}

          {err&&<p style={LS.err}>{err}</p>}
          <div style={{display:"flex",gap:10,marginTop:4}}>
            <button style={LS.loginBtn} onClick={doNew}>إنشاء {nc.branches?.length===2?"الدفعتين":"الدفعة"}</button>
            <button style={{...LS.loginBtn,background:"#1e293b",color:"#94a3b8"}} onClick={()=>{setMode("login");setErr("");}}>رجوع</button>
          </div>
        </>}
      </div>
    </div>
  );
}

/* ═══════════ SUPERVISOR DASHBOARD ═══════════ */
function SupervisorDashboard({db,setDB,session,onLogout,searchNav,onSearchNavDone}){
  const [page,setPage]=useState("entries");
  const [highlightStudentId,setHighlightStudentId]=useState(null);

  useEffect(()=>{
    if(!searchNav)return;
    if(searchNav.courseId===session.courseId){
      setPage(searchNav.page||"entries");
      if(searchNav.studentId) setHighlightStudentId(searchNav.studentId);
      onSearchNavDone&&onSearchNavDone();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[searchNav]);
  const [filterGroup,setFilterGroup]=useState("all");
  const [showAdd,setShowAdd]=useState(false);
  const [editEntry,setEditEntry]=useState(null);
  const [delEntry,setDelEntry]=useState(null);
  const [showComplete,setShowComplete]=useState(false);
  const [showArchive,setShowArchive]=useState(false);
  const [showChangePw,setShowChangePw]=useState(false);
  const [showReset,setShowReset]=useState(false);
  const [activeBranch,setActiveBranch]=useState("المنصورة");

  const course=(db.courses||[]).find(c=>c.id===session.courseId);
  const entries=(db.entries||[]).filter(e=>e.courseId===session.courseId);
  const books=(db.books||{})[session.courseId]||[];
  const students=(db.students||{})[session.courseId]||[];
  const assistants=course?.assistants||[];
  const courseBranches=course?.branches||(course?.branch==="المنصورة والقاهرة"?["المنصورة","القاهرة"]:[course?.branch||"المنصورة"]);
  const myArchive=(db.archivedBatches||[]).filter(b=>b.courseId===session.courseId);

  if(!course) return <div style={{color:"#fff",padding:40,fontFamily:"Cairo,sans-serif"}}>الدورة غير موجودة</div>;

  // [#2] إذا كانت الدورة مكتملة — أرشيف فقط
  if(course?.archived) return (
    <div style={D.root}>
      <header style={D.header}>
        <div style={D.hInner}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {course.logo?<img src={course.logo} alt="logo" style={{width:34,height:34,borderRadius:9,objectFit:"contain",background:"#1e293b",padding:2}}/>:<span style={D.hEmoji}></span>}
            <div><div style={D.hTitle}>{course.name} — دفعة {course.batchNumber}</div><div style={{...D.hSub,color:"#f59e0b"}}>مكتملة — للاستعراض فقط</div></div>
          </div>
          <button style={D.logoutBtn} onClick={onLogout}>خروج</button>
        </div>
      </header>
      <main style={D.main}>
        {myArchive.length===0
          ? <EmptyState icon="-" label="لا توجد دفعات مؤرشفة" sub="تواصل مع الإدارة"/>
          : <ArchiveModal batches={myArchive} onClose={onLogout} inline/>
        }
      </main>
    </div>
  );

  const calcStats=()=>{
    const inc=entries.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
    const exp=entries.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
    const bkP=entries.filter(e=>e.type==="book_print").reduce((s,e)=>s+num(e.amount),0);
    const bStats=bookStats(db,session.courseId);
    const bkS=bStats.revenue;
    const subPrice=course?.subPrice||0;
    // المتبقي الحقيقي لكل طالب = سعر الاشتراك - (المدفوع فعلياً + المعفى) — أبداً بالسالب
    const pendingSubs=subPrice>0
      ? students.filter(s=>!s.exempted).reduce((sum,s)=>sum+Math.max(0,subPrice-(s.paidAmount||0)-(s.exemptedAmount||0)),0)
      : 0;
    const totalExempted=students.reduce((sum,s)=>sum+(s.exempted?subPrice:(s.exemptedAmount||0)),0);
    const pendingBooks=bStats.pending;
    const pending=pendingSubs+pendingBooks;
    return{inc,exp,bkS,bkP,net:inc-exp+bkS-bkP,pending,pendingSubs,pendingBooks,totalExempted};
  };

  const branchEntries= courseBranches.length>1
    ? entries.filter(e=> activeBranch==="المنصورة"
        ? (e.type==="sub_mansura"||(!e.type?.startsWith("sub_")&&(e.branch===activeBranch||!e.branch)))
        : (e.type==="sub_cairo"||(!e.type?.startsWith("sub_")&&(e.branch===activeBranch||!e.branch))))
    : entries;

  const shown=branchEntries.filter(e=>filterGroup==="all"?true:typeById(e.type).group===filterGroup).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));

  const recalcStudentPayments=(entries, students, subPrice)=>{
    return students.map(s=>{
      const subEntries=entries.filter(e=>(e.type==="sub_mansura"||e.type==="sub_cairo")&&e.studentName===s.name);
      const fullExempt=subEntries.some(e=>e.exemption==="full");
      if(fullExempt) return {...s,paid:true,paidAmount:0,exemptedAmount:0,exempted:true,partialExempt:false};
      const paidAmount=subEntries.reduce((sum,e)=>sum+num(e.amount),0);
      const exemptedAmount=subEntries.reduce((sum,e)=>sum+num(e.exemptAmount||0),0);
      const hadPartial=subEntries.some(e=>e.exemption==="partial");
      // الطالب "دفع بالكامل" لو دفع فعلياً + المعفى = سعر الاشتراك (أو أكتر)
      const fullyPaid=subPrice===0?paidAmount>0:(paidAmount+exemptedAmount)>=subPrice;
      return {...s,paid:fullyPaid,paidAmount,exemptedAmount,exempted:false,partialExempt:hadPartial&&fullyPaid};
    });
  };

  const doAdd=(data)=>{
    const entry={id:uid(),courseId:session.courseId,...data,createdAt:nowISO(),createdBy:session.userId,edited:false,editLog:[]};
    setDB(p=>{
      let next={...p,entries:[entry,...(p.entries||[])]};
      if((data.type==="sub_mansura"||data.type==="sub_cairo")&&data.studentName){
        const sp=((p.courses||[]).find(c=>c.id===session.courseId))?.subPrice||0;
        const updatedStudents=recalcStudentPayments(next.entries.filter(e=>e.courseId===session.courseId),(p.students||{})[session.courseId]||[],sp);
        next.students={...next.students,[session.courseId]:updatedStudents};
      }
      return next;
    });
  };

  const doEdit=(id,changes)=>{
    setDB(p=>{
      const updatedEntries=(p.entries||[]).map(e=>e.id!==id?e:{...e,...changes,edited:true,editLog:[...(e.editLog||[]),{at:nowISO(),by:session.userId,changes}]});
      let next={...p,entries:updatedEntries};
      const edited=updatedEntries.find(e=>e.id===id);
      if(edited&&(edited.type==="sub_mansura"||edited.type==="sub_cairo")&&edited.studentName){
        const sp=((p.courses||[]).find(c=>c.id===session.courseId))?.subPrice||0;
        const updatedStudents=recalcStudentPayments(updatedEntries.filter(e=>e.courseId===session.courseId),(p.students||{})[session.courseId]||[],sp);
        next.students={...next.students,[session.courseId]:updatedStudents};
      }
      return next;
    });
  };

  const doDel=(entry)=>{
    setDB(p=>{
      const newEntries=(p.entries||[]).filter(e=>e.id!==entry.id);
      let next={...p,entries:newEntries};
      if((entry.type==="sub_mansura"||entry.type==="sub_cairo")&&entry.studentName){
        const sp=((p.courses||[]).find(c=>c.id===session.courseId))?.subPrice||0;
        const updatedStudents=recalcStudentPayments(newEntries.filter(e=>e.courseId===session.courseId),(p.students||{})[session.courseId]||[],sp);
        next.students={...next.students,[session.courseId]:updatedStudents};
      }
      const deleteLog={...entry,deletedAt:nowISO(),deletedBy:session.userId,deletedByCourse:course?.name};
      next.deletedEntries=[deleteLog,...(p.deletedEntries||[])].slice(0,500);
      return next;
    });
  };

  // [#2] إتمام الدفعة — تحويلها لأرشيف مقروء فقط
  const doComplete=(pw)=>{
    if(pw!==course.password){alert("كلمة المرور غير صحيحة");return;}
    const snapshot={id:uid(),courseId:session.courseId,courseName:course?.name,batchNumber:course?.batchNumber,branch:course?.branch,manager:course?.manager,completedAt:nowISO(),entries:[...entries],books:[...books],students:[...students],assistants:[...(course?.assistants||[])],trainers:[...(course?.trainers||[])],bookPayments:(db.bookPayments||{})[session.courseId]||{},gradeSchema:(db.gradeSchemas||{})[session.courseId]||[],gradeWeeks:(db.gradeWeeks||{})[session.courseId]||[]};
    setDB(p=>({...p,courses:(p.courses||[]).map(c=>c.id===session.courseId?{...c,archived:true}:c),archivedBatches:[...(p.archivedBatches||[]),snapshot]}));
    alert("تم إغلاق الدفعة وحفظها في الأرشيف.");
    setShowComplete(false);
    onLogout();
  };

  const doReset=(pw)=>{
    if(pw!==course.password){alert("كلمة المرور غير صحيحة");return false;}
    const snap={id:uid(),type:"reset_snapshot",courseId:session.courseId,courseName:course?.name,batchNumber:course?.batchNumber,savedAt:nowISO(),savedBy:session.userId,entries:[...entries],books:[...books],students:[...students],bookPayments:(db.bookPayments||{})[session.courseId]||{}};
    setDB(p=>({
      ...p,
      entries:(p.entries||[]).filter(e=>e.courseId!==session.courseId),
      students:{...p.students,[session.courseId]:(p.students[session.courseId]||[]).map(s=>({...s,paid:false,paidAmount:0,exempted:false}))},
      bookPayments:{...(p.bookPayments||{}),[session.courseId]:{}},
      resetSnapshots:[snap,...(p.resetSnapshots||[])].slice(0,10),
    }));
    return true;
  };

  const doRestore=(snapId)=>{
    setDB(p=>{
      const snap=(p.resetSnapshots||[]).find(s=>s.id===snapId);
      if(!snap)return p;
      return{...p,entries:[...snap.entries,...(p.entries||[]).filter(e=>e.courseId!==session.courseId)],students:{...p.students,[session.courseId]:snap.students},books:{...p.books,[session.courseId]:snap.books},bookPayments:{...(p.bookPayments||{}),[session.courseId]:snap.bookPayments||{}},resetSnapshots:(p.resetSnapshots||[]).filter(s=>s.id!==snapId)};
    });
  };

  const myResetSnapshots=(db.resetSnapshots||[]).filter(s=>s.courseId===session.courseId);
  const changePw=(old,nw)=>{if(old!==course.password){alert("كلمة المرور الحالية غير صحيحة");return false;}setDB(p=>({...p,courses:(p.courses||[]).map(c=>c.id===session.courseId?{...c,password:nw}:c)}));return true;};
  const toggleStudentPaid=(studentName)=>{
    const subPrice=(course?.subPrice)||0;
    if(!subPrice)return; // لا يُسمح بتسجيل دفع قبل تحديد سعر الاشتراك
    setDB(p=>{
      const list=(p.students||{})[session.courseId]||[];
      const student=list.find(s=>s.name===studentName);
      const newPaid=!student?.paid;
      let next={...p,students:{...p.students,[session.courseId]:list.map(s=>s.name===studentName?{...s,paid:newPaid}:s)}};
      if(newPaid){
        const brType=student?.branch==="القاهرة"?"sub_cairo":"sub_mansura";
        const entry={id:uid(),courseId:session.courseId,type:brType,amount:subPrice,studentName,createdAt:nowISO(),createdBy:session.userId,edited:false,editLog:[],autoFromStatus:true};
        next.entries=[entry,...(next.entries||[])];
      }
      return next;
    });
  };

  const myEdits=entries.filter(e=>e.edited&&(e.editLog||[]).some(l=>l.by===session.userId));
  const s=calcStats();

  return(
    <div style={D.root}>
      <header style={D.header}>
        <div style={D.hInner}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {course.logo
              ?<img src={course.logo} alt="logo" style={{width:36,height:36,borderRadius:9,objectFit:"contain",background:"#1e293b",padding:2}}/>
              :<span style={D.hEmoji}>R</span>
            }
            <div><div style={D.hTitle}>{course.name} — دفعة {course.batchNumber}</div><div style={D.hSub}>{course.branch} · {course.manager}</div></div>
          </div>
          <div className="sup-nav" style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
            {courseBranches.length>1&&(
              <div style={{display:"flex",background:"#1e293b",borderRadius:8,padding:2,gap:2,border:"1px solid #334155",marginLeft:4}}>
                {courseBranches.map(br=>(
                  <button key={br} style={{...D.pill,fontSize:11,padding:"3px 10px",border:"none",background:activeBranch===br?"#3b82f6":"transparent",color:activeBranch===br?"#fff":"#64748b",borderRadius:6,fontWeight:activeBranch===br?700:400}} onClick={()=>setActiveBranch(br)}>{br}</button>
                ))}
              </div>
            )}
            {[["entries","القيود"],["books","الكتب"],["students","الطلاب"],["grades","الدرجات"],["staff","الكوادر"],["chat","المحادثة"],["myedits","تعديلاتي"]].map(([p,l])=>(
              <NavPill key={p} active={page===p} onClick={()=>setPage(p)}>{l}</NavPill>
            ))}
            <button style={{...D.pill,background:"#052e16",color:"#4ade80",borderColor:"#166534"}} onClick={()=>setShowArchive(true)}>الأرشيف</button>
            <button style={{...D.pill,background:"#052e16",color:"#86efac",borderColor:"#15803d"}} onClick={()=>setShowComplete(true)}>إتمام</button>
            <button style={{...D.pill,background:"#1c0a0a",color:"#fca5a5",borderColor:"#7f1d1d"}} onClick={()=>setShowReset(true)}>تصفير</button>
            <button style={{...D.pill,fontSize:11}} onClick={()=>setShowChangePw(true)}>كلمة المرور</button>
            <button style={D.logoutBtn} onClick={onLogout}>خروج</button>
          </div>
        </div>
      </header>
      <style>{`
        @media(max-width:640px){
          .sup-nav{flex-wrap:nowrap!important;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:4px}
          .sup-nav button{white-space:nowrap;flex-shrink:0}
          .del-btn{opacity:1!important}
        }
      `}</style>
      <main style={D.main}>
        <div style={D.statsGrid} className="rw-stats-mobile">
          <MiniStat label="الوارد"          value={currency(s.inc)}  color="#22c55e"/>
          <MiniStat label="المصروف"         value={currency(s.exp)}  color="#ef4444"/>
          <MiniStat label="مبيعات الكتب"   value={currency(s.bkS)}  color="#0ea5e9"/>
          <MiniStat label="تكلفة طباعة"    value={currency(s.bkP)}  color="#f43f5e"/>
          <MiniStat label="الصافي"          value={currency(s.net)}  color={s.net>=0?"#a3e635":"#fb923c"} bold/>
          <MiniStat label="تحت التحصيل (اشتراكات)"    value={currency(s.pendingSubs)} color="#f59e0b"/>
          <MiniStat label="تحت التحصيل (كتب)"    value={currency(s.pendingBooks)} color="#fb923c"/>
          <MiniStat label="إجمالي تحت التحصيل"    value={currency(s.pending)} color="#ef4444" bold/>
          <MiniStat label="إعفاءات الاشتراكات"    value={currency(s.totalExempted)} color="#60a5fa"/>
        </div>

        {page==="entries"&&<>
          <div style={D.pageHead}>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[["all","الكل"],["income","إيرادات"],["expense","مصروفات"],["books","كتب"]].map(([v,l])=>(
                <button key={v} style={{...D.pill,...(filterGroup===v?D.pillActive:{})}} onClick={()=>setFilterGroup(v)}>{l}</button>
              ))}
            </div>
            <Btn onClick={()=>setShowAdd(true)}>+ قيد جديد</Btn>
          </div>
          {shown.length===0&&<EmptyState icon="—" label="لا توجد قيود" sub="أضف أول قيد"/>}
          <div style={D.list}>{shown.map(e=><EntryRow key={e.id} entry={e} books={books} onEdit={()=>setEditEntry(e)} onDelete={()=>setDelEntry(e)}/>)}</div>
        </>}
        {page==="books"    &&<BooksPage courseId={session.courseId} books={books} setDB={setDB} students={students} db={db}/>}
        {page==="students" &&<StudentsPage courseId={session.courseId} students={students} setDB={setDB} onTogglePaid={toggleStudentPaid} subPrice={course?.subPrice||0} activeBranch={activeBranch} courseBranches={courseBranches} highlightStudentId={highlightStudentId} onHighlightDone={()=>setHighlightStudentId(null)}/>}
        {/* [#9] داشبورد الدرجات — يتضمن الحضور */}
        {page==="grades"   &&<GradesPage courseId={session.courseId} students={students} db={db} setDB={setDB}/>}
        {page==="staff"    &&<StaffPage course={course} setDB={setDB}/>}
        {page==="chat"     &&<ChatPage db={db} setDB={setDB} session={session} courseName={course.name+" — دفعة "+course.batchNumber}/>}
        {page==="myedits"  &&<MyEditsPage entries={myEdits} userId={session.userId}/>}
      </main>
      {showAdd    &&<AddEntryModal books={books} students={students} assistants={assistants} subPrice={course?.subPrice||0} activeBranch={activeBranch} courseBranches={courseBranches} onAdd={doAdd} onClose={()=>setShowAdd(false)}/>}
      {editEntry  &&<EditEntryModal entry={editEntry} students={students} assistants={assistants} coursePassword={course.password} onSave={ch=>{doEdit(editEntry.id,ch);setEditEntry(null);}} onClose={()=>setEditEntry(null)}/>}
      {delEntry   &&<DeleteConfirmModal entry={delEntry} coursePassword={course.password} onConfirm={()=>{doDel(delEntry);setDelEntry(null);}} onClose={()=>setDelEntry(null)}/>}
      {showComplete&&<CompleteBatchModal coursePassword={course.password} onConfirm={doComplete} onClose={()=>setShowComplete(false)}/>}
      {showReset   &&<ResetBatchModal coursePassword={course.password} snapshots={myResetSnapshots} onReset={doReset} onRestore={doRestore} onClose={()=>setShowReset(false)}/>}
      {showArchive &&<ArchiveModal batches={myArchive} onClose={()=>setShowArchive(false)}/>}
      {showChangePw&&<ChangePwModal onSave={changePw} onClose={()=>setShowChangePw(false)}/>}
    </div>
  );
}

/* ═══════════ ADMIN DASHBOARD ═══════════ */
function AdminDashboard({db,setDB,session,onLogout,searchNav,onSearchNavDone}){
  const [tab,setTab]=useState("overview");

  useEffect(()=>{
    if(!searchNav)return;
    if(searchNav.type==="course"){setTab("courses");setSelCourse(searchNav.courseId);}
    else if(searchNav.type==="entry"){setTab("allentries");}
    else if(searchNav.type==="student"){setTab("courses");setSelCourse(searchNav.courseId);}
    onSearchNavDone&&onSearchNavDone();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[searchNav]);
  const [selCourse,setSelCourse]=useState(null);
  const [showChangePw,setShowChangePw]=useState(false);
  const logoRef=useRef();

  const allEntries=db.entries||[];
  const tInc=allEntries.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
  const tExp=allEntries.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
  const tBkP=allEntries.filter(e=>e.type==="book_print").reduce((s,e)=>s+num(e.amount),0);
  const tBkS=(db.courses||[]).reduce((sum,c)=>sum+bookStats(db,c.id).revenue,0);
  const edited=allEntries.filter(e=>e.edited).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));

  const changeAdminPw=(old,nw)=>{const a=(db.users||[]).find(u=>u.id==="admin");if(old!==a?.password){alert("كلمة المرور الحالية غير صحيحة");return false;}setDB(p=>({...p,users:(p.users||[]).map(u=>u.id==="admin"?{...u,password:nw}:u)}));return true;};
  const uploadCenterLogo=e=>{if(!e.target.files[0])return;const r=new FileReader();r.onload=ev=>setDB(p=>({...p,centerLogo:ev.target.result}));r.readAsDataURL(e.target.files[0]);e.target.value="";};

  return(
    <div style={D.root}>
      <header style={{...D.header,background:"#1a0a2e"}}>
        <div style={D.hInner}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{position:"relative",cursor:"pointer"}} onClick={()=>logoRef.current.click()} title="تغيير لوجو المركز">
              {db.centerLogo
                ?<img src={db.centerLogo} alt="logo" style={{width:38,height:38,borderRadius:10,objectFit:"contain",background:"#1e293b",padding:3,border:"1px solid #334155"}}/>
                :<div style={{...D.hEmoji,background:"#7c3aed",border:"1px dashed #9333ea",cursor:"pointer"}}>A</div>
              }
            </div>
            <input ref={logoRef} type="file" accept="image/*" style={{display:"none"}} onChange={uploadCenterLogo}/>
            <div><div style={D.hTitle}>لوحة الإدارة المركزية</div><div style={D.hSub}>بيانات حية — مزامنة فورية</div></div>
          </div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
            {[["overview","عامة"],["courses","الدورات"],["coursetypes","إدارة الدورات"],["allentries","القيود"],["edits","التعديلات"],["deleted","المحذوفات"],["archive","الأرشيف"],["chat","المحادثة"]].map(([t,l])=>(
              <NavPill key={t} active={tab===t} onClick={()=>setTab(t)}>{l}</NavPill>
            ))}
            <button style={{...D.pill,fontSize:11}} onClick={()=>setShowChangePw(true)}>كلمة المرور</button>
            <button style={D.logoutBtn} onClick={onLogout}>خروج</button>
          </div>
        </div>
      </header>
      <main style={D.main}>
        {tab==="overview"&&<>
          <div style={D.statsGrid} className="rw-stats-mobile">
            <MiniStat label="إجمالي الوارد"  value={currency(tInc)} color="#22c55e" bold/>
            <MiniStat label="إجمالي المصروف" value={currency(tExp)} color="#ef4444" bold/>
            <MiniStat label="مبيعات الكتب"   value={currency(tBkS)} color="#0ea5e9" bold/>
            <MiniStat label="تكلفة الطباعة"  value={currency(tBkP)} color="#f43f5e" bold/>
            <MiniStat label="الصافي الكلي"   value={currency(tInc-tExp+tBkS-tBkP)} color="#a3e635" bold/>
          </div>
          <h2 style={D.secTitle}>الدورات النشطة</h2>
          {(()=>{
            const activeCourses=(db.courses||[]).filter(c=>!c.archived);
            // تجميع الدورات حسب الاسم
            const grouped={};
            activeCourses.forEach(c=>{
              if(!grouped[c.name]) grouped[c.name]=[];
              grouped[c.name].push(c);
            });
            return(
              <div style={D.courseGrid}>
                {Object.entries(grouped).map(([name,courses])=>{
                  const allEs=courses.flatMap(c=>allEntries.filter(e=>e.courseId===c.id));
                  const ci=allEs.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
                  const ce=allEs.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
                  const cp=allEs.filter(e=>e.type==="book_print").reduce((s,e)=>s+num(e.amount),0);
                  const cs=courses.reduce((s,c)=>s+bookStats(db,c.id).revenue,0);
                  const net=ci-ce+cs-cp;
                  const logo=courses[0]?.logo||"";
                  const isMulti=courses.length>1;
                  return(
                    <div key={name} className="rw-course-card"
                      style={{...D.courseCard,background:isMulti?"linear-gradient(135deg,#0f172a,#111827)":D.courseCard.background,cursor:"pointer"}}
                      onClick={()=>{
                        if(courses.length===1){setSelCourse(courses[0].id);setTab("courses");}
                        else{setSelCourse(courses[0].id);setTab("courses");}
                      }}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                        {logo
                          ?<img src={logo} alt="" style={{width:32,height:32,borderRadius:8,objectFit:"contain",background:"#1e293b",padding:3}}/>
                          :<div style={{width:32,height:32,borderRadius:8,background:"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,color:"#60a5fa",fontSize:13}}>{name[0]}</div>
                        }
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:700,color:"#f1f5f9",fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
                          {isMulti
                            ?<div style={{color:"#60a5fa",fontSize:11}}>{courses.length} دفعات نشطة · {courses.map(c=>c.branch).join(" & ")}</div>
                            :<div style={{color:"#64748b",fontSize:11}}>دفعة {courses[0].batchNumber} · {courses[0].branch} · {courses[0].manager}</div>
                          }
                        </div>
                      </div>
                      {isMulti&&(
                        <div style={{display:"flex",gap:4,marginBottom:8,flexWrap:"wrap"}}>
                          {courses.map(c=>(
                            <span key={c.id} onClick={e=>{e.stopPropagation();setSelCourse(c.id);setTab("courses");}}
                              style={{background:"#1e3a5f",border:"1px solid #1d4ed855",borderRadius:6,padding:"2px 8px",color:"#93c5fd",fontSize:11,cursor:"pointer"}}>
                              دفعة {c.batchNumber} — {c.branch}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{display:"flex",gap:10,fontSize:12,borderTop:"1px solid #1e293b",paddingTop:8}}>
                        <span style={{color:"#22c55e"}}>{currency(ci)}</span>
                        <span style={{color:"#ef4444"}}>{currency(ce)}</span>
                        <span style={{color:net>=0?"#4ade80":"#f87171",fontWeight:700,marginRight:"auto"}}>{currency(net)}</span>
                        <span style={{color:"#64748b",fontSize:11}}>{courses.reduce((s,c)=>s+((db.students||{})[c.id]||[]).length,0)} طالب</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </>}
        {tab==="courses"    &&<AdminCourseView db={db} setDB={setDB} allEntries={allEntries} selCourse={selCourse} setSelCourse={setSelCourse}/>}
        {/* [#1] إدارة أنواع الدورات */}
        {tab==="coursetypes"&&<CourseTypesManager db={db} setDB={setDB}/>}
        {tab==="allentries" &&<><h2 style={D.secTitle}>جميع القيود ({allEntries.length})</h2><div style={D.list}>{[...allEntries].sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).map(e=>{const c=(db.courses||[]).find(x=>x.id===e.courseId);return<EntryRow key={e.id} entry={e} books={(db.books||{})[e.courseId]||[]} showCourse course={c} adminView/>;})}</div></>}
        {tab==="edits"      &&<><h2 style={D.secTitle}>سجل التعديلات ({edited.length})</h2>{edited.length===0&&<EmptyState icon="—" label="لا توجد تعديلات" sub=""/>}<EditLogList entries={edited} courses={db.courses||[]} setDB={setDB}/></>}
        {tab==="deleted"    &&<DeletedEntriesView deletedEntries={db.deletedEntries||[]} courses={db.courses||[]} setDB={setDB}/>}
        {tab==="archive"    &&<AdminArchiveView batches={db.archivedBatches||[]} db={db} setDB={setDB}/>}
        {tab==="chat"       &&<ChatPage db={db} setDB={setDB} session={{userId:"admin",role:"admin"}} courseName="الإدارة المركزية"/>}
      </main>
      {showChangePw&&<ChangePwModal onSave={changeAdminPw} onClose={()=>setShowChangePw(false)}/>}
    </div>
  );
}

/* ═══════════ COURSE TYPES MANAGER + COURSE DELETION (الإدارة فقط) ═══════════ */
function CourseTypesManager({db,setDB}){
  const [name,setName]=useState("");
  const [expandedType,setExpandedType]=useState(null);
  const types=db.courseTypes||[];

  const addType=()=>{
    if(!name.trim())return;
    setDB(p=>({...p,courseTypes:[...(p.courseTypes||[]),{id:uid(),name:name.trim(),createdAt:nowISO()}]}));
    setName("");
  };

  const deleteType=async(id)=>{
    const activeCourses=(db.courses||[]).filter(c=>c.courseTypeId===id&&!c.archived);
    if(activeCourses.length>0){
      alert("لا يمكن حذف هذا النوع — يوجد "+activeCourses.length+" دفعة نشطة تستخدمه. أكمل الدورات أولاً ثم احذف النوع.");
      return;
    }
    
    setDB(p=>({
      ...p,
      courseTypes:(p.courseTypes||[]).filter(t=>t.id!==id),
      archivedBatches:(p.archivedBatches||[]).filter(b=>b.courseTypeId!==id),
    }));
  };

  const deleteCourse=async(courseId)=>{
    const c=(db.courses||[]).find(x=>x.id===courseId);
    if(!c)return;
    const ok=await showConfirm("سيتم حذف جميع بيانات هذه الدفعة نهائياً ولا يمكن التراجع",{title:`حذف: ${c.name} — دفعة ${c.batchNumber} (${c.branch})`,danger:true,confirmText:"حذف نهائي"});
    if(!ok)return;
    setDB(p=>{
      // بناء objects جديدة بدون الـ courseId (Firebase لا يدعم delete)
      const newStudents=Object.fromEntries(Object.entries(p.students||{}).filter(([k])=>k!==courseId));
      const newBooks=Object.fromEntries(Object.entries(p.books||{}).filter(([k])=>k!==courseId));
      const newAttendance=Object.fromEntries(Object.entries(p.attendance||{}).filter(([k])=>k!==courseId));
      const newGradeSchemas=Object.fromEntries(Object.entries(p.gradeSchemas||{}).filter(([k])=>k!==courseId));
      const newGradeRecords=Object.fromEntries(Object.entries(p.gradeRecords||{}).filter(([k])=>k!==courseId));
      const newGradeWeeks=Object.fromEntries(Object.entries(p.gradeWeeks||{}).filter(([k])=>k!==courseId));
      const newBookPayments=Object.fromEntries(Object.entries(p.bookPayments||{}).filter(([k])=>k!==courseId));
      return{
        ...p,
        courses:(p.courses||[]).filter(x=>x.id!==courseId),
        entries:(p.entries||[]).filter(e=>e.courseId!==courseId),
        chatMessages:(p.chatMessages||[]).filter(m=>!m.convKey?.includes(courseId)),
        students:newStudents,
        books:newBooks,
        bookPayments:newBookPayments,
        attendance:newAttendance,
        gradeSchemas:newGradeSchemas,
        gradeRecords:newGradeRecords,
        gradeWeeks:newGradeWeeks,
      };
    });
  };

  const deleteArchivedBatch=(batchId)=>{
    const b=(db.archivedBatches||[]).find(x=>x.id===batchId);
    if(!b)return;
    
    setDB(p=>({...p,archivedBatches:(p.archivedBatches||[]).filter(x=>x.id!==batchId)}));
  };

  return(<>
    <h2 style={D.secTitle}>إدارة الدورات</h2>
    <p style={{color:"#64748b",fontSize:13,marginBottom:16}}>
      أضف أنواع الدورات ليتمكن المشرفون من إنشاء دفعات. يمكنك هنا حذف أي دورة أو دفعة بالضغط على السهم لعرض تفاصيلها.
    </p>

    <div style={{...D.card,marginBottom:20}}>
      <h3 style={{color:"#94a3b8",fontSize:13,marginBottom:12}}>إضافة نوع دورة جديد</h3>
      <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
        <Field label="اسم الدورة *" style={{margin:0,flex:1}}>
          <input style={D.input} placeholder="مثال: دورة التجويد" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addType()}/>
        </Field>
        <Btn onClick={addType}>إضافة</Btn>
      </div>
    </div>

    {types.length===0&&<EmptyState icon="—" label="لا توجد أنواع دورات بعد" sub="أضف أنواع الدورات ليتمكن المشرفون من إنشاء دفعات"/>}

    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {types.map(t=>{
        const activeCourses=(db.courses||[]).filter(c=>c.courseTypeId===t.id&&!c.archived);
        const archivedBatches=(db.archivedBatches||[]).filter(b=>b.courseTypeId===t.id);
        const isExpanded=expandedType===t.id;
        return(
          <div key={t.id} style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,overflow:"hidden"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 16px",cursor:"pointer"}} onClick={()=>setExpandedType(isExpanded?null:t.id)}>
              <div style={{width:40,height:40,borderRadius:10,background:"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <span style={{color:"#60a5fa",fontWeight:800,fontSize:18}}>{t.name[0]}</span>
              </div>
              <div style={{flex:1}}>
                <div style={{color:"#f1f5f9",fontWeight:700,fontSize:14}}>{t.name}</div>
                <div style={{display:"flex",gap:12,marginTop:3,fontSize:12}}>
                  <span style={{color:"#22c55e"}}>{activeCourses.length} نشطة</span>
                  <span style={{color:"#64748b"}}>{archivedBatches.length} مؤرشفة</span>
                  <span style={{color:"#475569"}}>أضيف: {fmtDate(t.createdAt)}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <button style={{...D.pill,background:"#450a0a",color:"#f87171",borderColor:"#7f1d1d",fontSize:11}}
                  onClick={e=>{e.stopPropagation();deleteType(t.id);}}>
                  حذف النوع
                </button>
                <span style={{color:"#64748b",fontSize:11,display:"inline-block",transform:isExpanded?"rotate(180deg)":"rotate(0)",transition:"transform .2s"}}>▼</span>
              </div>
            </div>

            {isExpanded&&(
              <div style={{borderTop:"1px solid #1e293b",background:"#060c18",padding:"12px 16px"}}>
                {activeCourses.length===0&&archivedBatches.length===0&&(
                  <div style={{color:"#475569",fontSize:12,textAlign:"center",padding:"8px 0"}}>لا توجد دفعات لهذا النوع بعد</div>
                )}
                {activeCourses.length>0&&(
                  <>
                    <div style={{color:"#22c55e",fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:1}}>الدفعات النشطة</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                      {activeCourses.map(c=>(
                        <div key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#0f172a",borderRadius:10,border:"1px solid #1e293b"}}>
                          <div style={{flex:1}}>
                            <div style={{color:"#f1f5f9",fontSize:13,fontWeight:600}}>دفعة {c.batchNumber} — {c.branch}</div>
                            <div style={{color:"#64748b",fontSize:11,marginTop:2}}>مشرف: {c.manager} · أُنشئت: {fmtDate(c.createdAt)}</div>
                          </div>
                          <span style={{background:"#052e16",color:"#4ade80",fontSize:10,padding:"2px 8px",borderRadius:6,flexShrink:0}}>نشطة</span>
                          <button style={{...D.pill,background:"#450a0a",color:"#f87171",borderColor:"#7f1d1d",fontSize:11}}
                            onClick={()=>deleteCourse(c.id)}>
                            حذف
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {archivedBatches.length>0&&(
                  <>
                    <div style={{color:"#64748b",fontSize:11,fontWeight:700,marginBottom:8,letterSpacing:1}}>الدفعات المؤرشفة</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {archivedBatches.map(b=>(
                        <div key={b.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:"#0f172a",borderRadius:10,border:"1px solid #1e293b"}}>
                          <div style={{flex:1}}>
                            <div style={{color:"#94a3b8",fontSize:13,fontWeight:600}}>دفعة {b.batchNumber} — {b.branch}</div>
                            <div style={{color:"#475569",fontSize:11,marginTop:2}}>اكتملت: {fmtDate(b.completedAt)}</div>
                          </div>
                          <span style={{background:"#111827",color:"#64748b",fontSize:10,padding:"2px 8px",borderRadius:6,flexShrink:0}}>مؤرشفة</span>
                          <button style={{...D.pill,background:"#450a0a",color:"#f87171",borderColor:"#7f1d1d",fontSize:11}}
                            onClick={()=>deleteArchivedBatch(b.id)}>
                            حذف
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </>);
}

/* ═══════════ CHAT PAGE — محادثات خاصة بين الإدارة وكل مشرف ═══════════ */
function ChatPage({db,setDB,session,courseName}){
  const allCourses=(db.courses||[]).filter(c=>!c.archived);
  const isAdmin=session.role==="admin";
  const myId=isAdmin?"admin":(session.courseId||"unknown");
  const mySenderName=isAdmin?"الإدارة المركزية":courseName;

  const conversations=isAdmin
    ? allCourses.map(c=>({id:c.id,name:c.name+" — دفعة "+c.batchNumber,sub:c.branch+" · "+c.manager,role:"supervisor",logo:c.logo||""}))
    : [
        {id:"admin",name:"الإدارة المركزية",sub:"",role:"admin",logo:(db.centerLogo||"")},
        ...allCourses.filter(c=>c.id!==session.courseId).map(c=>({id:c.id,name:c.name+" — دفعة "+c.batchNumber,sub:c.branch+" · "+c.manager,role:"supervisor",logo:c.logo||""}))
      ];

  const getConvKey=(a,b)=>[a,b].sort().join("_");

  const [activeConvId,setActiveConvId]=useState(null);
  const [msg,setMsg]=useState("");
  const [imageFile,setImageFile]=useState(null);
  const [lightboxSrc,setLightboxSrc]=useState(null);
  const [confirmDelId,setConfirmDelId]=useState(null);
  const [editingMsgId,setEditingMsgId]=useState(null);
  const [editText,setEditText]=useState("");
  const [isMobile,setIsMobile]=useState(typeof window!=="undefined"&&window.innerWidth<=720);
  const bottomRef=useRef();
  const inputRef=useRef();
  const fileRef=useRef();

  useEffect(()=>{
    const onResize=()=>setIsMobile(window.innerWidth<=720);
    window.addEventListener("resize",onResize);
    return ()=>window.removeEventListener("resize",onResize);
  },[]);

  const allMessages=db.chatMessages||[];
  const convKey=activeConvId?getConvKey(myId,activeConvId):null;
  const convMessages=convKey
    ? [...allMessages].filter(m=>m.convKey===convKey).sort((a,b)=>new Date(a.createdAt)-new Date(b.createdAt))
    : [];

  const unreadCount=(convId)=>{
    const key=getConvKey(myId,convId);
    return allMessages.filter(m=>m.convKey===key&&m.senderId!==myId&&!m.readBy?.includes(myId)).length;
  };
  const totalUnread=conversations.reduce((s,c)=>s+unreadCount(c.id),0);

  useEffect(()=>{
    if(bottomRef.current) bottomRef.current.scrollIntoView({behavior:"smooth"});
  },[convMessages.length,activeConvId]);

  useEffect(()=>{
    if(!convKey)return;
    const unread=allMessages.filter(m=>m.convKey===convKey&&m.senderId!==myId&&!m.readBy?.includes(myId));
    if(unread.length===0)return;
    setDB(p=>({...p,chatMessages:(p.chatMessages||[]).map(m=>
      m.convKey===convKey&&m.senderId!==myId&&!m.readBy?.includes(myId)
        ?{...m,readBy:[...(m.readBy||[]),myId]}:m
    )}));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activeConvId,convMessages.length]);

  const toBase64=(file)=>new Promise((res,rej)=>{const r=new FileReader();r.onload=e=>res(e.target.result);r.onerror=rej;r.readAsDataURL(file);});

  const sendMsg=async()=>{
    const text=msg.trim();
    if(!text&&!imageFile)return;
    if(!activeConvId)return;
    let imageData=null;
    if(imageFile) imageData=await toBase64(imageFile);
    const newMsg={id:uid(),convKey:getConvKey(myId,activeConvId),text:text||"",image:imageData||null,audio:null,senderName:mySenderName,senderRole:session.role,senderId:myId,createdAt:nowISO(),readBy:[myId]};
    setDB(p=>({...p,chatMessages:[...(p.chatMessages||[]),newMsg]}));
    setMsg("");setImageFile(null);
    inputRef.current?.focus();
  };

  const deleteMsg=(id)=>{
    setDB(p=>({...p,chatMessages:(p.chatMessages||[]).filter(m=>m.id!==id)}));
    setConfirmDelId(null);
  };

  const editMsg=(id,newText)=>{
    if(!newText.trim())return;
    setDB(p=>({...p,chatMessages:(p.chatMessages||[]).map(m=>m.id===id?{...m,text:newText.trim(),edited:true}:m)}));
    setEditingMsgId(null);setEditText("");
  };

  const sendAudio=async(audioBlob)=>{
    const reader=new FileReader();
    reader.onload=e=>{
      const newMsg={id:uid(),convKey:getConvKey(myId,activeConvId),text:"",image:null,audio:e.target.result,senderName:mySenderName,senderRole:session.role,senderId:myId,createdAt:nowISO(),readBy:[myId]};
      setDB(p=>({...p,chatMessages:[...(p.chatMessages||[]),newMsg]}));
    };
    reader.readAsDataURL(audioBlob);
  };

  const activeConv=conversations.find(c=>c.id===activeConvId);

  // تجميع الرسائل حسب التاريخ
  const groupedMessages=convMessages.reduce((groups,m)=>{
    const dateKey=new Date(m.createdAt).toLocaleDateString("ar-EG",{weekday:"long",year:"numeric",month:"long",day:"numeric"});
    if(!groups[dateKey]) groups[dateKey]=[];
    groups[dateKey].push(m);
    return groups;
  },{});

  return(
    <div style={{display:"flex",height:"calc(100vh - 180px)",minHeight:520,background:"#0a0f1a",borderRadius:16,border:"1px solid #1e293b",overflow:"hidden",boxShadow:"0 8px 32px rgba(0,0,0,.4)"}}>

      {lightboxSrc&&(
        <div onClick={()=>setLightboxSrc(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.92)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center",cursor:"zoom-out"}}>
          <img src={lightboxSrc} alt="" style={{maxWidth:"90vw",maxHeight:"90vh",borderRadius:12,boxShadow:"0 0 60px rgba(0,0,0,.8)"}} onClick={e=>e.stopPropagation()}/>
          <button onClick={()=>setLightboxSrc(null)} style={{position:"fixed",top:20,left:20,background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:40,height:40,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      )}

      {confirmDelId&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:400,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:"24px 28px",width:300,textAlign:"center"}}>
            <div style={{color:"#f1f5f9",fontWeight:700,fontSize:15,marginBottom:8}}>حذف الرسالة؟</div>
            <div style={{color:"#64748b",fontSize:13,marginBottom:20}}>لن تتمكن من استعادتها</div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <button style={{...D.btn,background:"#dc2626"}} onClick={()=>deleteMsg(confirmDelId)}>حذف</button>
              <button style={{...D.btn,background:"#1e293b",color:"#94a3b8"}} onClick={()=>setConfirmDelId(null)}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar — يظهر على الموبايل فقط إذا لم تُفتح محادثة */}
      {(!isMobile||!activeConvId)&&(
      <div style={{width:isMobile?"100%":270,borderLeft:isMobile?"none":"1px solid #1e293b",display:"flex",flexDirection:"column",flexShrink:0,background:"#060c18"}}>
        <div style={{padding:"14px 16px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{color:"#f1f5f9",fontWeight:700,fontSize:14}}>المحادثات</span>
          {totalUnread>0&&<span style={{background:"#ef4444",color:"#fff",borderRadius:10,padding:"2px 8px",fontSize:11,fontWeight:700}}>{totalUnread}</span>}
        </div>
        <div style={{flex:1,overflowY:"auto"}}>
          {conversations.length===0&&(
            <div style={{padding:"24px 16px",color:"#475569",fontSize:12,textAlign:"center",lineHeight:1.6}}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#334155" strokeWidth="1.5" style={{display:"block",margin:"0 auto 8px"}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              لا توجد دورات نشطة
            </div>
          )}
          {conversations.map(c=>{
            const unread=unreadCount(c.id);
            const key=getConvKey(myId,c.id);
            const lastMsg=[...allMessages].filter(m=>m.convKey===key).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt))[0];
            const isActive=activeConvId===c.id;
            return(
              <div key={c.id} onClick={()=>setActiveConvId(c.id)}
                style={{padding:"12px 16px",borderBottom:"1px solid #111827",cursor:"pointer",background:isActive?"#0f2044":"transparent",borderRight:isActive?"3px solid #3b82f6":"3px solid transparent",transition:"all .15s"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  {/* Avatar */}
                  <div style={{width:40,height:40,borderRadius:"50%",overflow:"hidden",flexShrink:0,background:c.role==="admin"?"#4c1d95":"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid "+(c.role==="admin"?"#7c3aed33":"#1d4ed833")}}>
                    {c.logo?<img src={c.logo} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                      :<span style={{color:c.role==="admin"?"#c084fc":"#60a5fa",fontWeight:800,fontSize:15}}>{c.name[0]}</span>}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:4}}>
                      <span style={{color:isActive?"#f1f5f9":"#94a3b8",fontSize:12,fontWeight:isActive?700:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>
                      {lastMsg&&<span style={{color:"#475569",fontSize:9,flexShrink:0}}>{new Date(lastMsg.createdAt).toLocaleTimeString("ar-EG",{hour:"2-digit",minute:"2-digit"})}</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:2}}>
                      <span style={{color:"#475569",fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>
                        {lastMsg?(lastMsg.audio?"رسالة صوتية":lastMsg.image?"صورة":lastMsg.text):(c.sub||"ابدأ المحادثة")}
                      </span>
                      {unread>0&&<span style={{background:"#22c55e",color:"#fff",borderRadius:"50%",width:18,height:18,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0}}>{unread}</span>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Main area — يظهر على الموبايل فقط عند فتح محادثة */}
      {(!isMobile||activeConvId)&&(
      <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0,background:"#060d1a"}}>
        {/* Header */}
        {activeConv?(
          <div style={{padding:"10px 16px",borderBottom:"1px solid #1e293b",display:"flex",alignItems:"center",gap:12,background:"#0a1628"}}>
            {isMobile&&(
              <button onClick={()=>setActiveConvId(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#94a3b8",padding:4,display:"flex",flexShrink:0}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              </button>
            )}
            <div style={{width:36,height:36,borderRadius:"50%",overflow:"hidden",flexShrink:0,background:activeConv.role==="admin"?"#4c1d95":"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {activeConv.logo?<img src={activeConv.logo} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}}/>
                :<span style={{color:activeConv.role==="admin"?"#c084fc":"#60a5fa",fontWeight:800,fontSize:14}}>{activeConv.name[0]}</span>}
            </div>
            <div>
              <div style={{color:"#f1f5f9",fontWeight:700,fontSize:13}}>{activeConv.name}</div>
              {activeConv.sub&&<div style={{color:"#64748b",fontSize:11}}>{activeConv.sub}</div>}
            </div>
          </div>
        ):(
          <div style={{padding:"14px 16px",borderBottom:"1px solid #1e293b",color:"#475569",fontSize:13,background:"#0a1628"}}>اختر محادثة</div>
        )}

        {/* Messages */}
        <div style={{flex:1,overflowY:"auto",padding:"16px 12px",display:"flex",flexDirection:"column",gap:0,
          backgroundImage:"radial-gradient(#0f2044 1px,transparent 1px)",backgroundSize:"24px 24px",backgroundAttachment:"local"}}>
          {!activeConvId&&(
            <div style={{textAlign:"center",color:"#334155",margin:"auto",padding:"40px 20px"}}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="1.5" style={{display:"block",margin:"0 auto 12px"}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              اختر محادثة للبدء
            </div>
          )}
          {activeConvId&&convMessages.length===0&&(
            <div style={{textAlign:"center",color:"#334155",margin:"auto",padding:"40px 20px"}}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="1.5" style={{display:"block",margin:"0 auto 12px"}}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              لا رسائل بعد — ابدأ المحادثة
            </div>
          )}

          {Object.entries(groupedMessages).map(([dateKey,msgs])=>(
            <div key={dateKey}>
              {/* Date separator */}
              <div style={{textAlign:"center",margin:"12px 0"}}>
                <span style={{background:"#0f172a",border:"1px solid #1e293b",borderRadius:20,padding:"3px 12px",color:"#475569",fontSize:10}}>{dateKey}</span>
              </div>
              {msgs.map(m=>{
                const isMe=m.senderId===myId;
                const canDelete=isMe; // يحذف فقط رسائله
                return(
                  <div key={m.id} style={{display:"flex",flexDirection:isMe?"row-reverse":"row",gap:6,alignItems:"flex-end",marginBottom:4}}>
                    {/* Avatar */}
                    {!isMe&&(
                      <div style={{width:26,height:26,borderRadius:"50%",background:m.senderRole==="admin"?"#4c1d95":"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:10,fontWeight:700,color:m.senderRole==="admin"?"#c084fc":"#60a5fa",marginBottom:2}}>
                        {m.senderName[0]}
                      </div>
                    )}
                    {/* Bubble */}
                    <div style={{maxWidth:"65%",position:"relative"}} className="msg-wrap">
                      {!isMe&&<div style={{color:"#60a5fa",fontSize:10,marginBottom:2,paddingRight:4}}>{m.senderName}</div>}
                      {editingMsgId===m.id?(
                        <div style={{padding:8,borderRadius:"16px 16px 2px 16px",background:"#1e3a5f",border:"1px solid #3b82f6"}}>
                          <textarea autoFocus value={editText} onChange={e=>setEditText(e.target.value)}
                            style={{...D.input,resize:"none",minHeight:40,background:"#0f172a",border:"1px solid #1e293b",fontSize:13,marginBottom:6}}
                            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();editMsg(m.id,editText);}if(e.key==="Escape")setEditingMsgId(null);}}/>
                          <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                            <button onClick={()=>setEditingMsgId(null)} style={{...D.pill,fontSize:11,padding:"4px 10px"}}>إلغاء</button>
                            <button onClick={()=>editMsg(m.id,editText)} style={{...D.pill,fontSize:11,padding:"4px 10px",background:"#1d4ed8",borderColor:"#1d4ed8",color:"#fff"}}>حفظ</button>
                          </div>
                        </div>
                      ):(
                      <div style={{
                        padding:m.image||m.audio?"8px":"10px 14px",
                        borderRadius:isMe?"16px 16px 2px 16px":"16px 16px 16px 2px",
                        background:isMe?"#1d4ed8":"#1e293b",
                        boxShadow:"0 1px 4px rgba(0,0,0,.3)",
                        color:"#f1f5f9",fontSize:13,lineHeight:1.5,
                        direction:"rtl",whiteSpace:"pre-wrap",wordBreak:"break-word",
                        position:"relative",
                      }}>
                        {/* صورة */}
                        {m.image&&(
                          <img src={m.image} alt="صورة" onClick={()=>setLightboxSrc(m.image)}
                            style={{maxWidth:"100%",maxHeight:220,borderRadius:10,display:"block",cursor:"zoom-in",objectFit:"cover",marginBottom:m.text?8:0}}/>
                        )}
                        {/* صوت */}
                        {m.audio&&(
                          <div style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",minWidth:180}}>
                            <audio controls src={m.audio} style={{flex:1,height:32,borderRadius:8,accentColor:"#3b82f6"}}/>
                          </div>
                        )}
                        {m.text&&<span>{m.text}</span>}
                        {/* وقت + علامات القراءة */}
                        <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end",marginTop:4}}>
                          {m.edited&&<span style={{color:isMe?"rgba(255,255,255,.5)":"#475569",fontSize:9}}>تم التعديل</span>}
                          <span style={{color:isMe?"rgba(255,255,255,.5)":"#475569",fontSize:10}}>{new Date(m.createdAt).toLocaleTimeString("ar-EG",{hour:"2-digit",minute:"2-digit"})}</span>
                          {isMe&&(
                            <svg width="14" height="9" viewBox="0 0 16 11" fill={m.readBy&&m.readBy.length>1?"#60a5fa":"rgba(255,255,255,.4)"}>
                              {m.readBy&&m.readBy.length>1
                                ?<><path d="M1 6l4 4L15 1" stroke={m.readBy.length>1?"#60a5fa":"rgba(255,255,255,.4)"} strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/><path d="M5 6l4 4" stroke={m.readBy.length>1?"#60a5fa":"rgba(255,255,255,.4)"} strokeWidth="1.8" fill="none" strokeLinecap="round"/></>
                                :<path d="M1 6l4 4L15 1" stroke="rgba(255,255,255,.4)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                              }
                            </svg>
                          )}
                        </div>
                      </div>
                      )}
                      {/* أزرار التعديل والحذف — تظهر عند hover */}
                      {canDelete&&editingMsgId!==m.id&&(
                        <div className="del-btn" style={{position:"absolute",top:-6,left:isMe?-6:undefined,right:!isMe?-6:undefined,display:"flex",gap:3,opacity:0,transition:"opacity .2s"}}
                          onMouseEnter={e=>e.currentTarget.style.opacity=1}
                          onMouseLeave={e=>e.currentTarget.style.opacity=0}>
                          {m.text&&!m.image&&!m.audio&&(
                            <button onClick={()=>{setEditingMsgId(m.id);setEditText(m.text);}}
                              style={{width:20,height:20,borderRadius:"50%",background:"#1d4ed8",border:"none",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                            </button>
                          )}
                          <button onClick={()=>setConfirmDelId(m.id)}
                            style={{width:20,height:20,borderRadius:"50%",background:"#dc2626",border:"none",color:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          <div ref={bottomRef}/>
        </div>

        {/* Input bar */}
        {activeConvId&&(
          <div style={{borderTop:"1px solid #1e293b",padding:"10px 14px",background:"#0a1628"}}>
            {/* Image preview */}
            {imageFile&&(
              <div style={{display:"flex",gap:8,marginBottom:8,padding:"8px",background:"#111827",borderRadius:10}}>
                <img src={URL.createObjectURL(imageFile)} alt="" style={{width:56,height:56,borderRadius:8,objectFit:"cover"}}/>
                <div style={{flex:1}}>
                  <div style={{color:"#94a3b8",fontSize:12,marginBottom:4}}>{imageFile.name}</div>
                  <button onClick={()=>setImageFile(null)} style={{background:"#450a0a",border:"none",borderRadius:6,color:"#f87171",padding:"3px 10px",cursor:"pointer",fontSize:11,fontFamily:"inherit"}}>إزالة</button>
                </div>
              </div>
            )}
            <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
              {/* Image attach */}
              <button onClick={()=>fileRef.current.click()} title="إرفاق صورة"
                style={{width:40,height:40,borderRadius:"50%",background:"#111827",border:"1px solid #1e293b",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:"#64748b",transition:"all .15s"}}
                onMouseEnter={e=>{e.currentTarget.style.background="#1e293b";e.currentTarget.style.color="#94a3b8";}}
                onMouseLeave={e=>{e.currentTarget.style.background="#111827";e.currentTarget.style.color="#64748b";}}>
                {/* Paperclip icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{if(e.target.files[0])setImageFile(e.target.files[0]);e.target.value="";}}/>

              {/* Text input */}
              <textarea ref={inputRef}
                style={{...D.input,flex:1,resize:"none",minHeight:40,maxHeight:120,lineHeight:1.5,padding:"9px 14px",borderRadius:22,background:"#111827",border:"1px solid #1e293b",fontSize:13}}
                placeholder="اكتب رسالة..."
                value={msg}
                onChange={e=>{setMsg(e.target.value);e.target.style.height="40px";e.target.style.height=Math.min(e.target.scrollHeight,120)+"px";}}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMsg();}}}
                rows={1}
              />

              {/* Voice recorder */}
              <VoiceRecorder onSend={sendAudio}/>

              {/* Send */}
              {(msg.trim()||imageFile)&&(
                <button onClick={sendMsg}
                  style={{width:40,height:40,borderRadius:"50%",background:"#1d4ed8",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 2px 8px rgba(29,78,216,.4)"}}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

/* ═══════════ GRADES PAGE — درجات أسبوعية + تصدير Excel ═══════════ */
function GradesPage({courseId,students,db,setDB}){
  const gradeSchema=(db.gradeSchemas||{})[courseId]||[];
  const gradeWeeks=(db.gradeWeeks||{})[courseId]||[]; // [{id,label,date,records:{studentId:{itemId:val}}}]
  const sessions=(db.attendance||{})[courseId]||[];

  const [showAddSchema,setShowAddSchema]=useState(false);
  const [newItem,setNewItem]=useState({name:"",maxScore:10,type:"attendance",description:""});
  const [activeWeekId,setActiveWeekId]=useState(gradeWeeks[gradeWeeks.length-1]?.id||null);
  const [showAddWeek,setShowAddWeek]=useState(false);
  const [newWeekLabel,setNewWeekLabel]=useState("");

  const typeColors={attendance:"#22c55e",session:"#06b6d4",exam:"#3b82f6",quiz:"#8b5cf6",behavior:"#f59e0b",other:"#94a3b8"};
  // typeLabels removed - unused
  const isAttendanceType=(t)=>t==="attendance"||t==="session";
  const getAttOptions=(maxScore)=>[
    {label:"حضر",value:num(maxScore)},
    {label:"متأخر",value:Math.max(0,Math.round(num(maxScore)*0.8))},
    {label:"لم يحضر",value:0},
  ];

  const activeWeek=gradeWeeks.find(w=>w.id===activeWeekId)||null;
  const maxTotal=gradeSchema.reduce((s,i)=>s+num(i.maxScore),0);

  const addSchemaItem=()=>{
    if(!newItem.name||!newItem.maxScore)return;
    const item={id:uid(),...newItem,maxScore:num(newItem.maxScore),createdAt:nowISO()};
    setDB(p=>({...p,gradeSchemas:{...(p.gradeSchemas||{}),[courseId]:[...(gradeSchema),item]}}));
    setNewItem({name:"",maxScore:10,type:"attendance",description:""});
    setShowAddSchema(false);
  };

  const deleteSchemaItem=async(id)=>{
    const ok=await showConfirm("سيتم حذف هذا البند من جميع الأسابيع المسجلة",{title:"حذف بند التقييم",danger:true,confirmText:"حذف"});if(!ok)return;
    setDB(p=>({...p,gradeSchemas:{...(p.gradeSchemas||{}),[courseId]:gradeSchema.filter(i=>i.id!==id)}}));
  };

  const addWeek=()=>{
    if(!newWeekLabel.trim())return;
    const weekNum=gradeWeeks.length+1;
    const id=uid();
    const week={id,label:newWeekLabel.trim()||("الأسبوع "+weekNum),date:nowISO(),records:{}};
    setDB(p=>({...p,gradeWeeks:{...(p.gradeWeeks||{}),[courseId]:[...(gradeWeeks),week]}}));
    setActiveWeekId(id);
    setNewWeekLabel("");
    setShowAddWeek(false);
  };

  const deleteWeek=async(weekId)=>{
    const week=gradeWeeks.find(w=>w.id===weekId);
    const ok=await showConfirm(`سيتم حذف "${week?.label||"هذا الأسبوع"}" وكل الدرجات المسجلة فيه نهائياً`,{title:"حذف أسبوع",danger:true,confirmText:"حذف"});
    if(!ok)return;
    const remaining=gradeWeeks.filter(w=>w.id!==weekId);
    setDB(p=>({...p,gradeWeeks:{...(p.gradeWeeks||{}),[courseId]:remaining}}));
    if(activeWeekId===weekId) setActiveWeekId(remaining[remaining.length-1]?.id||null);
  };

  const renameWeek=(weekId,newLabel)=>{
    if(!newLabel.trim())return;
    setDB(p=>{
      const weeks=(p.gradeWeeks||{})[courseId]||[];
      return{...p,gradeWeeks:{...(p.gradeWeeks||{}),[courseId]:weeks.map(w=>w.id===weekId?{...w,label:newLabel.trim()}:w)}};
    });
  };

  const setGrade=(studentId,itemId,value)=>{
    if(!activeWeekId)return;
    setDB(p=>{
      const weeks=(p.gradeWeeks||{})[courseId]||[];
      const updated=weeks.map(w=>{
        if(w.id!==activeWeekId)return w;
        const recs=w.records||{};
        const studentRec=recs[studentId]||{};
        return{...w,records:{...recs,[studentId]:{...studentRec,[itemId]:value}}};
      });
      return{...p,gradeWeeks:{...(p.gradeWeeks||{}),[courseId]:updated}};
    });
  };

  // اجمالي كل طالب في أسبوع معين
  const getWeekTotal=(week,studentId)=>{
    const recs=(week.records||{})[studentId]||{};
    return gradeSchema.reduce((s,item)=>s+num(recs[item.id]||0),0);
  };

  // اجمالي كل طالب عبر كل الأسابيع
  const getGrandTotal=(studentId)=>gradeWeeks.reduce((s,w)=>s+getWeekTotal(w,studentId),0);
  const grandMaxTotal=maxTotal*gradeWeeks.length;

  // حضور الطالب من سجل الحضور
  const getAttendanceCount=(studentName)=>sessions.filter(sess=>(sess.present||[]).includes(studentName)).length;

  // تصدير Excel
  const exportExcel=()=>{
    if(gradeWeeks.length===0){alert("لا توجد أسابيع مسجلة بعد");return;}
    const rows=[];
    // Header: رقم، اسم، فرع، حضور، ثم كل بند مجمّع
    const headerCols=["#","الاسم","الفرع","الحضور ("+sessions.length+" جلسة)"];
    gradeSchema.forEach(item=>{
      headerCols.push(item.name+" ("+gradeWeeks.length+" أسابيع) /"+item.maxScore*gradeWeeks.length);
    });
    headerCols.push("المجموع الكلي /"+grandMaxTotal);
    headerCols.push("النسبة %");
    rows.push(headerCols);
    // Data: لكل طالب مجموع كل بند عبر الأسابيع
    students.forEach((st,i)=>{
      const row=[i+1,st.name,st.branch,getAttendanceCount(st.name)+" / "+sessions.length];
      gradeSchema.forEach(item=>{
        const itemTotal=gradeWeeks.reduce((s,w)=>s+num((w.records||{})[st.id]?.[item.id]||0),0);
        row.push(itemTotal);
      });
      const grand=getGrandTotal(st.id);
      const pct=grandMaxTotal>0?Math.round((grand/grandMaxTotal)*100):0;
      row.push(grand);row.push(pct+"%");
      rows.push(row);
    });
    const csvText=rows.map(r=>r.map(v=>String(v)).join(",")).join("\r\n");
    const csv="\uFEFF"+csvText;
    const blob=new Blob([csv],{type:"text/csv;charset=utf-8;"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url;a.download="درجات_مجمعة.csv";
    document.body.appendChild(a);a.click();document.body.removeChild(a);URL.revokeObjectURL(url);
  };

  return(<>
    <h2 style={D.secTitle}>لوحة الدرجات الأسبوعية</h2>

    {/* Schema — البنود الثابتة */}
    <div style={{...D.card,marginBottom:16}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <h3 style={{color:"#f1f5f9",fontSize:13,fontWeight:700,margin:0}}>
          بنود الدرجات ({gradeSchema.length} بند — الحد الأقصى الأسبوعي: {maxTotal} درجة)
        </h3>
        <Btn onClick={()=>setShowAddSchema(p=>!p)}>+ بند جديد</Btn>
      </div>

      {showAddSchema&&(
        <div style={{background:"#111827",borderRadius:10,padding:14,marginBottom:10,border:"1px solid #1e293b"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10,marginBottom:10}}>
            <Field label="اسم البند" style={{margin:0}}>
              <input style={D.input} placeholder="مثال: حضور المحاضرة" value={newItem.name} onChange={e=>setNewItem(p=>({...p,name:e.target.value}))}/>
            </Field>
            <Field label="الدرجة القصوى" style={{margin:0}}>
              <input style={D.input} type="number" min="0" value={newItem.maxScore} onChange={e=>setNewItem(p=>({...p,maxScore:e.target.value}))}/>
            </Field>
            <Field label="النوع" style={{margin:0}}>
              <CustomSelect value={newItem.type} onChange={v=>setNewItem(p=>({...p,type:v}))} options={[
                {value:"attendance",label:"حضور",sub:"حضر/متأخر/لم يحضر"},
                {value:"session",label:"ورشة/جلسة",sub:"حضر/متأخر/لم يحضر"},
                {value:"exam",label:"امتحان",sub:"رقمي"},
                {value:"quiz",label:"اختبار قصير",sub:"رقمي"},
                {value:"behavior",label:"سلوك",sub:"رقمي"},
                {value:"other",label:"أخرى",sub:"رقمي"},
              ]}/>
            </Field>
            <Field label="ملاحظة" style={{margin:0}}>
              <input style={D.input} placeholder="مثال: يُخصم 2 عند التأخر" value={newItem.description} onChange={e=>setNewItem(p=>({...p,description:e.target.value}))}/>
            </Field>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn onClick={addSchemaItem}>إضافة البند</Btn>
            <Btn ghost onClick={()=>setShowAddSchema(false)}>إلغاء</Btn>
          </div>
        </div>
      )}

      {gradeSchema.length===0
        ? <div style={{color:"#475569",fontSize:12,textAlign:"center",padding:"8px 0"}}>ابدأ بإضافة بنود الدرجات</div>
        : <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {gradeSchema.map(item=>(
              <div key={item.id} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",background:"#111827",borderRadius:8,border:"1px solid "+(typeColors[item.type]||"#1e293b")+"44"}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:typeColors[item.type]||"#94a3b8",flexShrink:0}}/>
                <span style={{color:"#f1f5f9",fontSize:11,fontWeight:600}}>{item.name}</span>
                <span style={{color:"#475569",fontSize:10}}>/{item.maxScore}</span>
                {item.description&&<span style={{color:"#64748b",fontSize:10}}>· {item.description}</span>}
                <button style={{...D.iconBtn,color:"#f87171",padding:"1px 4px",fontSize:11}} onClick={()=>deleteSchemaItem(item.id)}>✕</button>
              </div>
            ))}
          </div>
      }
    </div>

    {/* Weeks tabs */}
    {gradeSchema.length>0&&(
      <>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",flex:1}}>
            {gradeWeeks.map(w=>(
              <WeekTab key={w.id} week={w} active={activeWeekId===w.id} onSelect={()=>setActiveWeekId(w.id)} onRename={renameWeek} onDelete={deleteWeek}/>
            ))}
            {showAddWeek?(
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input style={{...D.input,width:140,fontSize:12}} placeholder={"الأسبوع "+(gradeWeeks.length+1)} value={newWeekLabel} onChange={e=>setNewWeekLabel(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addWeek()} autoFocus/>
                <Btn small onClick={addWeek}>حفظ</Btn>
                <Btn ghost small onClick={()=>setShowAddWeek(false)}>إلغاء</Btn>
              </div>
            ):(
              <button style={{...D.pill,borderStyle:"dashed",color:"#60a5fa",borderColor:"#1e3a5f"}} onClick={()=>setShowAddWeek(true)}>
                + أسبوع جديد
              </button>
            )}
          </div>
          {gradeWeeks.length>0&&(
            <button style={{...D.btn,background:"#16a34a",fontSize:12,padding:"7px 14px"}} onClick={exportExcel}>
              ⬇ تصدير Excel
            </button>
          )}
        </div>

        {/* Active week table */}
        {activeWeek&&students.length>0&&(
          <div className="table-scroll" style={{marginBottom:16}}>
            <table style={{width:"100%",borderCollapse:"collapse",background:"#0f172a",borderRadius:12,overflow:"hidden",fontSize:12,minWidth:500}}>
              <thead>
                <tr style={{background:"#1e293b"}}>
                  <th style={{padding:"10px 12px",textAlign:"right",color:"#64748b",fontWeight:700,position:"sticky",right:0,background:"#1e293b",zIndex:2,minWidth:32}}>#</th>
                  <th style={{padding:"10px 12px",textAlign:"right",color:"#64748b",fontWeight:700,position:"sticky",right:44,background:"#1e293b",zIndex:2,minWidth:140}}>الاسم</th>
                  <th style={{padding:"10px 12px",textAlign:"center",color:"#22c55e",fontWeight:700,whiteSpace:"nowrap"}}>
                    الحضور<br/><span style={{color:"#475569",fontWeight:400,fontSize:10}}>/{sessions.length} جلسة</span>
                  </th>
                  {gradeSchema.map(item=>(
                    <th key={item.id} style={{padding:"10px 12px",textAlign:"center",color:typeColors[item.type]||"#64748b",fontWeight:700,whiteSpace:"nowrap",minWidth:90}}>
                      {item.name}<br/><span style={{color:"#475569",fontWeight:400,fontSize:10}}>/{item.maxScore}</span>
                    </th>
                  ))}
                  <th style={{padding:"10px 12px",textAlign:"center",color:"#f59e0b",fontWeight:700,minWidth:80}}>
                    مجموع الأسبوع<br/><span style={{color:"#475569",fontWeight:400,fontSize:10}}>/{maxTotal}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((st,i)=>{
                  const weekTotal=getWeekTotal(activeWeek,st.id);
                  const pct=maxTotal>0?Math.round((weekTotal/maxTotal)*100):0;
                  const attCount=getAttendanceCount(st.name);
                  return(
                    <tr key={st.id} style={{borderTop:"1px solid #1e293b"}}>
                      <td style={{padding:"8px 12px",color:"#475569",position:"sticky",right:0,background:"#0f172a"}}>{i+1}</td>
                      <td style={{padding:"8px 12px",color:"#f1f5f9",fontWeight:600,position:"sticky",right:44,background:"#0f172a"}}>
                        {st.name}<div style={{color:"#475569",fontSize:10}}>{st.branch}</div>
                      </td>
                      <td style={{padding:"8px 12px",textAlign:"center"}}>
                        <span style={{color:attCount===sessions.length?"#4ade80":attCount>0?"#f59e0b":"#f87171",fontWeight:700}}>{attCount}</span>
                        <span style={{color:"#475569"}}> / {sessions.length}</span>
                      </td>
                      {gradeSchema.map(item=>{
                        const val=(activeWeek.records||{})[st.id]?.[item.id]||"";
                        const numVal=num(val);const isOver=numVal>num(item.maxScore);
                        if(isAttendanceType(item.type)){
                          const opts=getAttOptions(item.maxScore);
                          // تحديد الخيار الحالي
                          // curOpt removed - unused
                          const bgColor=val===""?"#111827":numVal===num(item.maxScore)?"#052e16":numVal===0?"#450a0a":"#1c1a05";
                          const txtColor=val===""?"#64748b":numVal===num(item.maxScore)?"#4ade80":numVal===0?"#f87171":"#fbbf24";
                          return(
                            <td key={item.id} style={{padding:"5px 6px",textAlign:"center"}}>
                              <select value={val} onChange={e=>setGrade(st.id,item.id,e.target.value)}
                                style={{background:bgColor,border:"1px solid "+(val===""?"#1f2937":numVal>0?"#334155":"#7f1d1d"),borderRadius:6,color:txtColor,padding:"5px 4px",fontSize:11,fontFamily:"inherit",outline:"none",cursor:"pointer",width:80}}>
                                <option value="">—</option>
                                {opts.map(o=><option key={o.value} value={o.value}>{o.label} ({o.value})</option>)}
                              </select>
                            </td>
                          );
                        }
                        return(
                          <td key={item.id} style={{padding:"6px 8px",textAlign:"center"}}>
                            <input type="number" min="0" max={item.maxScore} value={val}
                              onChange={e=>setGrade(st.id,item.id,e.target.value)}
                              style={{width:60,background:"#111827",border:"1px solid "+(isOver?"#ef4444":numVal>0?"#334155":"#1f2937"),borderRadius:6,color:isOver?"#f87171":numVal===num(item.maxScore)?"#4ade80":"#f1f5f9",padding:"5px 6px",fontSize:12,fontFamily:"inherit",textAlign:"center",outline:"none"}}/>
                          </td>
                        );
                      })}
                      <td style={{padding:"8px 12px",textAlign:"center"}}>
                        <div style={{color:pct>=90?"#4ade80":pct>=75?"#a3e635":pct>=50?"#f59e0b":"#f87171",fontWeight:800,fontSize:14}}>{weekTotal}</div>
                        <div style={{height:4,background:"#1e293b",borderRadius:2,width:50,margin:"3px auto 0"}}><div style={{width:pct+"%",height:"100%",background:pct>=75?"#22c55e":pct>=50?"#f59e0b":"#ef4444",borderRadius:2}}/></div>
                        <div style={{color:"#475569",fontSize:9,marginTop:1}}>{pct}%</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Grand total summary */}
        {gradeWeeks.length>1&&students.length>0&&(
          <div style={{...D.card,marginTop:4}}>
            <h3 style={{color:"#f59e0b",fontSize:13,fontWeight:700,marginBottom:12}}>
              الإجمالي التراكمي — {gradeWeeks.length} أسابيع (الحد الأقصى: {grandMaxTotal} درجة)
            </h3>
            <div className="table-scroll">
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:400}}>
                <thead>
                  <tr style={{background:"#1e293b"}}>
                    <th style={{padding:"8px 12px",textAlign:"right",color:"#64748b",fontWeight:700}}>#</th>
                    <th style={{padding:"8px 12px",textAlign:"right",color:"#64748b",fontWeight:700}}>الاسم</th>
                    <th style={{padding:"8px 12px",textAlign:"center",color:"#22c55e",fontWeight:700}}>الحضور</th>
                    {gradeWeeks.map(w=>(<th key={w.id} style={{padding:"8px 12px",textAlign:"center",color:"#64748b",fontWeight:700,whiteSpace:"nowrap"}}>{w.label}</th>))}
                    <th style={{padding:"8px 12px",textAlign:"center",color:"#f59e0b",fontWeight:700}}>الإجمالي</th>
                    <th style={{padding:"8px 12px",textAlign:"center",color:"#94a3b8",fontWeight:700}}>النسبة</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((st,i)=>{
                    const grand=getGrandTotal(st.id);
                    const pct=grandMaxTotal>0?Math.round((grand/grandMaxTotal)*100):0;
                    const attCount=getAttendanceCount(st.name);
                    return(
                      <tr key={st.id} style={{borderTop:"1px solid #1e293b"}}>
                        <td style={{padding:"7px 12px",color:"#475569"}}>{i+1}</td>
                        <td style={{padding:"7px 12px",color:"#f1f5f9",fontWeight:600}}>{st.name}</td>
                        <td style={{padding:"7px 12px",textAlign:"center",color:"#22c55e"}}>{attCount}/{sessions.length}</td>
                        {gradeWeeks.map(w=>(<td key={w.id} style={{padding:"7px 12px",textAlign:"center",color:"#94a3b8"}}>{getWeekTotal(w,st.id)}</td>))}
                        <td style={{padding:"7px 12px",textAlign:"center",color:pct>=75?"#4ade80":pct>=50?"#f59e0b":"#f87171",fontWeight:800,fontSize:14}}>{grand}</td>
                        <td style={{padding:"7px 12px",textAlign:"center"}}>
                          <div style={{display:"inline-flex",alignItems:"center",gap:6}}>
                            <div style={{width:50,height:6,background:"#1e293b",borderRadius:3,overflow:"hidden"}}><div style={{width:pct+"%",height:"100%",background:pct>=75?"#22c55e":pct>=50?"#f59e0b":"#ef4444",borderRadius:3}}/></div>
                            <span style={{color:pct>=75?"#4ade80":pct>=50?"#f59e0b":"#f87171",fontWeight:700}}>{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {gradeWeeks.length===0&&(
          <div style={{...D.card,textAlign:"center",padding:"30px 20px",color:"#475569"}}>
            <div style={{fontSize:30,marginBottom:8}}>📅</div>
            <div style={{color:"#94a3b8",fontWeight:600}}>لا توجد أسابيع مسجلة بعد</div>
            <div style={{fontSize:12,marginTop:4}}>اضغط "+ أسبوع جديد" لبدء تسجيل درجات الأسبوع الأول</div>
          </div>
        )}
      </>
    )}

    {gradeSchema.length===0&&(
      <EmptyState icon="—" label="ابدأ بإضافة بنود الدرجات" sub="حدد البنود أولاً ثم ستتمكن من إضافة الأسابيع وتسجيل الدرجات"/>
    )}
  </>);
}

function WeekTab({week,active,onSelect,onRename,onDelete}){
  const [editing,setEditing]=useState(false);
  const [val,setVal]=useState(week.label);
  const save=()=>{onRename(week.id,val);setEditing(false);};
  if(editing){
    return(
      <div style={{display:"flex",gap:4,alignItems:"center"}}>
        <input style={{...D.input,width:120,fontSize:12,padding:"5px 8px"}} value={val} onChange={e=>setVal(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")save();if(e.key==="Escape")setEditing(false);}} autoFocus/>
        <Btn small onClick={save}>حفظ</Btn>
        <Btn ghost small onClick={()=>setEditing(false)}>إلغاء</Btn>
      </div>
    );
  }
  return(
    <div style={{display:"flex",alignItems:"center",gap:2}}>
      <button style={{...D.pill,...(active?D.pillActive:{}),borderRadius:"8px 0 0 8px"}} onClick={onSelect}>
        {week.label}
      </button>
      <button style={{...D.pill,...(active?D.pillActive:{}),borderRadius:0,borderRight:"none",borderLeft:"none",padding:"6px 8px",fontSize:11}} onClick={()=>setEditing(true)} title="إعادة تسمية">✎</button>
      <button style={{...D.pill,...(active?D.pillActive:{}),borderRadius:"0 8px 8px 0",borderRight:"none",padding:"6px 8px",fontSize:11,color:active?"#fff":"#f87171"}} onClick={()=>onDelete(week.id)} title="حذف الأسبوع">✕</button>
    </div>
  );
}

/* ═══════════ EDIT LOG LIST ═══════════ */
function EditLogList({entries,courses,setDB}){
  const clearEdit=async(entryId)=>{
    const ok=await showConfirm("سيتم إزالة هذا القيد من سجل التعديلات (القيد نفسه يظل موجوداً)",{title:"حذف من سجل التعديلات",danger:true,confirmText:"حذف"});
    if(!ok)return;
    setDB(p=>({...p,entries:(p.entries||[]).map(e=>e.id!==entryId?e:{...e,edited:false,editLog:[]})}));
  };
  return (
    <div style={D.list}>
      {entries.map(e => {
        const c = courses.find(x => x.id === e.courseId);
        return (
          <div key={e.id} style={{...D.entryRow, borderRight:"3px solid #f59e0b"}}>
            <div style={{flex:1}}>
              <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", marginBottom:4}}>
                <span style={{fontSize:10, background:"#78350f", color:"#fbbf24", borderRadius:4, padding:"1px 6px"}}>معدّل</span>
                <span style={{color:"#f1f5f9", fontWeight:600}}>{typeById(e.type).label}</span>
                {c && <span style={{color:"#64748b", fontSize:12}}>({c.name})</span>}
              </div>
              <div style={{color:"#94a3b8", fontSize:12}}>أُنشئ: {fmtDT(e.createdAt)}</div>
              {(e.editLog||[]).map((log, i) => (
                <div key={i} style={{marginTop:6, padding:"6px 10px", background:"#1e293b", borderRadius:8, fontSize:12, color:"#cbd5e1"}}>
                  <span style={{color:"#f59e0b"}}>تعديل {fmtDT(log.at)}</span> — {log.by}:&nbsp;
                  {Object.entries(log.changes||{}).map(([k,v]) => `${k}: ${v}`).join(" | ")}
                </div>
              ))}
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
              <div style={{color: typeById(e.type).group==="expense" ? "#f87171" : "#4ade80", fontWeight:700}}>
                {currency(e.amount)}
              </div>
              {setDB&&<button style={{...D.iconBtn,color:"#f87171",fontSize:11,border:"1px solid #450a0a",borderRadius:6,padding:"2px 8px"}} onClick={()=>clearEdit(e.id)}>حذف من السجل</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════ ADMIN COURSE VIEW ═══════════ */
function AdminCourseView({db,setDB,allEntries,selCourse,setSelCourse}){
  const courses=(db.courses||[]).filter(c=>!c.archived);
  const course=selCourse?(db.courses||[]).find(c=>c.id===selCourse):null;

  if(!course) return(
    <>
      <h2 style={D.secTitle}>الدورات النشطة ({courses.length})</h2>
      {courses.length===0&&<EmptyState icon="-" label="لا توجد دورات نشطة" sub=""/>}
      {(()=>{
        const grouped={};
        courses.forEach(c=>{if(!grouped[c.name])grouped[c.name]=[];grouped[c.name].push(c);});
        return(
          <div style={D.courseGrid}>
            {Object.entries(grouped).map(([name,group])=>{
              const allEs=group.flatMap(c=>allEntries.filter(e=>e.courseId===c.id));
              const ci=allEs.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
              const ce=allEs.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
              const net=ci-ce;
              const stCount=group.reduce((s,c)=>s+((db.students||{})[c.id]?.length||0),0);
              const isMulti=group.length>1;
              const logo=group[0]?.logo||"";
              return(
                <div key={name} className="rw-course-card" style={{...D.courseCard,cursor:"pointer"}} onClick={()=>setSelCourse(group[0].id)}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    {logo?<img src={logo} alt="" style={{width:32,height:32,borderRadius:8,objectFit:"contain",background:"#1e293b",padding:2}}/>:<div style={{width:32,height:32,borderRadius:8,background:"#1e3a5f",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#60a5fa"}}>{name[0]}</div>}
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontWeight:700,color:"#f1f5f9",fontSize:14,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</div>
                      {isMulti
                        ?<div style={{color:"#60a5fa",fontSize:11}}>{group.length} دفعات · {group.map(c=>c.branch).join(" & ")}</div>
                        :<div style={{color:"#64748b",fontSize:11}}>دفعة {group[0].batchNumber} · {group[0].branch} · {group[0].manager}</div>
                      }
                    </div>
                  </div>
                  {isMulti&&(
                    <div style={{display:"flex",gap:4,marginBottom:8,flexWrap:"wrap"}}>
                      {group.map(c=>(
                        <span key={c.id} onClick={e=>{e.stopPropagation();setSelCourse(c.id);}}
                          style={{background:"#1e3a5f",border:"1px solid #1d4ed855",borderRadius:6,padding:"2px 8px",color:"#93c5fd",fontSize:11,cursor:"pointer"}}>
                          دفعة {c.batchNumber} — {c.branch}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{display:"flex",gap:10,fontSize:12,flexWrap:"wrap",borderTop:"1px solid #1e293b",paddingTop:8}}>
                    <span style={{color:"#22c55e"}}>وارد {currency(ci)}</span>
                    <span style={{color:"#ef4444"}}>مصروف {currency(ce)}</span>
                    <span style={{color:net>=0?"#a3e635":"#fb923c",fontWeight:700}}>صافي {currency(net)}</span>
                    <span style={{color:"#64748b",marginRight:"auto"}}>طلاب {stCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}
    </>
  );

  const entries=allEntries.filter(e=>e.courseId===selCourse).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
  const books=(db.books||{})[selCourse]||[];
  const students=(db.students||{})[selCourse]||[];
  const sessions=(db.attendance||{})[selCourse]||[];
  const courseBranches=course.branches||(course.branch==="المنصورة والقاهرة"?["المنصورة","القاهرة"]:[course.branch||"المنصورة"]);
  const mansura=students.filter(s=>s.branch==="المنصورة");
  const cairo=students.filter(s=>s.branch==="القاهرة");
  const ci=entries.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
  const ce=entries.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
  const cp=entries.filter(e=>e.type==="book_print").reduce((s,e)=>s+num(e.amount),0);
  const bStats=bookStats(db,selCourse);
  const cs=bStats.revenue;
  const pendingBooks=bStats.pending;
  const net=ci-ce+cs-cp;
  const subPrice=course.subPrice||0;
  const fullyExemptStudents=students.filter(s=>s.exempted);
  const paidStudents=students.filter(s=>!s.exempted&&s.paid);
  const unpaidStudents=students.filter(s=>!s.exempted&&!s.paid&&!(s.paidAmount>0));
  const partialStudents=students.filter(s=>!s.exempted&&!s.paid&&s.paidAmount>0);
  const totalPendingSubs=subPrice>0?students.filter(s=>!s.exempted).reduce((sum,s)=>sum+Math.max(0,subPrice-(s.paidAmount||0)-(s.exemptedAmount||0)),0):0;
  const totalExempted=students.reduce((sum,s)=>sum+(s.exempted?subPrice:(s.exemptedAmount||0)),0);
  const totalPending=totalPendingSubs+pendingBooks;
  const avgAttPct=sessions.length&&students.length?Math.round(sessions.reduce((s,sess)=>s+(sess.present?.length||0),0)/sessions.length/students.length*100):0;
  const updateSubPrice=(val)=>setDB(p=>({...p,courses:(p.courses||[]).map(c=>c.id===selCourse?{...c,subPrice:num(val)}:c)}));

  const printReport=()=>{
    const w=window.open("","_blank");
    const bStats=bookStats(db,selCourse);
    const bookPay=(db.bookPayments||{})[selCourse]||{};
    const incomeEntries=entries.filter(e=>typeById(e.type).group==="income");
    const expenseEntries=entries.filter(e=>typeById(e.type).group==="expense");
    const printEntries=entries.filter(e=>e.type==="book_print");
    const fullyExemptStudents=students.filter(s=>s.exempted);
    const paidStudents=students.filter(s=>!s.exempted&&s.paid);
    const unpaidStudents=students.filter(s=>!s.exempted&&!s.paid&&!(s.paidAmount>0));
    const partialStudents=students.filter(s=>!s.exempted&&!s.paid&&s.paidAmount>0);
    const totalPendingSubs=subPrice>0?students.filter(s=>!s.exempted).reduce((sum,s)=>sum+Math.max(0,subPrice-(s.paidAmount||0)-(s.exemptedAmount||0)),0):0;
    const totalExempted=students.reduce((sum,s)=>sum+(s.exempted?subPrice:(s.exemptedAmount||0)),0);
    const totalPendingBooks=bStats.pending;
    const printDate=new Date().toLocaleDateString("ar-EG",{year:"numeric",month:"long",day:"numeric"});
    const css=`
      @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800;900&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'Cairo',sans-serif;direction:rtl;background:#fff;color:#0f172a;font-size:11px;line-height:1.5}
      .page{max-width:900px;margin:0 auto;padding:28px 32px}
      /* Header */
      .hdr{display:flex;align-items:flex-start;justify-content:space-between;padding-bottom:16px;margin-bottom:20px;border-bottom:3px solid #1d4ed8}
      .hdr-brand{display:flex;align-items:center;gap:10}
      .hdr-logo{width:48px;height:48px;border-radius:50%;background:#1d4ed8;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;font-size:16px}
      .hdr-title{font-size:20px;font-weight:900;color:#0f172a;margin-bottom:3px}
      .hdr-sub{font-size:11px;color:#64748b}
      .badge{background:#1d4ed8;color:#fff;font-size:10px;font-weight:700;padding:4px 10px;border-radius:6px;white-space:nowrap}
      /* Stats */
      .stats{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin-bottom:20px}
      .stat{border-radius:10px;padding:12px 10px;text-align:center;border:1px solid #e2e8f0}
      .stat-label{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:5px}
      .stat-val{font-size:16px;font-weight:900}
      /* Section */
      .sec{margin-bottom:18px}
      .sec-hdr{display:flex;align-items:center;gap:8px;margin-bottom:10px}
      .sec-bar{width:4px;border-radius:2px;align-self:stretch;min-height:20px}
      .sec-title{font-size:13px;font-weight:800;color:#0f172a}
      .sec-count{font-size:10px;color:#94a3b8;margin-right:auto}
      /* Table */
      table{width:100%;border-collapse:collapse;font-size:10px}
      th{background:#0f172a;color:#fff;padding:7px 10px;text-align:right;font-weight:700;white-space:nowrap}
      td{padding:6px 10px;border-bottom:1px solid #f1f5f9;text-align:right}
      tr:nth-child(even) td{background:#f8fafc}
      tfoot td{font-weight:700;background:#f1f5f9;border-top:2px solid #e2e8f0}
      /* Colors */
      .green{color:#16a34a;font-weight:700} .red{color:#dc2626;font-weight:700}
      .amber{color:#d97706;font-weight:700} .blue{color:#2563eb;font-weight:700}
      .gray{color:#64748b}
      /* 2-col grid */
      .grid2{display:grid;grid-template-columns:1fr 1fr;gap:12px}
      .info-box{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px}
      .info-row{display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid #f1f5f9;font-size:11px}
      .info-row:last-child{border-bottom:none}
      .info-label{color:#64748b}
      .info-val{font-weight:700;color:#0f172a}
      /* Footer */
      .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}
      @media print{.page{padding:16px}.no-print{display:none}}
    `;

    w.document.write(`<!DOCTYPE html><html lang="ar" dir="rtl"><head><meta charset="utf-8">
      <title>تقرير ${course.name} — دفعة ${course.batchNumber}</title>
      <style>${css}</style></head><body><div class="page">

      <div class="hdr">
        <div class="hdr-brand">
          <div class="hdr-logo">ر</div>
          <div>
            <div class="hdr-title">${course.name}</div>
            <div class="hdr-sub">دفعة ${course.batchNumber} &nbsp;·&nbsp; ${course.branch} &nbsp;·&nbsp; مشرف: ${course.manager}</div>
          </div>
        </div>
        <div style="text-align:left">
          <div class="badge">تقرير مرحلي</div>
          <div style="font-size:10px;color:#94a3b8;margin-top:6px">${printDate}</div>
          <div style="font-size:10px;color:#94a3b8">رواحل — نظام الحسابات</div>
        </div>
      </div>

      <div class="stats">
        <div class="stat" style="background:#f0fdf4;border-color:#bbf7d0">
          <div class="stat-label">الوارد</div>
          <div class="stat-val green">${currency(ci)}</div>
        </div>
        <div class="stat" style="background:#fef2f2;border-color:#fecaca">
          <div class="stat-label">المصروف</div>
          <div class="stat-val red">${currency(ce)}</div>
        </div>
        <div class="stat" style="background:#eff6ff;border-color:#bfdbfe">
          <div class="stat-label">إيراد الكتب</div>
          <div class="stat-val blue">${currency(cs)}</div>
        </div>
        <div class="stat" style="background:#fff7ed;border-color:#fed7aa">
          <div class="stat-label">تحت التحصيل</div>
          <div class="stat-val amber">${currency(totalPendingSubs+totalPendingBooks)}</div>
        </div>
        <div class="stat" style="background:#eff6ff;border-color:#bfdbfe">
          <div class="stat-label">إعفاءات الاشتراكات</div>
          <div class="stat-val blue">${currency(totalExempted)}</div>
        </div>
        <div class="stat" style="background:${net>=0?"#f0fdf4":"#fef2f2"};border-color:${net>=0?"#86efac":"#fca5a5"}">
          <div class="stat-label">الصافي</div>
          <div class="stat-val ${net>=0?"green":"red"}">${currency(net)}</div>
        </div>
      </div>

      <div class="grid2" style="margin-bottom:18px">
        <div class="info-box">
          <div style="font-size:12px;font-weight:800;margin-bottom:8px;color:#0f172a">معلومات الدورة</div>
          <div class="info-row"><span class="info-label">الفرع</span><span class="info-val">${course.branch}</span></div>
          <div class="info-row"><span class="info-label">المشرف</span><span class="info-val">${course.manager}</span></div>
          <div class="info-row"><span class="info-label">المساعدون</span><span class="info-val">${(course.assistants||[]).map(a=>a.name).join("، ")||"—"}</span></div>
          <div class="info-row"><span class="info-label">تاريخ الإنشاء</span><span class="info-val">${fmtDate(course.createdAt)}</span></div>
          <div class="info-row"><span class="info-label">سعر الاشتراك</span><span class="info-val">${currency(subPrice)}</span></div>
        </div>
        <div class="info-box">
          <div style="font-size:12px;font-weight:800;margin-bottom:8px;color:#0f172a">إحصائيات الطلاب (${students.length})</div>
          <div class="info-row"><span class="info-label">رجال</span><span class="info-val">${students.filter(s=>(s.gender||"ذكر")!=="أنثى").length}</span></div>
          <div class="info-row"><span class="info-label">نساء</span><span class="info-val">${students.filter(s=>(s.gender||"ذكر")==="أنثى").length}</span></div>
          <div class="info-row"><span class="info-label green">دفعوا كامل</span><span class="info-val green">${paidStudents.length} (${students.length?Math.round(paidStudents.length/students.length*100):0}%)</span></div>
          <div class="info-row"><span class="info-label amber">دفعوا جزئياً</span><span class="info-val amber">${partialStudents.length}</span></div>
          <div class="info-row"><span class="info-label blue">إعفاء كامل</span><span class="info-val blue">${fullyExemptStudents.length}</span></div>
          <div class="info-row"><span class="info-label red">لم يدفعوا</span><span class="info-val red">${unpaidStudents.length} — ${currency(totalPendingSubs)}</span></div>
        </div>
      </div>

      ${(fullyExemptStudents.length>0||students.some(s=>s.exemptedAmount>0))?`<div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#3b82f6"></div><div class="sec-title">تقرير الإعفاءات</div></div>
        <table>
          <tr><th>الطالب</th><th>نوع الإعفاء</th><th>المبلغ المعفى</th></tr>
          ${fullyExemptStudents.map(s=>`<tr><td>${s.name}</td><td>إعفاء كامل</td><td>${currency(subPrice)}</td></tr>`).join("")}
          ${students.filter(s=>!s.exempted&&s.exemptedAmount>0).map(s=>`<tr><td>${s.name}</td><td>إعفاء جزئي</td><td>${currency(s.exemptedAmount)}</td></tr>`).join("")}
        </table>
      </div>`:""}

      <div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#22c55e"></div><div class="sec-title">الإيرادات</div><div class="sec-count">${incomeEntries.length} قيد</div></div>
        <table><thead><tr><th>النوع</th><th>المبلغ</th><th>التاريخ</th><th>ملاحظة</th></tr></thead>
        <tbody>${incomeEntries.map(e=>`<tr><td>${typeById(e.type).label}</td><td class="green">${currency(e.amount)}</td><td class="gray">${fmtDT(e.createdAt)}</td><td class="gray">${[e.recipient,e.description].filter(Boolean).join(" · ")||"—"}</td></tr>`).join("")}</tbody>
        <tfoot><tr><td>الإجمالي</td><td class="green">${currency(ci)}</td><td></td><td></td></tr></tfoot>
        </table>
      </div>

      <div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#ef4444"></div><div class="sec-title">المصروفات</div><div class="sec-count">${expenseEntries.length} قيد</div></div>
        <table><thead><tr><th>النوع</th><th>المبلغ</th><th>التاريخ</th><th>المستلم / التفاصيل</th></tr></thead>
        <tbody>${expenseEntries.map(e=>`<tr><td>${typeById(e.type).label}</td><td class="red">${currency(e.amount)}</td><td class="gray">${fmtDT(e.createdAt)}</td><td class="gray">${[e.recipient,e.description].filter(Boolean).join(" · ")||"—"}</td></tr>`).join("")}</tbody>
        <tfoot><tr><td>الإجمالي</td><td class="red">${currency(ce)}</td><td></td><td></td></tr></tfoot>
        </table>
      </div>

      ${books.length>0?`<div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#0ea5e9"></div><div class="sec-title">الكتب</div><div class="sec-count">${books.length} كتاب</div></div>
        <table><thead><tr><th>الكتاب</th><th>سعر البيع</th><th>المخزون</th><th>دفعوا</th><th>الإيراد</th><th>تحت التحصيل</th><th>معفى</th></tr></thead>
        <tbody>${books.map(b=>{
          const st=bookPay[b.id]||{};
          const paid=students.filter(s=>st[s.id]==="paid").length;
          const owed=students.filter(s=>st[s.id]==="owed").length;
          const exempt=students.filter(s=>st[s.id]==="exempt").length;
          return`<tr><td style="font-weight:700">${b.name}</td><td>${currency(b.salePrice)}</td><td>${b.stock||0}</td><td class="green">${paid}</td><td class="green">${currency(paid*num(b.salePrice))}</td><td class="amber">${currency(owed*num(b.salePrice))}</td><td class="blue">${exempt}</td></tr>`;
        }).join("")}</tbody>
        <tfoot><tr><td>الإجمالي</td><td></td><td>${books.reduce((s,b)=>s+(b.stock||0),0)}</td><td class="green">${bStats.revenue>0?bStats.revenue:""}</td><td class="green">${currency(bStats.revenue)}</td><td class="amber">${currency(bStats.pending)}</td><td class="blue">${books.reduce((s,b)=>{const st=bookPay[b.id]||{};return s+students.filter(x=>st[x.id]==="exempt").length;},0)}</td></tr></tfoot>
        </table>
      </div>`:""}

      ${printEntries.length>0?`<div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#f43f5e"></div><div class="sec-title">تكاليف الطباعة</div></div>
        <table><thead><tr><th>التاريخ</th><th>المبلغ</th><th>ملاحظة</th></tr></thead>
        <tbody>${printEntries.map(e=>`<tr><td class="gray">${fmtDT(e.createdAt)}</td><td class="red">${currency(e.amount)}</td><td class="gray">${e.description||"—"}</td></tr>`).join("")}</tbody>
        <tfoot><tr><td>الإجمالي</td><td class="red">${currency(cp)}</td><td></td></tr></tfoot>
        </table>
      </div>`:""}

      ${subPrice>0&&(paidStudents.length>0)?`<div class="sec">
        <div class="sec-hdr"><div class="sec-bar" style="background:#22c55e"></div><div class="sec-title">الطلاب الذين دفعوا الاشتراك (${paidStudents.length})</div></div>
        <div style="display:flex;flex-wrap:wrap;gap:5px">
          ${paidStudents.map(s=>`<span style="background:#f0fdf4;border:1px solid #86efac;border-radius:6px;padding:3px 10px;font-size:10px;color:#16a34a;font-weight:700">${s.name}</span>`).join("")}
        </div>
      </div>`:""}

      ${(()=>{
        const subEntries=entries.filter(e=>(e.type==="sub_mansura"||e.type==="sub_cairo")&&e.paymentMonth);
        if(subEntries.length===0)return"";
        const byMonth={};
        subEntries.forEach(e=>{
          if(!byMonth[e.paymentMonth])byMonth[e.paymentMonth]={count:0,total:0};
          byMonth[e.paymentMonth].count++;
          byMonth[e.paymentMonth].total+=num(e.amount);
        });
        return `<div class="sec">
          <div class="sec-hdr"><div class="sec-bar" style="background:#8b5cf6"></div><div class="sec-title">الدفع حسب الشهر</div></div>
          <table>
            <tr><th>الشهر</th><th>عدد الدفعات</th><th>الإجمالي</th></tr>
            ${Object.entries(byMonth).map(([m,d])=>`<tr><td>${m}</td><td>${d.count}</td><td>${currency(d.total)}</td></tr>`).join("")}
          </table>
        </div>`;
      })()}

      <div class="footer">
        <span>رواحل — نظام الحسابات</span>
        <span>${course.name} · دفعة ${course.batchNumber} · ${course.branch}</span>
        <span>طُبع: ${printDate}</span>
      </div>

      </div></body></html>`);
    w.document.close();w.focus();setTimeout(()=>{w.print();},700);
  };

  const siblingCourses=courses.filter(c=>c.name===course.name);

  return(
    <>
      <div style={{display:"flex",gap:10,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
        <button style={D.pill} onClick={()=>setSelCourse(null)}>رجوع</button>
        <h2 style={{...D.secTitle,margin:0,flex:1}}>{course.name} — دفعة {course.batchNumber}</h2>
        <button style={{...D.btn,background:"#dc2626"}} onClick={printReport}>طباعة التقرير</button>
      </div>

      {siblingCourses.length>1&&(
        <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
          {siblingCourses.map(c=>(
            <button key={c.id} onClick={()=>setSelCourse(c.id)}
              style={{...D.pill,...(c.id===selCourse?{background:"#1d4ed8",borderColor:"#3b82f6",color:"#fff"}:{})}}>
              دفعة {c.batchNumber} — {c.branch}
            </button>
          ))}
        </div>
      )}

      <div style={{...D.card,marginBottom:16,display:"flex",gap:16,alignItems:"center",flexWrap:"wrap",background:"linear-gradient(135deg,#0f172a,#1e293b)"}}>
        {course.logo&&<img src={course.logo} alt="" style={{width:56,height:56,borderRadius:12,objectFit:"contain",background:"#0f172a",padding:4}}/>}
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:"#f1f5f9",fontWeight:800,fontSize:16,marginBottom:4}}>{course.name}</div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            <span style={{color:"#64748b",fontSize:12}}>دفعة {course.batchNumber}</span>
            <span style={{color:"#64748b",fontSize:12}}>{course.branch}</span>
            <span style={{color:"#60a5fa",fontSize:12}}>مشرف: {course.manager}</span>
          </div>
        </div>
        <div style={{...D.card,background:"#1e293b",padding:"10px 16px",textAlign:"center",minWidth:120}}>
          <div style={{color:"#64748b",fontSize:11,marginBottom:2}}>سعر الاشتراك</div>
          <input style={{...D.input,textAlign:"center",fontWeight:700,fontSize:14,width:100,color:"#f59e0b"}} type="number" min="0"
            defaultValue={course.subPrice||0}
            onBlur={e=>updateSubPrice(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&updateSubPrice(e.target.value)}/>
        </div>
      </div>

      <div style={{...D.statsGrid,marginBottom:16}} className="rw-stats-mobile">
        <MiniStat label="الوارد"       value={currency(ci)} color="#22c55e"/>
        <MiniStat label="المصروف"      value={currency(ce)} color="#ef4444"/>
        <MiniStat label="مبيعات الكتب" value={currency(cs)} color="#0ea5e9"/>
        <MiniStat label="تكلفة طباعة" value={currency(cp)} color="#f43f5e"/>
        <MiniStat label="الصافي"       value={currency(net)} color={net>=0?"#a3e635":"#fb923c"} bold/>
        {subPrice>0&&<MiniStat label="تحت التحصيل (اشتراكات)" value={currency(totalPendingSubs)} color="#f59e0b"/>}
        <MiniStat label="تحت التحصيل (كتب)" value={currency(pendingBooks)} color="#fb923c"/>
        <MiniStat label="إجمالي تحت التحصيل" value={currency(totalPending)} color="#ef4444" bold/>
        {subPrice>0&&<MiniStat label="إعفاءات الاشتراكات" value={currency(totalExempted)} color="#60a5fa"/>}
        {sessions.length>0&&<MiniStat label="متوسط الحضور" value={avgAttPct+"%"} color={avgAttPct>=75?"#a3e635":avgAttPct>=50?"#f59e0b":"#f87171"}/>}
      </div>

      <CollapsibleGroup label={`الكوادر — ${1+(course.assistants||[]).length} مشرف`} color="#60a5fa">
        <div style={{padding:14,background:"#0f172a"}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <span style={{padding:"5px 10px",background:"#1e293b",borderRadius:6,color:"#f1f5f9",fontSize:12}}>{course.manager} (رئيسي)</span>
            {(course.assistants||[]).map((a,i)=><span key={i} style={{padding:"5px 10px",background:"#1e293b",borderRadius:6,color:"#94a3b8",fontSize:12}}>{a.name} (مساعد)</span>)}
          </div>
        </div>
      </CollapsibleGroup>

      {books.length>0&&(
        <CollapsibleGroup label={`الكتب — ${books.length} كتاب`} color="#0ea5e9" defaultOpen={false}>
          <div style={{padding:12,background:"#0f172a"}}><BooksTable books={books} bookPayments={(db.bookPayments||{})[selCourse]||{}} students={students}/></div>
        </CollapsibleGroup>
      )}

      <CollapsibleGroup label={`الطلاب — ${students.length} طالب · دفع ${paidStudents.length}`} color="#f59e0b" defaultOpen={false}>
        <div style={{padding:12,background:"#0f172a"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:12}}>
            {[["المنصورة",mansura,"#22c55e"],["القاهرة",cairo,"#3b82f6"]]
              .filter(([br])=>courseBranches.includes(br))
              .map(([br,list,col])=>(
              <div key={br}>
                <div style={{color:col,fontSize:12,fontWeight:700,marginBottom:6}}>{br} ({list.length})</div>
                <AdminStudentMiniTable rows={list} subPrice={subPrice}/>
              </div>
            ))}
          </div>
        </div>
      </CollapsibleGroup>

      <CollapsibleGroup label={`القيود — ${entries.length} قيد`} color="#94a3b8" defaultOpen={false}>
        <div style={{padding:12,background:"#0f172a"}}>
          <div style={D.list}>{entries.map(e=><EntryRow key={e.id} entry={e} books={books} adminView/>)}</div>
        </div>
      </CollapsibleGroup>
    </>
  );
}

function AdminStudentMiniTable({rows,subPrice}){
  if(rows.length===0) return <div style={{padding:"8px 14px",color:"#475569",fontSize:12,background:"#0f172a"}}>لا يوجد</div>;
  return(
    <div style={{background:"#0f172a"}}>
      {rows.map((s,i)=>(
        <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 12px",borderTop:i===0?"none":"1px solid #1e293b",fontSize:12}}>
          <span style={{color:"#475569",fontSize:11,minWidth:20}}>{i+1}</span>
          <span style={{color:"#f1f5f9",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
          <span style={{color:s.exempted?"#60a5fa":s.paid?"#4ade80":s.paidAmount>0?"#fbbf24":"#f87171",fontSize:11,fontWeight:700,flexShrink:0}}>
            {s.exempted?"معفى كامل":s.paid?(s.partialExempt?"دفع+إعفاء":"دفع"):s.paidAmount>0?`جزئي ${currency(s.paidAmount)}`:`${subPrice>0?currency(subPrice):"لم يدفع"}`}
          </span>
        </div>
      ))}
    </div>
  );
}

function DeletedEntriesView({deletedEntries,courses,setDB}){
  const clearDeleted=async(idx)=>{
    const ok=await showConfirm("سيتم إزالة هذا السجل من قائمة المحذوفات نهائياً",{title:"حذف من سجل المحذوفات",danger:true,confirmText:"حذف"});
    if(!ok)return;
    setDB(p=>({...p,deletedEntries:(p.deletedEntries||[]).filter((_,i)=>i!==idx)}));
  };
  if(deletedEntries.length===0) return <><h2 style={D.secTitle}>المحذوفات (0)</h2><EmptyState icon="—" label="لا توجد محذوفات" sub=""/></>;
  return(<>
    <h2 style={D.secTitle}>سجل المحذوفات ({deletedEntries.length})</h2>
    <div style={D.list}>
      {deletedEntries.map((e,i)=>{
        const t=typeById(e.type);
        const c=courses.find(x=>x.id===e.courseId);
        return(
          <div key={i} style={{...D.entryRow,borderRight:"3px solid #dc2626"}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:4}}>
                <span style={{fontSize:10,background:"#450a0a",color:"#f87171",borderRadius:4,padding:"1px 6px",fontWeight:700}}>محذوف</span>
                <span style={{color:"#f1f5f9",fontWeight:600,fontSize:13}}>{t.label}</span>
                {c&&<span style={{color:"#64748b",fontSize:12}}>({c.name})</span>}
              </div>
              {e.studentName&&<div style={D.eSub}>الطالب: {e.studentName}</div>}
              {e.description&&<div style={D.eSub}>{e.description}</div>}
              <div style={D.eSub}>أُنشئ: {fmtDT(e.createdAt)}</div>
              <div style={{...D.eSub,color:"#f87171"}}>حُذف: {fmtDT(e.deletedAt)} — بواسطة: {e.deletedBy||"—"}</div>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
              <div style={{color:"#f87171",fontWeight:700,fontSize:14}}>- {currency(e.amount)}</div>
              {setDB&&<button style={{...D.iconBtn,color:"#f87171",fontSize:11,border:"1px solid #450a0a",borderRadius:6,padding:"2px 8px"}} onClick={()=>clearDeleted(i)}>حذف من السجل</button>}
            </div>
          </div>
        );
      })}
    </div>
  </>);
}

/* ═══════════ [#2] ADMIN ARCHIVE VIEW — قراءة فقط ═══════════ */
function AdminArchiveView({batches,db,setDB}){
  const [sel,setSel]=useState(null);
  const batch=sel?batches.find(b=>b.id===sel):null;
  if(batch) return <BatchReport batch={batch} onBack={()=>setSel(null)}/>;
  return(<>
    <h2 style={D.secTitle}>الدفعات المكتملة ({batches.length})</h2>
    <p style={{color:"#64748b",fontSize:13,marginBottom:16}}>الدفعات المؤرشفة للاستعراض فقط — لا يمكن التعديل عليها</p>
    {batches.length===0&&<EmptyState icon="—" label="لا توجد دفعات مكتملة" sub=""/>}
    <div style={D.courseGrid}>
      {[...batches].reverse().map(b=>(
        <div key={b.id} style={D.courseCard} onClick={()=>setSel(b.id)}>
          <div style={{fontWeight:700,color:"#f1f5f9",marginBottom:4}}>{b.courseName}</div>
          <div style={{color:"#64748b",fontSize:12,marginBottom:4}}>دفعة {b.batchNumber} · {b.branch}</div>
          <div style={{color:"#475569",fontSize:11}}>اكتملت: {fmtDate(b.completedAt)}</div>
          <div style={{marginTop:8,padding:"4px 8px",background:"#052e16",borderRadius:6,display:"inline-block"}}>
            <span style={{color:"#4ade80",fontSize:11}}>استعراض التقرير</span>
          </div>
        </div>
      ))}
    </div>
  </>);
}

/* ═══════════ MY EDITS ═══════════ */
function MyEditsPage({entries,userId}){
  return(<>
    <h2 style={D.secTitle}>سجل تعديلاتي ({entries.length})</h2>
    {entries.length===0&&<EmptyState icon="—" label="لم تقم بأي تعديلات" sub=""/>}
    <EditLogList entries={entries} courses={[]}/>
  </>);
}

/* ═══════════ BOOKS PAGE ═══════════ */
function BooksPage({courseId,books,setDB,students,db}){
  const [form,setForm]=useState({name:"",printCost:"",stock:"",prevStock:"",salePrice:""});
  const [del,setDel]=useState(null);
  const [editBook,setEditBook]=useState(null);
  const [activeBookId,setActiveBookId]=useState(books[0]?.id||null);
  const setF=k=>e=>setForm(p=>({...p,[k]:e.target.value}));
  const bookPayments=(db.bookPayments||{})[courseId]||{};

  const addBook=()=>{
    if(!form.name||!form.printCost)return;
    const newStock=num(form.stock);const prev=num(form.prevStock);const totalStock=newStock+prev;
    const id=uid();
    setDB(p=>({...p,books:{...p.books,[courseId]:[...(p.books[courseId]||[]),{id,name:form.name,printCost:num(form.printCost),salePrice:num(form.salePrice),stock:totalStock,newStock,prevStock:prev}]}}));
    setForm({name:"",printCost:"",stock:"",prevStock:"",salePrice:""});
    setActiveBookId(id);
  };
  const updateBook=(id,changes)=>setDB(p=>({...p,books:{...p.books,[courseId]:(p.books[courseId]||[]).map(b=>b.id===id?{...b,...changes}:b)}}));

  const setStudentStatus=(bookId,studentId,status)=>{
    setDB(p=>{
      const cur=(p.bookPayments||{})[courseId]||{};
      const bookStatuses={...(cur[bookId]||{})};
      if(status===null) delete bookStatuses[studentId];
      else bookStatuses[studentId]=status;
      return{...p,bookPayments:{...(p.bookPayments||{}),[courseId]:{...cur,[bookId]:bookStatuses}}};
    });
  };

  const activeBook=books.find(b=>b.id===activeBookId)||books[0];

  return(<>
    <h2 style={D.secTitle}>إدارة الكتب</h2>
    <CollapsibleGroup label="إضافة كتاب جديد" color="#0ea5e9" defaultOpen={books.length===0}>
      <div style={{padding:14,background:"#0f172a"}}>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10}}>
          <input style={D.input} placeholder="اسم الكتاب" value={form.name} onChange={setF("name")}/>
          <input style={D.input} type="number" placeholder="تكلفة طباعة الوحدة" value={form.printCost} onChange={setF("printCost")}/>
          <input style={D.input} type="number" placeholder="سعر البيع للطالب" value={form.salePrice} onChange={setF("salePrice")}/>
          <input style={D.input} type="number" placeholder="عدد الكتب الجديدة" value={form.stock} onChange={setF("stock")}/>
          <input style={{...D.input,borderColor:"#7c3aed66"}} type="number" placeholder="متبقي من الدفعة السابقة" value={form.prevStock} onChange={setF("prevStock")}/>
        </div>
        {(num(form.stock)+num(form.prevStock))>0&&(
          <div style={{marginTop:10,padding:"8px 14px",background:"#1e3a5f",borderRadius:8,fontSize:12,color:"#93c5fd",display:"flex",gap:16}}>
            <span>جديد: <strong>{num(form.stock)}</strong></span>
            <span>سابق: <strong style={{color:"#c084fc"}}>{num(form.prevStock)}</strong></span>
            <span>الإجمالي: <strong style={{color:"#4ade80",fontSize:14}}>{num(form.stock)+num(form.prevStock)}</strong></span>
          </div>
        )}
        <Btn style={{marginTop:12}} onClick={addBook}>إضافة كتاب</Btn>
      </div>
    </CollapsibleGroup>

    {books.length===0&&<EmptyState icon="—" label="لا توجد كتب" sub="أضف كتباً أولاً"/>}

    {books.length>0&&<>
      <BooksTable books={books} bookPayments={bookPayments} students={students} editable onEdit={setEditBook} onDelete={setDel}/>

      <h3 style={{...D.secTitle,fontSize:14,marginTop:20,marginBottom:10}}>متابعة دفع الكتب لكل طالب</h3>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
        {books.map(b=>(
          <button key={b.id} style={{...D.pill,...(activeBookId===b.id?D.pillActive:{})}} onClick={()=>setActiveBookId(b.id)}>{b.name}</button>
        ))}
      </div>
      {activeBook&&<BookPaymentGrid book={activeBook} students={students} statuses={bookPayments[activeBook.id]||{}} onSetStatus={(sid,st)=>setStudentStatus(activeBook.id,sid,st)}/>}
    </>}

    {editBook&&<EditBookModal book={editBook} onSave={ch=>{updateBook(editBook.id,ch);setEditBook(null);}} onClose={()=>setEditBook(null)}/>}
    {del&&<DeleteConfirmModal entry={{description:`حذف كتاب "${del.name}"`}} coursePassword="__skip__" onConfirm={()=>{
      setDB(p=>{
        const newBooks=(p.books[courseId]||[]).filter(b=>b.id!==del.id);
        const newBookPay=Object.fromEntries(Object.entries((p.bookPayments||{})[courseId]||{}).filter(([k])=>k!==del.id));
        return{...p,books:{...p.books,[courseId]:newBooks},bookPayments:{...(p.bookPayments||{}),[courseId]:newBookPay}};
      });
      setDel(null);
    }} onClose={()=>setDel(null)}/>}
  </>);
}

function BookPaymentGrid({book,students,statuses,onSetStatus}){
  const [searchQ,setSearchQ]=useState("");
  const filtered=searchQ?students.filter(s=>fuzzy(s.name,searchQ)):students;
  const paidCount=students.filter(s=>statuses[s.id]==="paid").length;
  const owedCount=students.filter(s=>statuses[s.id]==="owed").length;
  const exemptCount=students.filter(s=>statuses[s.id]==="exempt").length;
  const noneCount=students.length-paidCount-owedCount-exemptCount;
  return(
    <div style={D.card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10,marginBottom:12}}>
        <div style={{color:"#f1f5f9",fontWeight:700,fontSize:14}}>{book.name} — {currency(book.salePrice)}</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",fontSize:12}}>
          <span style={{color:"#4ade80"}}>دفعوا: {paidCount}</span>
          <span style={{color:"#fbbf24"}}>تحت التحصيل: {owedCount} ({currency(owedCount*num(book.salePrice))})</span>
          <span style={{color:"#60a5fa"}}>معفى: {exemptCount}</span>
          <span style={{color:"#64748b"}}>بدون تحديد: {noneCount}</span>
        </div>
      </div>
      <input style={{...D.input,marginBottom:10}} placeholder="بحث عن طالب..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
      <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:480,overflowY:"auto"}}>
        {filtered.map((s,i)=>{
          const st=statuses[s.id]||null;
          return(
            <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"#0f172a",border:"1px solid #1e293b",borderRadius:8}}>
              <span style={{color:"#475569",fontSize:11,minWidth:22}}>{i+1}</span>
              <span style={{color:"#f1f5f9",fontSize:13,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
              <span style={{color:"#64748b",fontSize:11,flexShrink:0}}>{s.branch}</span>
              <div style={{display:"flex",gap:4,flexShrink:0}}>
                <button style={{...D.pill,fontSize:11,padding:"4px 9px",...(st==="paid"?{background:"#16a34a",borderColor:"#16a34a",color:"#fff"}:{})}} onClick={()=>onSetStatus(s.id,st==="paid"?null:"paid")}>دفع</button>
                <button style={{...D.pill,fontSize:11,padding:"4px 9px",...(st==="owed"?{background:"#d97706",borderColor:"#d97706",color:"#fff"}:{})}} onClick={()=>onSetStatus(s.id,st==="owed"?null:"owed")}>عليه</button>
                <button style={{...D.pill,fontSize:11,padding:"4px 9px",...(st==="exempt"?{background:"#2563eb",borderColor:"#2563eb",color:"#fff"}:{})}} onClick={()=>onSetStatus(s.id,st==="exempt"?null:"exempt")}>معفى</button>
              </div>
            </div>
          );
        })}
        {filtered.length===0&&<div style={{color:"#475569",fontSize:12,padding:10,textAlign:"center"}}>لا يوجد طلاب</div>}
      </div>
    </div>
  );
}

function BooksTable({books,bookPayments,students,editable,onEdit,onDelete}){
  if(!books||books.length===0) return null;
  const bp=bookPayments||{};
  const st=students||[];
  const totals={printCost:0,totalPrintCost:0,newStock:0,prevStock:0,stock:0,paid:0,owed:0,exempt:0,revenue:0,pending:0};
  books.forEach(b=>{
    const statuses=bp[b.id]||{};
    const paidCount=st.filter(s=>statuses[s.id]==="paid").length;
    const owedCount=st.filter(s=>statuses[s.id]==="owed").length;
    const exemptCount=st.filter(s=>statuses[s.id]==="exempt").length;
    const unitCost=num(b.printCost);
    totals.printCost+=unitCost;totals.totalPrintCost+=unitCost*num(b.stock);
    totals.newStock+=num(b.newStock||b.stock||0);totals.prevStock+=num(b.prevStock||0);totals.stock+=num(b.stock);
    totals.paid+=paidCount;totals.owed+=owedCount;totals.exempt+=exemptCount;
    totals.revenue+=paidCount*num(b.salePrice);totals.pending+=owedCount*num(b.salePrice);
  });
  return(
    <div className="table-scroll" style={{marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",background:"#0f172a",borderRadius:12,overflow:"hidden",fontSize:11,minWidth:880}}>
        <thead>
          <tr style={{background:"#1e293b"}}>
            {["الكتاب","تكلفة الوحدة","إجمالي التكلفة","سعر البيع","جديد","سابق","الإجمالي","دفعوا","الإيراد","تحت التحصيل","معفى",...(editable?[""]:[])].map((h,i)=>(
              <th key={i} style={{padding:"9px 8px",textAlign:"right",color:"#64748b",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {books.map(b=>{
            const statuses=bp[b.id]||{};
            const paidCount=st.filter(s=>statuses[s.id]==="paid").length;
            const owedCount=st.filter(s=>statuses[s.id]==="owed").length;
            const exemptCount=st.filter(s=>statuses[s.id]==="exempt").length;
            const unitCost=num(b.printCost);
            const newSt=num(b.newStock||b.stock||0);const prevSt=num(b.prevStock||0);
            return(
              <tr key={b.id} style={{borderTop:"1px solid #1e293b"}}>
                <td style={{padding:"8px",color:"#f1f5f9",fontWeight:600}}>{b.name}</td>
                <td style={{padding:"8px",color:"#f43f5e"}}>{currency(unitCost)}</td>
                <td style={{padding:"8px",color:"#f97316"}}>{currency(unitCost*num(b.stock))}</td>
                <td style={{padding:"8px",color:"#0ea5e9"}}>{currency(b.salePrice)}</td>
                <td style={{padding:"8px",color:"#94a3b8"}}>{newSt}</td>
                <td style={{padding:"8px",color:"#c084fc",fontWeight:600}}>{prevSt}</td>
                <td style={{padding:"8px",color:"#4ade80",fontWeight:700}}>{num(b.stock)}</td>
                <td style={{padding:"8px",color:"#22c55e",fontWeight:700}}>{paidCount}</td>
                <td style={{padding:"8px",color:"#22c55e"}}>{currency(paidCount*num(b.salePrice))}</td>
                <td style={{padding:"8px",color:"#fbbf24",fontWeight:700}}>{currency(owedCount*num(b.salePrice))}</td>
                <td style={{padding:"8px",color:"#60a5fa"}}>{exemptCount}</td>
                {editable&&<td style={{padding:"8px"}}><div style={{display:"flex",gap:4}}>
                  <button style={{...D.iconBtn,color:"#60a5fa"}} onClick={()=>onEdit(b)}>تعديل</button>
                  <button style={D.iconBtn} onClick={()=>onDelete(b)}>حذف</button>
                </div></td>}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{borderTop:"2px solid #334155",background:"#1e293b"}}>
            <td style={{padding:"8px",color:"#f1f5f9",fontWeight:700}}>الإجمالي</td>
            <td style={{padding:"8px",color:"#f43f5e",fontWeight:700}}>{currency(totals.printCost)}</td>
            <td style={{padding:"8px",color:"#f97316",fontWeight:700}}>{currency(totals.totalPrintCost)}</td>
            <td/>
            <td style={{padding:"8px",color:"#94a3b8",fontWeight:700}}>{totals.newStock}</td>
            <td style={{padding:"8px",color:"#c084fc",fontWeight:700}}>{totals.prevStock}</td>
            <td style={{padding:"8px",color:"#4ade80",fontWeight:700}}>{totals.stock}</td>
            <td style={{padding:"8px",color:"#22c55e",fontWeight:700}}>{totals.paid}</td>
            <td style={{padding:"8px",color:"#22c55e",fontWeight:700}}>{currency(totals.revenue)}</td>
            <td style={{padding:"8px",color:"#fbbf24",fontWeight:700}}>{currency(totals.pending)}</td>
            <td style={{padding:"8px",color:"#60a5fa",fontWeight:700}}>{totals.exempt}</td>
            {editable&&<td/>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function EditBookModal({book,onSave,onClose}){
  const [f,setF]=useState({printCost:book.printCost||0,salePrice:book.salePrice||0,newStock:book.newStock||(book.prevStock?book.stock-(book.prevStock||0):book.stock)||0,prevStock:book.prevStock||0});
  const sf=k=>e=>setF(p=>({...p,[k]:Number(e.target.value)}));
  const totalStock=f.newStock+f.prevStock;const unitCost=f.printCost;const margin=f.salePrice-unitCost;
  const saveWithTotal=()=>onSave({...f,stock:totalStock});
  return(
    <Modal title={`تعديل: ${book.name}`} onClose={onClose}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <Field label="تكلفة طباعة الوحدة (ج.م)"><input style={D.input} type="number" value={f.printCost} onChange={sf("printCost")}/></Field>
        <Field label="سعر البيع (ج.م)"><input style={D.input} type="number" value={f.salePrice} onChange={sf("salePrice")}/></Field>
        <Field label="طباعة جديدة (عدد)"><input style={D.input} type="number" value={f.newStock} onChange={sf("newStock")}/></Field>
        <Field label="متبقي من الدفعة السابقة"><input style={{...D.input,borderColor:"#7c3aed66"}} type="number" value={f.prevStock} onChange={sf("prevStock")}/></Field>
        <div style={{gridColumn:"1/-1",padding:"10px 14px",background:"#1e3a5f",borderRadius:8,display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{color:"#64748b",fontSize:12}}>إجمالي المخزن:</span>
          <span style={{color:"#4ade80",fontWeight:800,fontSize:18}}>{totalStock}</span>
          <span style={{color:"#c084fc",fontSize:12}}>({f.prevStock} من السابقة + {f.newStock} جديد)</span>
          <span style={{color:margin>=0?"#22c55e":"#f87171",fontSize:12,marginRight:"auto"}}>هامش الربح: {currency(margin)}</span>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginTop:16}}><Btn onClick={saveWithTotal}>حفظ</Btn><Btn ghost onClick={onClose}>إلغاء</Btn></div>
    </Modal>
  );
}

/* ═══════════ STUDENTS PAGE ═══════════ */
function StudentsPage({courseId,students,setDB,onTogglePaid,subPrice,activeBranch,courseBranches,highlightStudentId,onHighlightDone}){
  const singleBranch=courseBranches?.length===1?courseBranches[0]:null;
  const [name,setName]=useState("");const [branch,setBranch]=useState(singleBranch||activeBranch||"المنصورة");const [gender,setGender]=useState("ذكر");
  const [del,setDel]=useState(null);const [searchQ,setSearchQ]=useState("");const [showNames,setShowNames]=useState(true);
  const [lastImportBatch,setLastImportBatch]=useState(null);const [showBulkDelete,setShowBulkDelete]=useState(false);const [selectedForDelete,setSelectedForDelete]=useState(new Set());
  const fileRef=useRef();
  const add=()=>{if(!name.trim())return;setDB(p=>({...p,students:{...p.students,[courseId]:[...(p.students[courseId]||[]),{id:uid(),name:name.trim(),branch,gender,paid:false,paidAmount:0,notes:""}]}}));setName("");};
  const importCSV=(e)=>{
    const file=e.target.files[0];if(!file)return;
    const tryDecode=(buf)=>{const utf8=new TextDecoder("utf-8",{fatal:true});try{return utf8.decode(buf);}catch{}const win1256=new TextDecoder("windows-1256",{fatal:false});return win1256.decode(buf);};
    const reader=new FileReader();
    reader.onload=(ev)=>{try{const text=tryDecode(ev.target.result);const lines=text.split(/\r?\n/).filter(l=>l.trim());const imported=lines.map(line=>{const parts=line.split(",").map(p=>p.trim());const nm=(parts[0]||"");let finalBr=branch;let finalGn=gender;for(let i=1;i<parts.length;i++){const p=parts[i];if(p==="المنصورة"||p==="القاهرة")finalBr=p;else if(p==="أنثى"||p==="انثى"||p==="female")finalGn="أنثى";else if(p==="ذكر"||p==="male")finalGn="ذكر";}return{id:uid(),name:nm,branch:finalBr,gender:finalGn,paid:false,paidAmount:0,notes:"",importBatch:Date.now()};}).filter(s=>s.name);setDB(p=>({...p,students:{...p.students,[courseId]:[...(p.students[courseId]||[]),...imported]}}));setLastImportBatch(imported[0]?.importBatch||null);alert(`تم استيراد ${imported.length} طالب`);}catch{alert("خطأ في قراءة الملف");}};
    reader.readAsArrayBuffer(file);e.target.value="";
  };
  const updateStudent=(id,changes)=>setDB(p=>({...p,students:{...p.students,[courseId]:(p.students[courseId]||[]).map(s=>s.id===id?{...s,...changes}:s)}}));
  const filterSearch=(q,list)=>q?list.filter(s=>fuzzy(s.name,q)):list;
  const toggleSelect=(id)=>setSelectedForDelete(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});
  const mansura=students.filter(s=>s.branch==="المنصورة");const cairo=students.filter(s=>s.branch==="القاهرة");
  const allBranchData=[["المنصورة",mansura,"#22c55e"],["القاهرة",cairo,"#3b82f6"]];
  const visibleBranches=courseBranches?.length>1?[[activeBranch,activeBranch==="المنصورة"?mansura:cairo,activeBranch==="المنصورة"?"#22c55e":"#3b82f6"]]:allBranchData.filter(([br])=>courseBranches?.includes(br)||courseBranches?.length===0);
  const highlightedStudent=highlightStudentId?students.find(s=>s.id===highlightStudentId):null;
  useEffect(()=>{
    if(highlightedStudent&&searchQ){setSearchQ("");}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[highlightStudentId]);
  return(<>
    <h2 style={D.secTitle}>قائمة الطلاب ({students.length})</h2>
    <div style={{...D.card,marginBottom:14,display:"flex",gap:10,alignItems:"center"}}>
      <input style={{...D.input,flex:1}} placeholder="بحث في أسماء الطلاب..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
      {searchQ&&<button style={D.iconBtn} onClick={()=>setSearchQ("")}>X</button>}
    </div>
    <div style={{...D.card,marginBottom:20}}>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
        <input style={{...D.input,flex:3}} placeholder="اسم الطالب" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()}/>
        {!singleBranch&&<CustomSelect style={{flex:1}} value={branch} onChange={setBranch} options={[{value:"المنصورة",label:"المنصورة"},{value:"القاهرة",label:"القاهرة"}]}/>}
        <CustomSelect style={{flex:1}} value={gender} onChange={setGender} options={[{value:"ذكر",label:"ذكر"},{value:"أنثى",label:"أنثى"}]}/>
        <Btn onClick={add}>إضافة</Btn>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        <button style={{...D.pill,background:"#1e3a5f",color:"#93c5fd",borderColor:"#1d4ed8"}} onClick={()=>fileRef.current.click()}>استيراد CSV</button>
        <input ref={fileRef} type="file" accept=".csv,.txt" style={{display:"none"}} onChange={importCSV}/>
        {lastImportBatch&&students.some(s=>s.importBatch===lastImportBatch)&&(
          <button style={{...D.pill,background:"#450a0a",color:"#f87171",borderColor:"#7f1d1d",fontSize:11}} onClick={()=>{showConfirm("حذف الطلاب الذين تم استيرادهم في آخر مرة",{title:"حذف آخر استيراد",danger:true,confirmText:"حذف"}).then(ok=>{if(ok){setDB(p=>({...p,students:{...p.students,[courseId]:(p.students[courseId]||[]).filter(s=>s.importBatch!==lastImportBatch)}}));setLastImportBatch(null);}});}}>حذف آخر استيراد ({students.filter(s=>s.importBatch===lastImportBatch).length})</button>
        )}
        <button style={{...D.pill,background:showBulkDelete?"#7c2d12":"#1e293b",color:showBulkDelete?"#fdba74":"#94a3b8",fontSize:11}} onClick={()=>{setShowBulkDelete(p=>!p);setSelectedForDelete(new Set());}}>حذف متعدد</button>
      </div>
      {showBulkDelete&&(
        <div style={{marginTop:12,padding:"10px 14px",background:"#2d0a0a",border:"1px solid #7f1d1d",borderRadius:10}}>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:10}}>
            <span style={{color:"#f87171",fontSize:13,fontWeight:700}}>وضع الحذف المتعدد</span>
            <button style={{...D.pill,fontSize:11}} onClick={()=>setSelectedForDelete(new Set(students.map(s=>s.id)))}>تحديد الكل</button>
            <button style={{...D.pill,fontSize:11}} onClick={()=>setSelectedForDelete(new Set())}>إلغاء التحديد</button>
            {selectedForDelete.size>0&&(<button style={{...D.btn,background:"#dc2626",fontSize:12,padding:"5px 12px"}} onClick={()=>{showConfirm(`حذف ${selectedForDelete.size} طالب نهائياً`,{title:"حذف جماعي",danger:true,confirmText:"حذف"}).then(ok=>{if(ok){setDB(p=>({...p,students:{...p.students,[courseId]:(p.students[courseId]||[]).filter(s=>!selectedForDelete.has(s.id))}}));setSelectedForDelete(new Set());setShowBulkDelete(false);}});}}>حذف {selectedForDelete.size} طالب</button>)}
            <button style={{...D.pill,fontSize:11,color:"#94a3b8"}} onClick={()=>{setShowBulkDelete(false);setSelectedForDelete(new Set());}}>إلغاء</button>
          </div>
        </div>
      )}
    </div>
    <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
      {[["رجال",students.filter(s=>(s.gender||"ذكر")!=="أنثى").length,"#3b82f6"],["نساء",students.filter(s=>(s.gender||"ذكر")==="أنثى").length,"#ec4899"],["دفعوا",students.filter(s=>s.paid).length,"#22c55e"],["لم يدفعوا",students.filter(s=>!s.paid).length,"#f87171"]].map(([l,v,c])=>(
      <div key={l} style={{...D.miniStat,flex:1,minWidth:100}}><div style={{color:"#64748b",fontSize:11}}>{l}</div><div style={{color:c,fontWeight:700,fontSize:16}}>{v}</div></div>
      ))}
    </div>
    {(()=>{
      const branchFilter=courseBranches?.length>1?activeBranch:null;
      const visible=branchFilter?students.filter(s=>s.branch===branchFilter):students;
      const fullyExempt=visible.filter(s=>s.exempted);
      const fullPaid=visible.filter(s=>!s.exempted&&s.paid&&!s.partialExempt);
      const partialExemptPaid=visible.filter(s=>!s.exempted&&s.paid&&s.partialExempt);
      const partialUnpaid=visible.filter(s=>!s.exempted&&!s.paid&&s.paidAmount>0);
      const unpaidAll=visible.filter(s=>!s.exempted&&!s.paid&&!(s.paidAmount>0));
      if(subPrice===0) return(
        <div style={{...D.card,marginBottom:14,background:"#0f172a",border:"1px solid #1e293b",display:"flex",alignItems:"center",gap:10,padding:"10px 16px"}}>
          <span style={{color:"#fbbf24",fontSize:16}}>!</span>
          <span style={{color:"#fbbf24",fontSize:13,fontWeight:600}}>لم تحدد الإدارة سعر الاشتراك بعد — متابعة الدفع معطّلة</span>
        </div>
      );
      const Group=({label,color,list,renderTag})=>list.length>0&&(
        <CollapsibleGroup label={`${label} (${list.length})`} color={color} defaultOpen={false}>
          <div style={{padding:12,background:"#0a1628"}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:6}}>
              {list.map(s=>(
                <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#0f172a",border:`1px solid ${color}33`,borderRadius:8,padding:"7px 12px",gap:8}}>
                  <span style={{color:"#f1f5f9",fontSize:12,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</span>
                  <div style={{display:"flex",gap:4,alignItems:"center",flexShrink:0}}>
                    <span style={{color:"#64748b",fontSize:10}}>{s.branch}</span>
                    {renderTag(s)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CollapsibleGroup>
      );
      return(<>
        <Group label="إعفاء كامل" color="#60a5fa" list={fullyExempt} renderTag={()=><span style={{color:"#60a5fa",fontSize:11,fontWeight:700,background:"#1e3a5f",borderRadius:5,padding:"2px 7px"}}>معفى</span>}/>
        <Group label="دفعوا كامل المبلغ" color="#22c55e" list={fullPaid} renderTag={()=><span style={{color:"#4ade80",fontSize:11,fontWeight:700,background:"#052e16",borderRadius:5,padding:"2px 7px"}}>{currency(subPrice)}</span>}/>
        <Group label="إعفاء جزئي (مكتمل)" color="#a78bfa" list={partialExemptPaid} renderTag={s=><span style={{color:"#c4b5fd",fontSize:11,fontWeight:700,background:"#2e1065",borderRadius:5,padding:"2px 7px"}}>دفع {currency(s.paidAmount)} + إعفاء {currency(s.exemptedAmount)}</span>}/>
        <Group label="دفعوا جزء" color="#fbbf24" list={partialUnpaid} renderTag={s=><span style={{color:"#fbbf24",fontSize:11,fontWeight:700,background:"#451a03",borderRadius:5,padding:"2px 7px"}}>دفع {currency(s.paidAmount)} · باقي {currency(Math.max(0,subPrice-(s.paidAmount||0)-(s.exemptedAmount||0)))}</span>}/>
        <Group label="لم يدفعوا" color="#f87171" list={unpaidAll} renderTag={()=><span style={{color:"#f87171",fontSize:11,fontWeight:700,background:"#450a0a",borderRadius:5,padding:"2px 7px"}}>{currency(subPrice)}</span>}/>
        {fullyExempt.length+fullPaid.length+partialExemptPaid.length+partialUnpaid.length+unpaidAll.length===0&&(
          <div style={{...D.card,marginBottom:12,background:"#0f172a",color:"#475569",fontSize:13,textAlign:"center",padding:20}}>لا يوجد طلاب بعد</div>
        )}
      </>);
    })()}
    <div style={{display:"flex",justifyContent:"flex-end",marginBottom:12}}>
      <button style={{...D.pill,fontSize:12}} onClick={()=>setShowNames(p=>!p)}>{showNames?"إخفاء الأسماء":"إظهار الأسماء"}</button>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:16}}>
      {visibleBranches.map(([br,list,col])=>{
        const males=filterSearch(searchQ,list.filter(s=>(s.gender||"ذكر")!=="أنثى"));const females=filterSearch(searchQ,list.filter(s=>(s.gender||"ذكر")==="أنثى"));
        const highlightInMales=highlightedStudent&&males.some(s=>s.id===highlightedStudent.id);
        const highlightInFemales=highlightedStudent&&females.some(s=>s.id===highlightedStudent.id);
        return(<div key={br}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <h3 style={{color:col,fontSize:14,fontWeight:700,margin:0}}>{br} ({list.length})</h3>
          </div>
          <CollapsibleGroup label={`رجال (${males.length})`} color="#3b82f6" forceOpen={highlightInMales}>
            <StudentTable rows={males} showNames={showNames} onPaid={onTogglePaid} onDel={setDel} onNote={updateStudent} subPrice={subPrice} bulkMode={showBulkDelete} selectedForDelete={selectedForDelete} onToggleSelect={toggleSelect} highlightId={highlightInMales?highlightedStudent.id:null} onHighlightDone={onHighlightDone}/>
          </CollapsibleGroup>
          <div style={{marginTop:8}}>
            <CollapsibleGroup label={`نساء (${females.length})`} color="#ec4899" forceOpen={highlightInFemales}>
              <StudentTable rows={females} showNames={showNames} onPaid={onTogglePaid} onDel={setDel} onNote={updateStudent} subPrice={subPrice} bulkMode={showBulkDelete} selectedForDelete={selectedForDelete} onToggleSelect={toggleSelect} highlightId={highlightInFemales?highlightedStudent.id:null} onHighlightDone={onHighlightDone}/>
            </CollapsibleGroup>
          </div>
        </div>);
      })}
    </div>
    {del&&<DeleteConfirmModal entry={{description:`حذف الطالب "${del.name}"`}} coursePassword="__skip__" onConfirm={()=>{setDB(p=>({...p,students:{...p.students,[courseId]:(p.students[courseId]||[]).filter(x=>x.id!==del.id)}}));setDel(null);}} onClose={()=>setDel(null)}/>}
  </>);
}

/* ═══════════ STAFF ═══════════ */
function StaffPage({course,setDB}){
  const [newAss,setNewAss]=useState("");const [tName,setTName]=useState("");const [tSub,setTSub]=useState("");const logoRef=useRef();
  const addAss=()=>{if(!newAss.trim())return;setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,assistants:[...(c.assistants||[]),{name:newAss.trim()}]}:c)}));setNewAss("");};
  const addT=()=>{if(!tName.trim())return;setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,trainers:[...(c.trainers||[]),{name:tName.trim(),subject:tSub.trim()}]}:c)}));setTName("");setTSub("");};
  const rmAss=i=>setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,assistants:c.assistants.filter((_,x)=>x!==i)}:c)}));
  const rmT=i=>setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,trainers:c.trainers.filter((_,x)=>x!==i)}:c)}));
  const uploadLogo=e=>{if(!e.target.files[0])return;const r=new FileReader();r.onload=ev=>setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,logo:ev.target.result}:c)}));r.readAsDataURL(e.target.files[0]);e.target.value="";};
  return(<>
    <h2 style={D.secTitle}>الكوادر البشرية</h2>
    <div style={{...D.card,marginBottom:16,display:"flex",alignItems:"center",gap:16}}>
      {course.logo?<img src={course.logo} alt="logo" style={{width:64,height:64,borderRadius:14,objectFit:"contain",background:"#1e293b",padding:4}}/>:<div style={{width:64,height:64,borderRadius:14,background:"#1e293b",display:"flex",alignItems:"center",justifyContent:"center",fontSize:30}}>-</div>}
      <div>
        <div style={{color:"#f1f5f9",fontWeight:700,marginBottom:6}}>{course.name}</div>
        <div style={{display:"flex",gap:8}}>
          <button style={{...D.pill,fontSize:11}} onClick={()=>logoRef.current.click()}>تغيير لوجو الدورة</button>
          {course.logo&&<button style={{...D.pill,fontSize:11,color:"#f87171"}} onClick={()=>setDB(p=>({...p,courses:p.courses.map(c=>c.id===course.id?{...c,logo:""}:c)}))}>حذف اللوجو</button>}
        </div>
        <input ref={logoRef} type="file" accept="image/*" style={{display:"none"}} onChange={uploadLogo}/>
      </div>
    </div>
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16}}>
      <div style={D.card}>
        <h3 style={{color:"#94a3b8",fontSize:13,marginBottom:12}}>المشرفون</h3>
        <div style={D.table}>
          <div style={{...D.tHead,gridTemplateColumns:"1fr 1fr"}}><span>الاسم</span><span>الدور</span></div>
          <div style={{...D.tRow,gridTemplateColumns:"1fr 1fr"}}><span style={{color:"#f1f5f9"}}>{course.manager}</span><span style={{color:"#94a3b8"}}>رئيسي</span></div>
          {(course.assistants||[]).map((a,i)=><div key={i} style={{...D.tRow,gridTemplateColumns:"1fr 1fr auto"}}><span style={{color:"#f1f5f9"}}>{a.name}</span><span style={{color:"#94a3b8"}}>مساعد</span><button style={D.iconBtn} onClick={()=>rmAss(i)}>X</button></div>)}
        </div>
        <div style={{display:"flex",gap:8,marginTop:12}}><input style={{...D.input,flex:1}} placeholder="اسم المساعد" value={newAss} onChange={e=>setNewAss(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addAss()}/><Btn small onClick={addAss}>إضافة</Btn></div>
      </div>
      <div style={D.card}>
        <h3 style={{color:"#94a3b8",fontSize:13,marginBottom:12}}>طاقم التدريس</h3>
        <div style={D.table}>
          <div style={{...D.tHead,gridTemplateColumns:"1fr 1fr auto"}}><span>الاسم</span><span>المادة</span><span></span></div>
          {(course.trainers||[]).length===0&&<div style={{padding:"10px 16px",color:"#475569",fontSize:13}}>لا يوجد</div>}
          {(course.trainers||[]).map((t,i)=><div key={i} style={{...D.tRow,gridTemplateColumns:"1fr 1fr auto"}}><span style={{color:"#f1f5f9"}}>{t.name}</span><span style={{color:"#93c5fd"}}>{t.subject||"—"}</span><button style={D.iconBtn} onClick={()=>rmT(i)}>X</button></div>)}
        </div>
        <div style={{display:"flex",gap:8,marginTop:12,flexWrap:"wrap"}}><input style={{...D.input,flex:2}} placeholder="الاسم" value={tName} onChange={e=>setTName(e.target.value)}/><input style={{...D.input,flex:2}} placeholder="المادة" value={tSub} onChange={e=>setTSub(e.target.value)}/><Btn small onClick={addT}>إضافة</Btn></div>
      </div>
    </div>
  </>);
}

/* ═══════════ BATCH REPORT ═══════════ */
function BatchReport({batch,onBack}){
  const entries=batch.entries||[];const books=batch.books||[];const students=batch.students||[];
  const inc=entries.filter(e=>typeById(e.type).group==="income").reduce((s,e)=>s+num(e.amount),0);
  const exp=entries.filter(e=>typeById(e.type).group==="expense").reduce((s,e)=>s+num(e.amount),0);
  const bookPay=batch.bookPayments||{};
  const bks=books.reduce((sum,b)=>{
    const statuses=bookPay[b.id]||{};
    const paidCount=students.filter(s=>statuses[s.id]==="paid").length;
    return sum+paidCount*num(b.salePrice);
  },0);
  const bkp=entries.filter(e=>e.type==="book_print").reduce((s,e)=>s+num(e.amount),0);
  const printPDF=()=>{const el=document.getElementById("rpt");const w=window.open("","_blank");w.document.write(`<html><head><meta charset="utf-8"><title>تقرير ${batch.courseName}</title><style>body{font-family:Cairo,sans-serif;direction:rtl;background:#fff;color:#0f172a;padding:24px;font-size:12px}h1{font-size:18px}h2{font-size:14px;margin:16px 0 8px;border-bottom:1px solid #e2e8f0;padding-bottom:4px}table{width:100%;border-collapse:collapse;margin-bottom:12px}th,td{padding:7px 10px;text-align:right;border:1px solid #e2e8f0}th{background:#f8fafc;font-weight:700}</style></head><body>${el.innerHTML}</body></html>`);w.document.close();w.focus();setTimeout(()=>{w.print();w.close();},400);};
  return(<>
    <div style={{display:"flex",gap:10,marginBottom:20,alignItems:"center"}}>
      {onBack&&<button style={D.pill} onClick={onBack}>رجوع</button>}
      <button style={{...D.btn,background:"#dc2626"}} onClick={printPDF}>طباعة / PDF</button>
    </div>
    <div id="rpt">
      <h2 style={{color:"#f1f5f9",fontSize:18,margin:"0 0 4px"}}>{batch.courseName}</h2>
      <p style={{color:"#64748b",fontSize:13,marginBottom:16}}>دفعة {batch.batchNumber} · {batch.branch} · {batch.manager} · اكتملت: {fmtDate(batch.completedAt)}</p>
      <div style={D.statsGrid} className="rw-stats-mobile"><MiniStat label="الوارد" value={currency(inc)} color="#22c55e"/><MiniStat label="المصروف" value={currency(exp)} color="#ef4444"/><MiniStat label="مبيعات الكتب" value={currency(bks)} color="#0ea5e9"/><MiniStat label="تكلفة طباعة" value={currency(bkp)} color="#f43f5e"/><MiniStat label="الصافي" value={currency(inc-exp+bks-bkp)} color={(inc-exp+bks-bkp)>=0?"#a3e635":"#fb923c"} bold/></div>
      <h2 style={{color:"#94a3b8",fontSize:14,margin:"20px 0 10px"}}>القيود</h2>
      <div className="table-scroll"><div style={D.table}><div style={{...D.tHead,gridTemplateColumns:"2fr 1fr 1fr 2fr"}}><span>النوع</span><span>المبلغ</span><span>التاريخ</span><span>تفاصيل</span></div>{[...entries].sort((a,b)=>new Date(a.createdAt)-new Date(b.createdAt)).map(e=><div key={e.id} style={{...D.tRow,gridTemplateColumns:"2fr 1fr 1fr 2fr"}}><span style={{color:"#f1f5f9"}}>{typeById(e.type).label}</span><span style={{color:typeById(e.type).group==="expense"?"#f87171":"#4ade80",fontWeight:600}}>{currency(e.amount)}</span><span style={{color:"#64748b",fontSize:11}}>{fmtDT(e.createdAt)}</span><span style={{color:"#94a3b8",fontSize:12}}>{[e.recipient,e.description].filter(Boolean).join(" · ")||"—"}</span></div>)}</div></div>
      <h2 style={{color:"#94a3b8",fontSize:14,margin:"16px 0 10px"}}>الكتب</h2><BooksTable books={books} bookPayments={bookPay} students={students}/>
      <h2 style={{color:"#94a3b8",fontSize:14,margin:"16px 0 10px"}}>ملخص الطلاب ({students.length})</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:10,marginBottom:16}}>
        {[["إجمالي",students.length,"#94a3b8"],["رجال",students.filter(s=>(s.gender||"ذكر")!=="أنثى").length,"#60a5fa"],["نساء",students.filter(s=>(s.gender||"ذكر")==="أنثى").length,"#f472b6"],["دفعوا",students.filter(s=>s.paid).length,"#4ade80"],["لم يدفعوا",students.filter(s=>!s.paid).length,"#f87171"]].map(([l,v,c])=>(
          <div key={l} style={{background:"#1e293b",borderRadius:10,padding:"10px 14px"}}><div style={{color:"#64748b",fontSize:11}}>{l}</div><div style={{color:c,fontWeight:700,fontSize:18}}>{v}</div></div>
        ))}
      </div>
      {students.filter(s=>s.paid).length>0&&(
        <>
          <h2 style={{color:"#94a3b8",fontSize:14,margin:"16px 0 10px"}}>الطلاب الذين دفعوا الاشتراك ({students.filter(s=>s.paid).length})</h2>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
            {students.filter(s=>s.paid).map(s=>(
              <span key={s.id} style={{background:"#052e16",border:"1px solid #166534",borderRadius:8,padding:"4px 10px",color:"#86efac",fontSize:12}}>{s.name}</span>
            ))}
          </div>
        </>
      )}
    </div>
  </>);
}

/* ─── CollapsibleGroup ─── */
function CollapsibleGroup({label,color,children,defaultOpen=true,forceOpen}){
  const [open,setOpen]=useState(defaultOpen);
  useEffect(()=>{if(forceOpen)setOpen(true);},[forceOpen]);
  return(
    <div style={{marginBottom:8}}>
      <button onClick={()=>setOpen(p=>!p)} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",background:"#1e293b",border:"1px solid #334155",borderRadius:open?"10px 10px 0 0":"10px",padding:"8px 14px",cursor:"pointer",fontFamily:"inherit"}}>
        <span style={{color,fontWeight:700,fontSize:13}}>{label}</span>
        <span style={{color:"#64748b",fontSize:12,transition:"transform .2s",transform:open?"rotate(180deg)":"rotate(0deg)"}}>V</span>
      </button>
      {open&&<div style={{border:"1px solid #334155",borderTop:"none",borderRadius:"0 0 10px 10px",overflow:"hidden"}}>{children}</div>}
    </div>
  );
}

function StudentTable({rows,showNames,onPaid,onDel,onNote,subPrice,bulkMode,selectedForDelete,onToggleSelect,highlightId,onHighlightDone}){
  if(rows.length===0) return <div style={{padding:"10px 16px",color:"#475569",fontSize:12,background:"#0f172a"}}>لا يوجد</div>;
  return(
    <div>
      {rows.map((s,i)=>(
        <StudentRow key={s.id} s={s} i={i} showNames={showNames} onPaid={onPaid} onDel={onDel} onNote={onNote} subPrice={subPrice} bulkMode={bulkMode} selectedForDelete={selectedForDelete} onToggleSelect={onToggleSelect} highlight={highlightId===s.id} onHighlightDone={onHighlightDone}/>
      ))}
    </div>
  );
}

function StudentRow({s,i,showNames,onPaid,onDel,onNote,subPrice,bulkMode,selectedForDelete,onToggleSelect,highlight,onHighlightDone}){
  const ref=useRef(null);
  const [flash,setFlash]=useState(false);
  useEffect(()=>{
    if(highlight&&ref.current){
      ref.current.scrollIntoView({behavior:"smooth",block:"center"});
      setFlash(true);
      const t=setTimeout(()=>{setFlash(false);onHighlightDone&&onHighlightDone();},2200);
      return ()=>clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[highlight]);
  return(
    <div ref={ref}
      style={{display:"grid",gridTemplateColumns:bulkMode?"auto auto 1fr auto auto":"auto 1fr auto auto auto",gap:6,padding:"8px 14px",borderTop:i===0?"none":"1px solid #1e293b",background:flash?"#1e3a5f":bulkMode&&selectedForDelete?.has(s.id)?"#2d0a0a":"#0f172a",alignItems:"center",cursor:bulkMode?"pointer":"default",transition:"background .4s"}}
      onClick={bulkMode?()=>onToggleSelect(s.id):undefined}>
      {bulkMode&&(<div style={{width:18,height:18,borderRadius:5,border:"2px solid",borderColor:selectedForDelete?.has(s.id)?"#f87171":"#334155",background:selectedForDelete?.has(s.id)?"#dc2626":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:11,color:"#fff"}}>{selectedForDelete?.has(s.id)&&"+"}</div>)}
      <span style={{color:"#475569",fontSize:12}}>{i+1}</span>
      <span style={{color:bulkMode&&selectedForDelete?.has(s.id)?"#fca5a5":"#f1f5f9",fontSize:13}}>{showNames?s.name:"•••••••"}</span>
      {!bulkMode&&(subPrice>0
        ?<button style={{background:s.paid?(s.exempted?"#1e3a5f":"#052e16"):s.paidAmount>0?"#1c1a05":"#450a0a",color:s.paid?(s.exempted?"#93c5fd":"#4ade80"):s.paidAmount>0?"#fbbf24":"#f87171",border:"none",borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:11,fontFamily:"inherit",whiteSpace:"nowrap"}} onClick={e=>{e.stopPropagation();onPaid(s.name);}}>
            {s.paid?(s.exempted?"معفى":"دفع"):s.paidAmount>0?`جزئي ${currency(s.paidAmount)}`:"لم يدفع"}
          </button>
        :<span style={{fontSize:10,color:"#475569",background:"#0f172a",border:"1px solid #1e293b",borderRadius:6,padding:"3px 8px"}}>بانتظار السعر</span>
      )}
      {!bulkMode&&subPrice>0&&(<span style={{fontSize:11,whiteSpace:"nowrap",color:s.paid?"#22c55e":s.paidAmount>0?"#fbbf24":"#f87171"}}>{s.paid?(s.exempted?"إعفاء كامل":currency(subPrice)):s.paidAmount>0?`دفع ${currency(s.paidAmount)}`:currency(subPrice)}</span>)}
      {!bulkMode&&<input style={{width:90,background:"#1e293b",border:"1px solid #334155",borderRadius:6,color:"#94a3b8",padding:"4px 8px",fontSize:11,fontFamily:"inherit",direction:"rtl",boxSizing:"border-box",outline:"none"}} placeholder="ملاحظة..." value={s.notes||""} onChange={e=>{e.stopPropagation();onNote(s.id,{notes:e.target.value});}}/>}
      {!bulkMode&&<button style={{background:"none",border:"none",cursor:"pointer",color:"#475569",padding:"2px 4px",fontSize:14}} onClick={e=>{e.stopPropagation();onDel(s);}}>حذف</button>}
    </div>
  );
}

/* ─── StudentSelector ─── */
function StudentSelector({students,branch,value,onChange,onCustomName}){
  const [q,setQ]=useState("");
  const brList=students.filter(s=>s.branch===branch);
  const shown=q?brList.filter(s=>fuzzy(s.name,q)):brList;
  const selectedStudent=brList.find(s=>s.name===value);
  return(
    <Field label="اسم الطالب *">
      <div style={{display:"flex",gap:8,marginBottom:8}}>
        <input style={{...D.input,flex:1}} placeholder="بحث سريع في الأسماء..." value={q} onChange={e=>setQ(e.target.value)} autoFocus/>
        {q&&<button style={D.iconBtn} onClick={()=>setQ("")}>X</button>}
      </div>
      {value&&value!=="__new__"&&!q&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#1e3a5f",border:"1px solid #1d4ed8",borderRadius:10,padding:"10px 14px",marginBottom:8}}>
          <span style={{color:"#93c5fd",fontSize:13,fontWeight:700}}>{selectedStudent?((selectedStudent.gender||"ذكر")==="أنثى"?"◆ ":"◇ "):""}{value}</span>
          <button onClick={()=>onChange("")} style={{...D.iconBtn,color:"#93c5fd"}}>تغيير</button>
        </div>
      )}
      {(!value||q)&&(
        <div style={{background:"#111827",border:"1px solid #1f2937",borderRadius:10,maxHeight:220,overflowY:"auto"}}>
          {shown.length===0&&<div style={{padding:"14px",color:"#5b6b85",fontSize:13,textAlign:"center"}}>لا نتائج</div>}
          {shown.map(s=>(
            <div key={s.id} onClick={()=>{onChange(s.name);setQ("");}}
              style={{padding:"10px 14px",borderBottom:"1px solid #1a2333",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",
                background:s.name===value?"#1e3a5f":"transparent"}}
              onMouseEnter={e=>{if(s.name!==value)e.currentTarget.style.background="#161f30";}}
              onMouseLeave={e=>{if(s.name!==value)e.currentTarget.style.background="transparent";}}>
              <span style={{color:"#f1f5f9",fontSize:13}}>{(s.gender||"ذكر")==="أنثى"?"◆ ":"◇ "}{s.name}</span>
              {s.paid&&<span style={{color:"#4ade80",fontSize:10,fontWeight:700,background:"#052e16",borderRadius:5,padding:"2px 6px"}}>دفع</span>}
            </div>
          ))}
          <div onClick={()=>{onChange("__new__");setQ("");}}
            style={{padding:"10px 14px",cursor:"pointer",color:"#60a5fa",fontSize:13,fontWeight:600,borderTop:shown.length>0?"1px solid #1a2333":"none"}}
            onMouseEnter={e=>e.currentTarget.style.background="#161f30"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            + طالب غير مسجل
          </div>
        </div>
      )}
      {value==="__new__"&&(<input style={{...D.input,marginTop:8}} placeholder="اكتب اسم الطالب" onChange={e=>onCustomName(e.target.value)} autoFocus/>)}
    </Field>
  );
}

/* ═══════════ ADD ENTRY MODAL ═══════════ */
function AddEntryModal({books,students,assistants,subPrice,activeBranch,courseBranches,onAdd,onClose}){
  const [selType,setSelType]=useState(null);const [form,setForm]=useState({});
  const setF=k=>e=>setForm(p=>({...p,[k]:e.target.value}));
  const t=selType?typeById(selType):null;
  const isSub=selType==="sub_mansura"||selType==="sub_cairo";
  const submit=()=>{
    if(!selType)return;
    if(isSub&&!subPrice){return;} // محمي أصلاً بمنع الاختيار، حماية إضافية
    let amount=num(form.amount);
    let exemptAmount=0;
    if(isSub){
      if(form.exemption==="full"){ amount=0; }
      else if(form.exemption==="partial"){
        // المبلغ المدفوع فعلياً يُسجَّل كاملاً في الاشتراكات
        // والباقي (الفرق حتى سعر الاشتراك) يُسجَّل في خانة الإعفاءات فقط — لا يبقى "متبقي" على الطالب
        amount=num(form.amount);
        exemptAmount=Math.max(0,subPrice-amount);
      }
    }
    if(!amount&&form.exemption!=="full"){return;}
    onAdd({type:selType,...form,amount,exemption:form.exemption||null,exemptAmount,exemptNote:form.exemptNote||"",paymentMonth:isSub?(form.paymentMonth||""):undefined});
    onClose();
  };
  const subLocked=isSub&&!subPrice;
  return(
    <Modal title="إضافة قيد جديد" onClose={onClose} wide>
      {!selType?<TypeSelector onSelect={setSelType} activeBranch={activeBranch} courseBranches={courseBranches} subPrice={subPrice}/>:<>
        <button style={{...D.pill,marginBottom:14}} onClick={()=>{setSelType(null);setForm({});}}>تغيير النوع</button>
        <div style={{...D.typeBadge,background:t.color+"22",borderColor:t.color+"55",color:t.color}}>{t.label}</div>

        {subLocked&&(
          <div style={{...D.warnBox,borderColor:"#dc2626",background:"#1a0a0a",color:"#fca5a5",marginBottom:14}}>
            لا يمكن تسجيل اشتراك قبل أن تحدد الإدارة سعر الاشتراك لهذه الدفعة.<br/>تواصل مع الإدارة المركزية لتحديد السعر أولاً.
          </div>
        )}

        {isSub&&!subLocked&&(<StudentSelector students={students} branch={selType==="sub_mansura"?"المنصورة":"القاهرة"} value={form.studentName||""} onChange={v=>setForm(p=>({...p,studentName:v}))} onCustomName={v=>setForm(p=>({...p,studentName:v}))}/>)}

        {selType==="gift"&&<Field label="المشرف المُكرَّم">
          <CustomSelect value={form.recipient||""} onChange={v=>setForm(p=>({...p,recipient:v}))} placeholder="اختر" options={[...assistants.map(a=>({value:a.name,label:a.name})),{value:"__other__",label:"آخر"}]}/>
          {form.recipient==="__other__"&&<input style={{...D.input,marginTop:8}} placeholder="الاسم" onChange={e=>setForm(p=>({...p,recipient:e.target.value}))}/>}
        </Field>}

        {(selType==="salary"||selType==="transport"||selType==="exp_supplies"||selType==="exp_other")&&<Field label="المستلم / التفاصيل"><input style={D.input} value={form.recipient||""} onChange={setF("recipient")}/></Field>}

        {isSub&&!subLocked&&(<>
          <Field label="شهر الدفع">
            <CustomSelect value={form.paymentMonth||""} onChange={v=>setForm(p=>({...p,paymentMonth:v}))} placeholder="اختياري" options={[
              {value:"الشهر الأول",label:"الشهر الأول"},
              {value:"الشهر الثاني",label:"الشهر الثاني"},
              {value:"الشهر الثالث",label:"الشهر الثالث"},
              {value:"دفعة كاملة",label:"دفعة كاملة"},
            ]}/>
          </Field>

          <Field label="الإعفاء">
            <CustomSelect value={form.exemption||""} onChange={v=>setForm(p=>({...p,exemption:v,amount:v==="full"?0:p.amount}))} placeholder="لا يوجد إعفاء" options={[
              {value:"",label:"لا يوجد إعفاء"},
              {value:"full",label:"إعفاء كامل"},
              {value:"partial",label:"إعفاء جزئي"},
            ]}/>
            {form.exemption==="partial"&&(
              <div style={{marginTop:8}}>
                <input style={D.input} type="number" min="0" max={subPrice} placeholder={`المبلغ المدفوع فعلياً (من أصل ${currency(subPrice)})`} value={form.amount||""} onChange={setF("amount")}/>
                {form.amount&&num(form.amount)<subPrice&&(
                  <div style={{marginTop:6,padding:"6px 10px",background:"#1e3a5f",borderRadius:8,color:"#93c5fd",fontSize:11}}>
                    سيُسجَّل {currency(num(form.amount))} في الاشتراكات، و{currency(subPrice-num(form.amount))} في الإعفاءات (لن يبقى متبقي على الطالب)
                  </div>
                )}
              </div>
            )}
            {form.exemption&&<input style={{...D.input,marginTop:6}} placeholder="سبب الإعفاء (اختياري)" value={form.exemptNote||""} onChange={setF("exemptNote")}/>}
          </Field>
        </>)}

        {!isSub&&(
          <Field label="المبلغ (ج.م) *">
            <input style={D.input} type="number" min="0" value={form.amount||""} onChange={setF("amount")}/>
          </Field>
        )}

        {isSub&&!subLocked&&form.exemption!=="partial"&&(
          <Field label="المبلغ (ج.م) *">
            <input style={D.input} type="number" min="0" value={form.exemption==="full"?0:form.amount||""} onChange={setF("amount")} readOnly={form.exemption==="full"}/>
            {!form.exemption&&(
              <button style={{...D.pill,marginTop:6,fontSize:11}} onClick={()=>setForm(p=>({...p,amount:subPrice}))}>تعبئة سعر الاشتراك ({currency(subPrice)})</button>
            )}
          </Field>
        )}

        <Field label="ملاحظة"><input style={D.input} placeholder="وصف إضافي..." value={form.description||""} onChange={setF("description")}/></Field>
        <div style={D.timeNote}>سيُسجَّل: {fmtDT(nowISO())}</div>
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <Btn onClick={submit} style={subLocked?{opacity:.4,pointerEvents:"none"}:{}}>تسجيل</Btn>
          <Btn ghost onClick={onClose}>إلغاء</Btn>
        </div>
      </>}
    </Modal>
  );
}

function TypeSelector({onSelect,activeBranch,courseBranches,subPrice}){
  const singleBranch=courseBranches?.length===1?courseBranches[0]:null;
  const hiddenTypes=singleBranch
    ?(singleBranch==="المنصورة"?["sub_cairo"]:["sub_mansura"])
    :courseBranches?.length>1
      ?(activeBranch==="المنصورة"?["sub_cairo"]:["sub_mansura"])
      :[];
  return(<div><p style={{color:"#94a3b8",fontSize:13,marginBottom:16}}>اختر نوع القيد</p>
    {!subPrice&&(
      <div style={{...D.warnBox,borderColor:"#92400e",background:"#1c1306",color:"#fbbf24",marginBottom:16}}>
        لم تحدد الإدارة سعر الاشتراك بعد — أنواع الاشتراك معطّلة مؤقتاً
      </div>
    )}
    {GROUPS.map(g=>(
    <div key={g.id} style={{marginBottom:20}}>
      <div style={{color:g.color,fontSize:11,fontWeight:700,marginBottom:10,letterSpacing:"1px"}}>{g.label}</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8}}>
        {ENTRY_TYPES.filter(t=>t.group===g.id&&!hiddenTypes.includes(t.id)).map(t=>{
          const isSubType=t.id==="sub_mansura"||t.id==="sub_cairo";
          const locked=isSubType&&!subPrice;
          return(
            <button key={t.id} style={{...D.typeBtn,...(locked?{opacity:.4,cursor:"not-allowed"}:{})}} onClick={()=>!locked&&onSelect(t.id)} disabled={locked}>
              <span style={{color:"#f1f5f9",fontSize:12,fontWeight:600,flex:1,textAlign:"right"}}>{t.label}</span>
              {locked
                ?<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
                :<span style={{width:8,height:8,borderRadius:"50%",background:t.color,flexShrink:0}}/>
              }
            </button>
          );
        })}
      </div>
    </div>
  ))}</div>);
}

/* ═══════════ MODALS ═══════════ */
function PwInput({label,value,onChange}){
  const [show,setShow]=useState(false);
  return(<Field label={label}><div style={{position:"relative"}}><input style={{...D.input,paddingLeft:40}} type={show?"text":"password"} placeholder="••••••••" value={value} onChange={onChange}/><button onClick={()=>setShow(p=>!p)} style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:"#64748b",display:"flex"}}>
    {show
      ?<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
      :<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
    }
  </button></div></Field>);
}

function EditEntryModal({entry,students,assistants,coursePassword,onSave,onClose}){
  const [form,setForm]=useState({amount:entry.amount,description:entry.description||"",studentName:entry.studentName||"",recipient:entry.recipient||""});
  const [pw,setPw]=useState("");const [step,setStep]=useState(1);
  const sf=k=>e=>setForm(p=>({...p,[k]:e.target.value}));
  const save=()=>{if(pw!==coursePassword){alert("كلمة المرور غير صحيحة");return;}const ch={};if(num(form.amount)!==num(entry.amount))ch.amount=num(form.amount);if(form.description!==(entry.description||""))ch.description=form.description;if(form.studentName!==(entry.studentName||""))ch.studentName=form.studentName;if(form.recipient!==(entry.recipient||""))ch.recipient=form.recipient;onSave(ch);};
  const t=typeById(entry.type);
  return(<Modal title="تعديل القيد" onClose={onClose}>
    {step===1?<>
      <div style={{...D.typeBadge,background:t.color+"22",borderColor:t.color+"55",color:t.color,marginBottom:14}}>{t.label} — {fmtDT(entry.createdAt)}</div>
      <Field label="المبلغ"><input style={D.input} type="number" value={form.amount} onChange={sf("amount")}/></Field>
      {entry.studentName!==undefined&&<Field label="الطالب"><CustomSelect value={form.studentName} onChange={v=>setForm(p=>({...p,studentName:v}))} options={students.map(s=>({value:s.name,label:s.name}))}/></Field>}
      {entry.type==="gift"&&<Field label="المشرف"><CustomSelect value={form.recipient} onChange={v=>setForm(p=>({...p,recipient:v}))} options={assistants.map(a=>({value:a.name,label:a.name}))}/></Field>}
      {entry.recipient!==undefined&&entry.type!=="gift"&&<Field label="المستلم"><input style={D.input} value={form.recipient} onChange={sf("recipient")}/></Field>}
      <Field label="الملاحظة"><input style={D.input} value={form.description} onChange={sf("description")}/></Field>
      <div style={D.warnBox}>سيظهر هذا التعديل في سجل الإدارة بتاريخه ووقته</div>
      <div style={{display:"flex",gap:10,marginTop:14}}><Btn onClick={()=>setStep(2)}>التالي</Btn><Btn ghost onClick={onClose}>إلغاء</Btn></div>
    </>:<>
      <div style={D.warnBox}>أدخل كلمة مرور حسابك لتأكيد التعديل</div>
      <PwInput label="كلمة المرور" value={pw} onChange={e=>setPw(e.target.value)}/>
      <div style={{display:"flex",gap:10,marginTop:14}}><Btn onClick={save}>حفظ</Btn><Btn ghost onClick={()=>setStep(1)}>رجوع</Btn></div>
    </>}
  </Modal>);
}

function DeleteConfirmModal({entry,coursePassword,onConfirm,onClose}){
  const [pw,setPw]=useState("");
  const [err,setErr]=useState("");
  const doDelete=()=>{if(coursePassword!=="__skip__"&&pw!==coursePassword){setErr("كلمة المرور غير صحيحة");return;}onConfirm();};
  return(<Modal title="تأكيد الحذف" onClose={onClose}>
    <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:16,padding:"14px 16px",background:"#1a0a0a",border:"1px solid #450a0a",borderRadius:12}}>
      <div style={{width:34,height:34,borderRadius:"50%",background:"#450a0a",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
      </div>
      <div style={{fontSize:13,color:"#fca5a5",lineHeight:1.6}}>
        هذا الإجراء لا يمكن التراجع عنه.<br/>سيتم حذف: <strong style={{color:"#fff"}}>{entry.description||typeById(entry.type)?.label||"هذا القيد"}</strong>
      </div>
    </div>
    {coursePassword!=="__skip__"&&<PwInput label="أدخل كلمة مرور حسابك للتأكيد" value={pw} onChange={e=>{setPw(e.target.value);setErr("");}}/>}
    {err&&<p style={{color:"#f87171",fontSize:12,margin:"4px 0"}}>{err}</p>}
    <div style={{display:"flex",gap:10,marginTop:14}}>
      <button style={{...D.btn,background:"#dc2626"}} onClick={doDelete}>حذف نهائي</button>
      <Btn ghost onClick={onClose}>إلغاء</Btn>
    </div>
  </Modal>);
}

function ResetBatchModal({coursePassword,snapshots,onReset,onRestore,onClose}){
  const [pw,setPw]=useState("");const [step,setStep]=useState("main");
  const doReset=()=>{if(onReset(pw)){alert("تم التصفير. يمكنك الاستعادة من الزر أدناه لمدة محدودة.");onClose();}};
  return(<Modal title="تصفير الدفعة" onClose={onClose}>
    {step==="main"&&<>
      <div style={{...D.warnBox,borderColor:"#dc2626",background:"#450a0a",color:"#fca5a5",marginBottom:14}}>تصفير الدفعة سيحذف جميع القيود ويعيد حالة الدفع.<br/><strong>سيتم حفظ نقطة استعادة تلقائياً قبل التصفير.</strong></div>
      {snapshots.length>0&&<><div style={{color:"#f59e0b",fontSize:13,fontWeight:700,marginBottom:8}}>نقاط الاستعادة المتاحة ({snapshots.length})</div><div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>{snapshots.map(s=>(<div key={s.id} style={{...D.card,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px"}}><div><div style={{color:"#f1f5f9",fontSize:13,fontWeight:600}}>{s.courseName} — دفعة {s.batchNumber}</div><div style={{color:"#64748b",fontSize:11}}>حُفظت: {fmtDT(s.savedAt)} · {s.entries?.length||0} قيد</div></div><button style={{...D.btn,background:"#16a34a",fontSize:12,padding:"5px 12px"}} onClick={()=>{showConfirm("سيتم استبدال البيانات الحالية ببيانات هذه النقطة",{title:"استعادة نقطة حفظ",confirmText:"استعادة"}).then(ok=>{if(ok){onRestore(s.id);onClose();}});}}>استعادة</button></div>))}</div></>}
      <PwInput label="أدخل كلمة مرور حسابك للتصفير" value={pw} onChange={e=>setPw(e.target.value)}/>
      <div style={{display:"flex",gap:10,marginTop:14}}><button style={{...D.btn,background:"#dc2626"}} onClick={()=>setStep("confirm")}>التالي</button><Btn ghost onClick={onClose}>إلغاء</Btn></div>
    </>}
    {step==="confirm"&&<>
      <div style={{...D.warnBox,borderColor:"#dc2626",background:"#450a0a",color:"#fca5a5",marginBottom:14,fontSize:14,textAlign:"center"}}>هل أنت متأكد تماماً؟<br/><strong>سيتم مسح جميع القيود وإعادة البداية</strong></div>
      <div style={{display:"flex",gap:10,marginTop:14,justifyContent:"center"}}>
        <button style={{...D.btn,background:"#dc2626",fontSize:14,padding:"10px 24px"}} onClick={doReset}>نعم، تصفير</button>
        <button style={{...D.btn,background:"#334155",fontSize:14,padding:"10px 24px"}} onClick={()=>setStep("main")}>رجوع</button>
      </div>
    </>}
  </Modal>);
}

function CompleteBatchModal({coursePassword,onConfirm,onClose}){
  const [pw,setPw]=useState("");
  return(<Modal title="إتمام الدفعة" onClose={onClose}>
    <div style={{...D.warnBox,borderColor:"#16a34a",background:"#052e16",color:"#86efac",marginBottom:14}}>سيتم إغلاق هذه الدفعة وحفظها في الأرشيف.<br/>لن تتمكن من إضافة قيود جديدة بعد الإتمام.</div>
    <PwInput label="أدخل كلمة مرور حسابك للتأكيد" value={pw} onChange={e=>setPw(e.target.value)}/>
    <div style={{display:"flex",gap:10,marginTop:14}}>
      <button style={{...D.btn,background:"#16a34a"}} onClick={()=>onConfirm(pw)}>إتمام الدفعة</button>
      <Btn ghost onClick={onClose}>إلغاء</Btn>
    </div>
  </Modal>);
}

function ChangePwModal({onSave,onClose}){
  const [old,setOld]=useState("");const [nw,setNw]=useState("");const [conf,setConf]=useState("");const [err,setErr]=useState("");
  const save=()=>{if(!nw){setErr("أدخل كلمة المرور الجديدة");return;}if(nw!==conf){setErr("كلمتا المرور غير متطابقتان");return;}if(nw.length<4){setErr("كلمة المرور قصيرة جداً");return;}if(onSave(old,nw)){alert("تم تغيير كلمة المرور");onClose();}};
  return(<Modal title="تغيير كلمة المرور" onClose={onClose}>
    <PwInput label="كلمة المرور الحالية" value={old} onChange={e=>setOld(e.target.value)}/>
    <PwInput label="كلمة المرور الجديدة" value={nw} onChange={e=>setNw(e.target.value)}/>
    <PwInput label="تأكيد كلمة المرور الجديدة" value={conf} onChange={e=>setConf(e.target.value)}/>
    {err&&<p style={{color:"#f87171",fontSize:12,margin:"4px 0"}}>{err}</p>}
    <div style={{display:"flex",gap:10,marginTop:16}}><Btn onClick={save}>حفظ</Btn><Btn ghost onClick={onClose}>إلغاء</Btn></div>
  </Modal>);
}

function ArchiveModal({batches,onClose,inline}){
  const [sel,setSel]=useState(null);
  const batch=sel?batches.find(b=>b.id===sel):null;
  const content = batch ? <>
    <button style={{...D.pill,marginBottom:14}} onClick={()=>setSel(null)}>رجوع</button>
    <BatchReport batch={batch}/>
  </> : <>
    {batches.length===0&&<EmptyState icon="—" label="لا توجد دفعات مكتملة" sub=""/>}
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {[...batches].reverse().map(b=><div key={b.id} style={{...D.card,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}} onClick={()=>setSel(b.id)}>
        <div><div style={{fontWeight:700,color:"#f1f5f9"}}>{b.courseName} — دفعة {b.batchNumber}</div><div style={{color:"#64748b",fontSize:12}}>{b.branch} · اكتملت: {fmtDate(b.completedAt)}</div></div>
        <span style={{color:"#60a5fa",fontSize:13}}>عرض</span>
      </div>)}
    </div>
  </>;
  if(inline) return <div><h2 style={D.secTitle}>الأرشيف</h2>{content}</div>;
  return(<Modal title="أرشيف الدفعات السابقة" onClose={onClose} wide>{content}</Modal>);
}

/* ═══════════ [#8] ATTENDANCE PAGE — زر ثابت ═══════════ */

/* ── Bar Chart ── */
function BarChart({sessions,students}){
  const data=sessions.slice(-12).map(sess=>{const pct=students.length>0?Math.round(((sess.present?.length||0)/students.length)*100):0;return{label:sess.title||fmtDate(sess.date),pct};});
  return(
    <div className="table-scroll">
      <div style={{minWidth:Math.max(400,data.length*70),padding:"0 8px"}}>
        <div style={{display:"flex",alignItems:"flex-end",gap:8,height:180,position:"relative"}}>
          {[0,25,50,75,100].map(p=>(<div key={p} style={{position:"absolute",bottom:(p/100)*160,left:0,right:0,borderTop:"1px dashed #1e293b",display:"flex",alignItems:"center"}}><span style={{color:"#475569",fontSize:10,marginRight:4,whiteSpace:"nowrap"}}>{p}%</span></div>))}
          {data.map((d,i)=>{const h=Math.max(4,(d.pct/100)*160);const color=d.pct>=75?"#22c55e":d.pct>=50?"#f59e0b":"#ef4444";return(<div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"flex-end",height:180,position:"relative",zIndex:1}}><span style={{color:"#f1f5f9",fontSize:10,fontWeight:700,marginBottom:3}}>{d.pct}%</span><div style={{width:"70%",height:h,background:`linear-gradient(180deg,${color},${color}88)`,borderRadius:"4px 4px 0 0"}}/></div>);})}
        </div>
        <div style={{display:"flex",gap:8,marginTop:8}}>{data.map((d,i)=>(<div key={i} style={{flex:1,textAlign:"center",color:"#64748b",fontSize:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{d.label}</div>))}</div>
      </div>
    </div>
  );
}

/* ── Line Chart ── */
function LineChart({sessions,students}){
  const data=sessions.slice(-15).map((sess,i)=>({i,pct:students.length>0?Math.round(((sess.present?.length||0)/students.length)*100):0,label:sess.title||fmtDate(sess.date)}));
  if(data.length<2) return <div style={{color:"#475569",textAlign:"center",padding:20}}>تحتاج جلستين على الأقل لعرض الخط</div>;
  const W=600,H=160,pad=40;const xStep=(W-pad*2)/(data.length-1);const points=data.map((d,i)=>({x:pad+i*xStep,y:H-pad-(d.pct/100)*(H-pad*2)}));
  const pathD="M"+points.map(p=>`${p.x},${p.y}`).join(" L");const areaD=pathD+` L${points[points.length-1].x},${H-pad} L${points[0].x},${H-pad} Z`;
  return(<svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:H,overflow:"visible"}}>
    <defs><linearGradient id="lg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/><stop offset="100%" stopColor="#3b82f6" stopOpacity="0"/></linearGradient></defs>
    {[0,25,50,75,100].map(p=>{const y=H-pad-(p/100)*(H-pad*2);return<g key={p}><line x1={pad} y1={y} x2={W-pad} y2={y} stroke="#1e293b" strokeWidth="1"/><text x={pad-4} y={y+4} textAnchor="end" fill="#475569" fontSize="10">{p}%</text></g>;})}
    <path d={areaD} fill="url(#lg)"/>
    <path d={pathD} fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinejoin="round"/>
    {points.map((p,i)=>(<g key={i}><circle cx={p.x} cy={p.y} r="5" fill="#3b82f6" stroke="#060c18" strokeWidth="2"/><text x={p.x} y={p.y-10} textAnchor="middle" fill="#f1f5f9" fontSize="10" fontWeight="700">{data[i].pct}%</text></g>))}
    {points.map((p,i)=>(<text key={i} x={p.x} y={H-8} textAnchor="middle" fill="#64748b" fontSize="9">{data[i].label.slice(0,8)}</text>))}
  </svg>);
}

/* ── Donut Chart ── */
function DonutChart({sessions,students}){
  const totalSessions=sessions.length;const totalStudents=students.length;
  if(totalSessions===0||totalStudents===0) return null;
  const totalPossible=totalSessions*totalStudents;const totalPresent=sessions.reduce((s,sess)=>s+(sess.present?.length||0),0);const totalAbsent=totalPossible-totalPresent;const pctPresent=Math.round((totalPresent/totalPossible)*100);const pctAbsent=100-pctPresent;
  const r=70,cx=100,cy=100,stroke=28;const circ=2*Math.PI*r;const presentArc=(pctPresent/100)*circ;
  return(<div style={{display:"flex",gap:24,flexWrap:"wrap",alignItems:"center"}}>
    <svg viewBox="0 0 200 200" style={{width:200,height:200,flexShrink:0}}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#1e293b" strokeWidth={stroke}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#22c55e" strokeWidth={stroke} strokeDasharray={`${presentArc} ${circ}`} strokeDashoffset={circ*0.25}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ef4444" strokeWidth={stroke} strokeDasharray={`${(pctAbsent/100)*circ} ${circ}`} strokeDashoffset={circ*0.25-presentArc}/>
      <text x={cx} y={cy-8} textAnchor="middle" fill="#f1f5f9" fontSize="24" fontWeight="800">{pctPresent}%</text>
      <text x={cx} y={cy+14} textAnchor="middle" fill="#64748b" fontSize="12">نسبة الحضور</text>
    </svg>
    <div style={{flex:1,minWidth:200}}>
      <div style={{display:"flex",gap:20,marginBottom:20}}>
        <div style={{textAlign:"center"}}><div style={{color:"#22c55e",fontSize:28,fontWeight:800}}>{totalPresent}</div><div style={{color:"#64748b",fontSize:12}}>حضور كلي</div></div>
        <div style={{textAlign:"center"}}><div style={{color:"#ef4444",fontSize:28,fontWeight:800}}>{totalAbsent}</div><div style={{color:"#64748b",fontSize:12}}>غياب كلي</div></div>
      </div>
    </div>
  </div>);
}

/* ── Add Attendance Modal ── */
function AddAttendanceModal({students,courseId,setDB,onClose}){
  const [title,setTitle]=useState("");const [date,setDate]=useState(new Date().toISOString().slice(0,10));const [notes,setNotes]=useState("");const [present,setPresent]=useState({});const [branch,setBranch]=useState("الكل");const [importText,setImportText]=useState("");const [importMode,setImportMode]=useState(false);const fileRef=useRef();
  const toggleAll=(val)=>setPresent(Object.fromEntries(students.map(s=>[s.name,val])));
  const toggleBranch=(br,val)=>setPresent(p=>({...p,...Object.fromEntries(students.filter(s=>s.branch===br).map(s=>[s.name,val]))}));
  const importCSV=(e)=>{const file=e.target.files[0];if(!file)return;const reader=new FileReader();reader.onload=(ev)=>{const names=ev.target.result.split(/\r?\n/).map(l=>l.trim()).filter(Boolean);setPresent(p=>{const np={...p};names.forEach(n=>{if(students.some(s=>s.name===n))np[n]=true;});return np;});alert(`تم استيراد ${names.length} اسم`);};reader.readAsText(file,"UTF-8");e.target.value="";};
  const applyImportText=()=>{const names=importText.split(/[\n,،]/).map(l=>l.trim()).filter(Boolean);setPresent(p=>{const np={...p};names.forEach(n=>{const match=students.find(s=>fuzzy(s.name,n));if(match)np[match.name]=true;});return np;});setImportMode(false);};
  const save=()=>{const sess={id:uid(),title:title||"جلسة",date,notes,present:students.filter(s=>present[s.name]).map(s=>s.name),createdAt:nowISO()};setDB(p=>({...p,attendance:{...(p.attendance||{}),[courseId]:[...(p.attendance?.[courseId]||[]),sess]}}));onClose();};
  const filtered=branch==="الكل"?students:students.filter(s=>s.branch===branch);const presentCount=students.filter(s=>present[s.name]).length;
  return(
    <Modal title="تسجيل جلسة حضور" onClose={onClose} wide>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
        <Field label="عنوان الجلسة"><input style={D.input} placeholder="مثال: محاضرة 1" value={title} onChange={e=>setTitle(e.target.value)}/></Field>
        <Field label="تاريخ الجلسة"><input style={D.input} type="date" value={date} onChange={e=>setDate(e.target.value)}/></Field>
      </div>
      <Field label="ملاحظات"><input style={D.input} placeholder="ملاحظات اختيارية..." value={notes} onChange={e=>setNotes(e.target.value)}/></Field>
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <button style={{...D.pill,fontSize:11}} onClick={()=>setImportMode(p=>!p)}>إدخال الأسماء نصياً</button>
        <button style={{...D.pill,fontSize:11}} onClick={()=>fileRef.current.click()}>استيراد CSV</button>
        <input ref={fileRef} type="file" accept=".csv,.txt" style={{display:"none"}} onChange={importCSV}/>
        <button style={{...D.pill,fontSize:11}} onClick={()=>toggleAll(true)}>تحضير الكل</button>
        <button style={{...D.pill,fontSize:11}} onClick={()=>toggleAll(false)}>إلغاء التحديد</button>
        {["المنصورة","القاهرة"].map(br=>(<button key={br} style={{...D.pill,fontSize:11}} onClick={()=>toggleBranch(br,true)}>{br}</button>))}
      </div>
      {importMode&&(<div style={{marginBottom:12}}><textarea style={{...D.input,height:80,resize:"vertical"}} placeholder="اكتب الأسماء مفصولة بسطر أو فاصلة..." value={importText} onChange={e=>setImportText(e.target.value)}/><button style={{...D.btn,marginTop:6,fontSize:12}} onClick={applyImportText}>تطبيق الأسماء</button></div>)}
      <div style={{display:"flex",gap:8,marginBottom:12}}>
        {["الكل","المنصورة","القاهرة"].map(br=>(<button key={br} style={{...D.pill,fontSize:11,...(branch===br?D.pillActive:{})}} onClick={()=>setBranch(br)}>{br}</button>))}
        <span style={{color:"#64748b",fontSize:12,marginRight:"auto",alignSelf:"center"}}>حضر: <strong style={{color:"#22c55e"}}>{presentCount}</strong> / {students.length}</span>
      </div>
      <div style={{maxHeight:280,overflowY:"auto",display:"flex",flexDirection:"column",gap:6}}>
        {filtered.map(s=>(<div key={s.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:present[s.name]?"#052e16":"#1e293b",borderRadius:8,border:`1px solid ${present[s.name]?"#16a34a":"#334155"}`,cursor:"pointer",transition:"all .15s"}} onClick={()=>setPresent(p=>({...p,[s.name]:!p[s.name]}))}>
          <div style={{width:20,height:20,borderRadius:6,background:present[s.name]?"#22c55e":"#334155",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{present[s.name]&&"+"}</div>
          <span style={{color:"#f1f5f9",fontSize:13,flex:1}}>{s.name}</span>
          <span style={{fontSize:10,color:"#64748b"}}>{s.branch}</span>
        </div>))}
      </div>
      <div style={{display:"flex",gap:10,marginTop:16}}><Btn onClick={save}>حفظ الجلسة</Btn><Btn ghost onClick={onClose}>إلغاء</Btn></div>
    </Modal>
  );
}

/* ── Session Detail Modal ── */
function SessionDetailModal({session,students,totalStudents,onClose}){
  const absent=students.filter(s=>!(session.present||[]).includes(s.name));const presentStudents=students.filter(s=>(session.present||[]).includes(s.name));const pct=totalStudents>0?Math.round(((session.present?.length||0)/totalStudents)*100):0;
  return(<Modal title={`تفاصيل: ${session.title||"جلسة"}`} onClose={onClose} wide>
    <div style={{display:"flex",gap:16,marginBottom:20,flexWrap:"wrap"}}>
      <div style={{...D.miniStat,flex:1}}><div style={{color:"#64748b",fontSize:11}}>التاريخ</div><div style={{color:"#f1f5f9",fontWeight:700}}>{fmtDate(session.date)}</div></div>
      <div style={{...D.miniStat,flex:1}}><div style={{color:"#64748b",fontSize:11}}>الحضور</div><div style={{color:"#22c55e",fontWeight:700}}>{session.present?.length||0}</div></div>
      <div style={{...D.miniStat,flex:1}}><div style={{color:"#64748b",fontSize:11}}>الغياب</div><div style={{color:"#f87171",fontWeight:700}}>{totalStudents-(session.present?.length||0)}</div></div>
      <div style={{...D.miniStat,flex:1}}><div style={{color:"#64748b",fontSize:11}}>النسبة</div><div style={{color:pct>=75?"#a3e635":pct>=50?"#f59e0b":"#f87171",fontWeight:700}}>{pct}%</div></div>
    </div>
    <div style={{height:10,background:"#1e293b",borderRadius:5,overflow:"hidden",marginBottom:20}}><div style={{width:pct+"%",height:"100%",background:`linear-gradient(90deg,#22c55e,#a3e635)`,borderRadius:5}}/></div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
      <div>
        <h3 style={{color:"#22c55e",fontSize:13,fontWeight:700,marginBottom:10}}>الحاضرون ({presentStudents.length})</h3>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>{presentStudents.map(s=><div key={s.id} style={{padding:"6px 12px",background:"#052e16",borderRadius:8,color:"#f1f5f9",fontSize:13}}>{s.name} <span style={{color:"#64748b",fontSize:11}}>({s.branch})</span></div>)}</div>
      </div>
      <div>
        <h3 style={{color:"#f87171",fontSize:13,fontWeight:700,marginBottom:10}}>الغائبون ({absent.length})</h3>
        <div style={{display:"flex",flexDirection:"column",gap:4}}>{absent.map(s=><div key={s.id} style={{padding:"6px 12px",background:"#450a0a",borderRadius:8,color:"#f1f5f9",fontSize:13}}>{s.name} <span style={{color:"#64748b",fontSize:11}}>({s.branch})</span></div>)}</div>
      </div>
    </div>
    {session.notes&&<div style={{marginTop:16,padding:"10px 14px",background:"#1e293b",borderRadius:8,color:"#94a3b8",fontSize:13}}>{session.notes}</div>}
  </Modal>);
}

/* ═══════════ VOICE RECORDER ═══════════ */
function VoiceRecorder({onSend}){
  const [recording,setRecording]=useState(false);
  const [duration,setDuration]=useState(0);
  const mrRef=useRef(null);
  const chunksRef=useRef([]);
  const timerRef=useRef(null);

  const start=async()=>{
    try{
      const stream=await navigator.mediaDevices.getUserMedia({audio:true});
      const mr=new MediaRecorder(stream);
      mrRef.current=mr;
      chunksRef.current=[];
      mr.ondataavailable=e=>{if(e.data.size>0)chunksRef.current.push(e.data);};
      mr.onstop=()=>{
        const blob=new Blob(chunksRef.current,{type:"audio/webm"});
        onSend(blob);
        stream.getTracks().forEach(t=>t.stop());
        setDuration(0);
      };
      mr.start();
      setRecording(true);
      setDuration(0);
      timerRef.current=setInterval(()=>setDuration(d=>d+1),1000);
    }catch(e){alert("لا يمكن الوصول للميكروفون. تأكد من إذن المتصفح.");}
  };

  const stop=()=>{
    if(mrRef.current&&mrRef.current.state!=="inactive"){mrRef.current.stop();}
    clearInterval(timerRef.current);
    setRecording(false);
  };

  const fmt=(s)=>Math.floor(s/60)+":"+String(s%60).padStart(2,"0");

  return(
    <button
      onMouseDown={start} onMouseUp={stop} onTouchStart={e=>{e.preventDefault();start();}} onTouchEnd={e=>{e.preventDefault();stop();}}
      title="اضغط مع الاستمرار للتسجيل"
      style={{width:40,height:40,borderRadius:"50%",background:recording?"#450a0a":"#111827",border:"1px solid "+(recording?"#dc2626":"#1e293b"),cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,color:recording?"#f87171":"#64748b",transition:"all .15s",userSelect:"none",WebkitUserSelect:"none"}}>
      {recording
        ? <span style={{color:"#f87171",fontWeight:700,fontSize:11,minWidth:32,textAlign:"center"}}>{fmt(duration)}</span>
        : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
      }
    </button>
  );
}

/* ═══════════ SHARED COMPONENTS ═══════════ */
function EntryRow({entry,books,onEdit,onDelete,adminView,showCourse,course}){
  const t=typeById(entry.type);const book=books?.find(b=>b.id===entry.bookId);
  return(<div style={{...D.entryRow,borderRight:`3px solid ${t.color}55`,...(entry.edited?{borderRight:"3px solid #f59e0b"}:{})}}>
    <div style={{flex:1,minWidth:0}}>
      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span style={{width:10,height:10,borderRadius:"50%",background:t.color,flexShrink:0}}/>
        <span style={{color:"#f1f5f9",fontWeight:600,fontSize:14}}>{t.label}</span>
        {entry.edited&&<span style={{fontSize:10,background:"#78350f",color:"#fbbf24",borderRadius:4,padding:"1px 6px"}}>معدّل</span>}
        {entry.exemption==="full"&&<span style={{fontSize:10,background:"#052e16",color:"#4ade80",borderRadius:4,padding:"1px 6px"}}>إعفاء كامل</span>}
        {entry.exemption==="partial"&&<span style={{fontSize:10,background:"#2a1a00",color:"#fbbf24",borderRadius:4,padding:"1px 6px"}}>إعفاء جزئي</span>}
        {showCourse&&course&&<span style={{fontSize:11,color:"#64748b"}}>({course.name})</span>}
      </div>
      {entry.studentName&&<div style={D.eSub}>{entry.studentName}</div>}
      {entry.paymentMonth&&<div style={{...D.eSub,color:"#a78bfa"}}>{entry.paymentMonth}</div>}
      {entry.exemption==="partial"&&entry.exemptAmount>0&&<div style={{...D.eSub,color:"#fbbf24"}}>معفى من المبلغ: {currency(entry.exemptAmount)}</div>}
      {entry.recipient&&<div style={D.eSub}>{entry.recipient}</div>}
      {entry.description&&<div style={D.eSub}>{entry.description}</div>}
      {book&&<div style={D.eSub}>{book.name} x{entry.bookCount||1} ({entry.saleBranch||""})</div>}
      <div style={{...D.eSub,color:"#475569",fontSize:11}}>{fmtDT(entry.createdAt)}</div>
    </div>
    <div style={{flexShrink:0}}><div style={{color:t.group==="expense"?"#f87171":"#4ade80",fontWeight:700,fontSize:15}}>{t.group==="expense"?"- ":"+ "}{currency(entry.amount)}</div></div>
    {!adminView&&<div style={{display:"flex",gap:4}}>
      <button style={{...D.iconBtn,color:"#60a5fa"}} onClick={onEdit}>تعديل</button>
      <button style={D.iconBtn} onClick={onDelete}>حذف</button>
    </div>}
  </div>);
}

function Modal({title,children,onClose,wide}){
  return(<div style={D.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...D.modal,...(wide?{maxWidth:720}:{})}} className="rw-modal-mobile">
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <span style={{color:"#f1f5f9",fontWeight:700,fontSize:16}}>{title}</span>
        <button style={D.closeBtn} onClick={onClose}>X</button>
      </div>
      {children}
    </div>
  </div>);
}

function Field({label,children,style}){return <div style={{marginBottom:14,...style}}><label style={{display:"block",color:"#94a3b8",fontSize:12,marginBottom:5}}>{label}</label>{children}</div>;}

function CustomSelect({value,onChange,options,placeholder,disabled,style}){
  const [open,setOpen]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    const onClick=e=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};
    document.addEventListener("mousedown",onClick);
    return ()=>document.removeEventListener("mousedown",onClick);
  },[]);
  const selected=options.find(o=>o.value===value);
  return(
    <div ref={ref} style={{position:"relative",...style}}>
      <button type="button" disabled={disabled}
        onClick={()=>!disabled&&setOpen(p=>!p)}
        style={{
          width:"100%",background:disabled?"#0c1322":"#111827",
          border:`1px solid ${open?"#3b82f6":"#1f2937"}`,
          borderRadius:10,color:selected?"#f1f5f9":"#5b6b85",
          padding:"11px 14px",fontSize:13,fontFamily:"inherit",
          display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,
          cursor:disabled?"not-allowed":"pointer",textAlign:"right",
          boxShadow:open?"0 0 0 2px rgba(59,130,246,.15)":"none",
          transition:"border-color .15s,box-shadow .15s",opacity:disabled?.6:1,
        }}>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{selected?selected.label:(placeholder||"اختر")}</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0,transition:"transform .15s",transform:open?"rotate(180deg)":"none"}}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open&&(
        <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,left:0,zIndex:300,background:"#111827",border:"1px solid #1f2937",borderRadius:12,boxShadow:"0 16px 40px rgba(0,0,0,.5)",maxHeight:260,overflowY:"auto",padding:5}}>
          {options.length===0&&<div style={{padding:"12px 14px",color:"#5b6b85",fontSize:13,textAlign:"center"}}>لا توجد خيارات</div>}
          {options.map(o=>(
            <div key={o.value} onClick={()=>{onChange(o.value);setOpen(false);}}
              style={{padding:"10px 14px",borderRadius:8,cursor:"pointer",fontSize:13,
                background:o.value===value?"#1e3a5f":"transparent",
                color:o.value===value?"#93c5fd":"#cbd5e1",
                fontWeight:o.value===value?700:400,
                display:"flex",alignItems:"center",justifyContent:"space-between",
                transition:"background .1s"}}
              onMouseEnter={e=>{if(o.value!==value)e.currentTarget.style.background="#1a2436";}}
              onMouseLeave={e=>{if(o.value!==value)e.currentTarget.style.background="transparent";}}>
              <span>{o.label}</span>
              {o.sub&&<span style={{color:"#5b6b85",fontSize:11}}>{o.sub}</span>}
              {o.value===value&&<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Btn({children,onClick,ghost,small,style}){return <button style={{...D.btn,...(ghost?D.btnGhost:{}),...(small?{padding:"6px 12px",fontSize:12}:{}),...(style||{})}} onClick={onClick}>{children}</button>;}
function NavPill({children,active,onClick}){return <button style={{...D.pill,...(active?D.pillActive:{})}} onClick={onClick}>{children}</button>;}
function MiniStat({label,value,color,bold,title}){return <div style={D.miniStat} title={title}><div style={{color:"#64748b",fontSize:11,marginBottom:4}}>{label}</div><div style={{color,fontWeight:bold?800:600,fontSize:bold?17:14}}>{value}</div></div>;}
function EmptyState({icon,label,sub}){return <div style={{textAlign:"center",padding:"40px 20px",color:"#475569"}}><div style={{fontSize:40}}>{icon}</div><div style={{color:"#94a3b8",marginTop:8,fontWeight:600}}>{label}</div><div style={{fontSize:13,marginTop:4}}>{sub}</div></div>;}

/* ═══════════ GLOBAL CSS — [#7] Mobile Responsive ═══════════ */
const base={fontFamily:"'Cairo','Tajawal',sans-serif",direction:"rtl"};
if(typeof document!=="undefined"&&!document.getElementById("rawahel-css")){
  const s=document.createElement("style");s.id="rawahel-css";
  s.textContent=`
    *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
    body{margin:0;padding:0;background:#060c18}
    input,select,button,textarea{font-family:'Cairo','Tajawal',sans-serif!important}
    ::-webkit-scrollbar{width:4px;height:4px}
    ::-webkit-scrollbar-track{background:#0f172a}
    ::-webkit-scrollbar-thumb{background:#334155;border-radius:4px}

    /* ── Animations ── */
    @keyframes glow-pulse{0%,100%{box-shadow:0 0 20px 4px rgba(212,190,140,.25),0 0 40px 10px rgba(180,150,80,.08)}50%{box-shadow:0 0 36px 10px rgba(212,190,140,.45),0 0 70px 20px rgba(180,150,80,.18)}}
    @keyframes fade-up{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
    .rw-logo-glow{animation:glow-pulse 3s ease-in-out infinite;border-radius:50%}
    .rw-page{animation:fade-up .18s ease both}

    /* ── Custom Select ── */
    select{
      appearance:none;-webkit-appearance:none;
      background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
      background-repeat:no-repeat;
      background-position:left 12px center;
      padding-left:36px!important;
      cursor:pointer;
    }
    select:focus{outline:none;border-color:#3b82f6!important;box-shadow:0 0 0 2px rgba(59,130,246,.15)}
    select option{background:#111827;color:#f1f5f9;padding:8px}

    @media(max-width:768px){
      .rw-nav-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch;display:flex;gap:4px;flex-wrap:nowrap;padding-bottom:2px}
      .rw-nav-scroll::-webkit-scrollbar{height:2px}
      .rw-stats-mobile{grid-template-columns:1fr 1fr!important}
      .rw-modal-mobile{max-width:100%!important;margin:0!important;border-radius:16px 16px 0 0!important;position:fixed!important;bottom:0!important;left:0!important;right:0!important;max-height:90vh!important}
      .rw-card-mobile{padding:12px!important}
      input,select,textarea{font-size:16px!important}
      .table-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
    }
    @media(max-width:480px){
      .hide-mobile{display:none!important}
      .sup-header-title{font-size:12px!important}
    }

    /* ── Login screen enhancements ── */
    .rw-login-bg{
      background:linear-gradient(160deg,#060c18 0%,#0a1220 50%,#060c18 100%);
      position:relative;overflow:hidden;
    }
    .rw-login-bg::before{
      content:'';position:fixed;inset:0;
      background:radial-gradient(ellipse 80% 50% at 50% -10%,rgba(29,78,216,.15),transparent),
                 radial-gradient(ellipse 60% 40% at 80% 80%,rgba(124,58,237,.08),transparent);
      pointer-events:none;
    }
    .rw-course-card{transition:border-color .15s,transform .15s,box-shadow .15s}
    .rw-course-card:hover{border-color:#3b82f6!important;transform:translateY(-2px);box-shadow:0 8px 24px rgba(29,78,216,.2)!important}
  `;
  document.head.appendChild(s);
}

/* ═══════════ STYLES ═══════════ */
const LS={
  bg:{...base,minHeight:"100vh",background:"linear-gradient(160deg,#060c18 0%,#0a1220 50%,#060c18 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:16},
  card:{background:"rgba(10,18,32,0.98)",border:"1px solid #1e3a5f",borderRadius:18,padding:"32px 28px",width:"100%",maxWidth:400,boxShadow:"0 32px 80px rgba(0,0,0,.7)"},
  input:{width:"100%",background:"#111827",border:"1px solid #1f2937",borderRadius:10,color:"#f1f5f9",padding:"11px 14px",fontSize:14,fontFamily:"'Cairo','Tajawal',sans-serif",direction:"rtl",boxSizing:"border-box",outline:"none",transition:"border-color .15s"},
  loginBtn:{width:"100%",background:"linear-gradient(135deg,#1d4ed8,#2563eb)",border:"none",cursor:"pointer",color:"#fff",borderRadius:10,padding:"12px",fontSize:15,fontFamily:"inherit",fontWeight:700,marginTop:10,boxShadow:"0 4px 20px rgba(29,78,216,.4)"},
  newBtn:{width:"100%",background:"transparent",border:"1px dashed #1e3a5f",cursor:"pointer",color:"#4b6488",borderRadius:10,padding:"10px",fontSize:13,fontFamily:"inherit",marginTop:10},
  err:{color:"#f87171",fontSize:12,margin:"6px 0 0",textAlign:"center"},
};
const D={
  root:{...base,minHeight:"100vh",background:"#060c18",color:"#e2e8f0"},
  header:{background:"linear-gradient(180deg,#0a1628,#0d1f3c)",borderBottom:"1px solid #1e3a5f",position:"sticky",top:0,zIndex:100},
  hInner:{maxWidth:1400,margin:"0 auto",padding:"0 14px",minHeight:54,display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"},
  hEmoji:{width:32,height:32,borderRadius:"50%",background:"#1d4ed8",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0,color:"#fff",fontWeight:700},
  hTitle:{color:"#f1f5f9",fontWeight:800,fontSize:14,letterSpacing:".3px"},
  hSub:{color:"#4b6488",fontSize:10},
  logoutBtn:{background:"transparent",border:"1px solid #1f2937",cursor:"pointer",color:"#6b7280",borderRadius:8,padding:"5px 12px",fontSize:12,fontFamily:"inherit"},
  main:{maxWidth:1400,margin:"0 auto",padding:"18px 14px 90px"},
  secTitle:{color:"#f1f5f9",fontSize:15,fontWeight:700,margin:"0 0 12px"},
  statsGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8,marginBottom:18},
  miniStat:{background:"linear-gradient(135deg,#0f172a,#111827)",border:"1px solid #1e293b",borderRadius:10,padding:"12px 14px"},
  courseGrid:{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))",gap:12,marginBottom:20},
  courseCard:{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:14,cursor:"pointer",transition:"border-color .15s"},
  pageHead:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:10},
  list:{display:"flex",flexDirection:"column",gap:6},
  entryRow:{background:"#0f172a",border:"1px solid #1e293b",borderRadius:10,padding:"10px 13px",display:"flex",alignItems:"flex-start",gap:10,flexWrap:"wrap",transition:"border-color .15s"},
  eSub:{color:"#4b6488",fontSize:11,marginTop:2,lineHeight:1.4},
  pill:{background:"#111827",border:"1px solid #1f2937",borderRadius:8,color:"#9ca3af",padding:"5px 11px",cursor:"pointer",fontSize:12,fontFamily:"inherit",whiteSpace:"nowrap",transition:"all .15s"},
  pillActive:{background:"#1d4ed8",borderColor:"#3b82f6",color:"#fff"},
  card:{background:"#0f172a",border:"1px solid #1e293b",borderRadius:12,padding:16},
  input:{width:"100%",background:"#111827",border:"1px solid #1f2937",borderRadius:8,color:"#f1f5f9",padding:"9px 12px",fontSize:13,fontFamily:"'Cairo','Tajawal',sans-serif",direction:"rtl",boxSizing:"border-box",outline:"none"},
  table:{background:"#0f172a",border:"1px solid #1e293b",borderRadius:10,overflow:"hidden",marginBottom:8},
  tHead:{display:"grid",padding:"8px 13px",background:"#111827",color:"#6b7280",fontSize:11,fontWeight:700,gap:8},
  tRow:{display:"grid",padding:"8px 13px",borderTop:"1px solid #1e293b",fontSize:12,color:"#9ca3af",gap:8,alignItems:"center"},
  typeBtn:{display:"flex",alignItems:"center",gap:8,background:"#111827",border:"1px solid #1f2937",borderRadius:10,padding:"10px 12px",cursor:"pointer",fontFamily:"inherit",transition:"border-color .15s"},
  typeBadge:{display:"flex",alignItems:"center",gap:8,border:"1px solid",borderRadius:8,padding:"7px 12px",fontSize:13,fontWeight:600,marginBottom:12},
  timeNote:{background:"#111827",borderRadius:7,padding:"6px 11px",fontSize:11,color:"#4b6488",marginTop:6},
  warnBox:{background:"#1c0a00",border:"1px solid #92400e",borderRadius:8,padding:"10px 13px",fontSize:13,color:"#fcd34d",lineHeight:1.7,marginBottom:8},
  btn:{background:"linear-gradient(135deg,#1d4ed8,#2563eb)",border:"none",cursor:"pointer",color:"#fff",borderRadius:8,padding:"8px 16px",fontSize:13,fontFamily:"inherit",fontWeight:600,display:"inline-flex",alignItems:"center",gap:6,whiteSpace:"nowrap"},
  btnGhost:{background:"#1e293b",color:"#94a3b8",backgroundImage:"none"},
  iconBtn:{background:"none",border:"none",cursor:"pointer",color:"#374151",padding:"3px 5px",borderRadius:6,fontSize:13},
  overlay:{position:"fixed",inset:0,background:"rgba(0,0,0,.85)",backdropFilter:"blur(6px)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:14},
  modal:{background:"#0f172a",border:"1px solid #1e293b",borderRadius:14,padding:20,width:"100%",maxWidth:480,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 24px 64px rgba(0,0,0,.6)"},
  closeBtn:{background:"#111827",border:"1px solid #1f2937",cursor:"pointer",color:"#6b7280",borderRadius:7,padding:"4px 10px",fontSize:14},
};

const RAWAHEL_LOGO = "data:image/png;base64,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";
